import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Calendar, Settings, Link2, Calculator, Upload, Pencil, GitBranch, Table2, BarChart3, ScrollText, ChevronsLeft, ChevronsRight, ChevronDown, ExternalLink } from 'lucide-react';
import ImportConfigModal from '../components/ImportConfigModal';
import { ScheduleOperationLogTab } from '../components/ScheduleOperationLogTab';
import type { Project, ScheduleVersion, SpecSchedule, ScheduleMilestone, Milestone, MilestoneRelation, MilestoneTemplate, LinkedIpdProject, LinkedSpecSku, ScheduleSkuMilestone } from '../types';
import { format, addDays, parseISO } from 'date-fns';
import { Checkbox, DatePicker, Dropdown, Input, InputNumber, Select, Popover, Spin, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
dayjs.locale('zh-cn');
import { brandApi, projectApi, scheduleApi, relationApi, templateApi, milestoneApi, milestoneNodeApi, specApi, columnWidthApi, calendarApi, permissionConfigApi, noteApi } from '../api/services';
import { hasEditableOrSystemAdminPermission } from '../utils/permissionCodes';
import { useListViewQuery } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';
import type {
  BrandVO,
  ProjectVO,
  ProjectUpdateDTO,
  ScheduleVO,
  ScheduleDetailVO,
  SpecScheduleVO,
  ScheduleMilestoneVO,
  ScheduleColumnVO,
  MilestoneRelationVO,
  MilestoneTemplateVO,
  MilestoneNodeVO,
  UserOptionVO,
  IpdProjectListItemVO,
  IpdSkuListItemVO,
} from '../api/types';
import './ProjectSchedule.css';
import { ScheduleGanttChart } from '../components/ScheduleGanttChart';
import { IpdProjectPickerModal, formatIpdProjectLabel } from '../components/IpdProjectPickerModal';
import { IpdSkuPickerModal, type IpdSkuOccupancy } from '../components/IpdSkuPickerModal';
import { SpecTerminateModal } from '../components/SpecTerminateModal';
import { ProjectTerminateModal } from '../components/ProjectTerminateModal';
import {
  buildTerminateMatrixCells,
  expandSpecsToTerminateCols,
  type TerminateMatrixCell,
  type TerminateMatrixCol,
} from '../utils/terminateMatrix';
import {
  IpcButton,
  IpcDateRangePicker,
  IpcDatePicker,
  IpcFilterToolbar,
  IpcFormField,
  IpcModuleTabs,
  IpcTable,
  IpcModal,
  IpcRowActions,
  IpcStatusBadge,
  IpcTag,
  IpcNodeStatus,
  IpcNodeStatusPicker,
  IpcSegmented,
  IpcRichTextEditor,
  IpcRichTextView,
  ipcConfirm,
  IPC_FILTER_MULTI_SELECT_PROPS,
} from '../components/ui';
import { formatSpecRowTitle } from '../utils/specDisplay';
import { isRichTextEmpty, normalizeRichTextForSave, richTextToPlain } from '../utils/richText';
import { normalizeNodeProgress, resolveNodeTimeStatus, type NodeProgressStatus } from '../utils/nodeStatus';
import {
  projectStatusLabel,
  specStatusLabel,
  milestoneStatusLabel,
  projectStatusOptions,
} from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';
import {
  normalizeSpecScheduleStatus,
  nodeStatusCellBackground,
  nodeStatusForegroundVar,
  projectStatusBadgeVariant,
  specStatusBadgeVariant,
} from '../utils/ipcStatusVisual';

function projectStatusDisplayText(status?: string): string {
  return projectStatusLabel(status);
}

function specScheduleStatusDisplayText(status?: string): string {
  return specStatusLabel(normalizeSpecScheduleStatus(status));
}

function joinIpdField(rows: IpdProjectListItemVO[], pick: (row: IpdProjectListItemVO) => string): string {
  return rows.map(pick).map(v => (v ?? '').trim()).join(',');
}

/** 入库日期：取所选 IPD 中第一个有有效成品入库时间的项目 */
function firstIpdReceiptIso(rows: IpdProjectListItemVO[]): string | undefined {
  for (const row of rows) {
    const raw = row.finished_goods_receipt_time?.trim();
    if (!raw) continue;
    const d = dayjs(raw);
    if (d.isValid() && d.year() > 1971) return d.toISOString();
  }
  return undefined;
}

function resolveIpcBrandCodeFromIpd(rows: IpdProjectListItemVO[], brandList: BrandVO[]): string {
  const first = rows[0];
  if (!first) return '';
  const byCode = first.brand_code?.trim()
    ? brandList.find(b => b.code === first.brand_code.trim())
    : undefined;
  if (byCode) return byCode.code;
  const byName = first.brand?.trim()
    ? brandList.find(b => b.name === first.brand.trim())
    : undefined;
  if (byName) return byName.code;
  return first.brand_code?.trim() || '';
}

function ipdStoryDetailUrl(projectKey: string, workItemId: number): string {
  const key = (projectKey ?? '').trim();
  return `https://project.feishu.cn/${encodeURIComponent(key)}/story/detail/${workItemId}`;
}

function linkedIpdLabel(item: LinkedIpdProject): string {
  const name = item.name?.trim() || '';
  const code = item.projectCode?.trim() || '';
  if (name && code) return `${name} ${code}`;
  return name || code || '-';
}

function linkedIpdDetailLabel(item: LinkedIpdProject): string {
  return formatIpdProjectLabel({
    project_code: item.projectCode,
    name: item.name,
  });
}

function linkedIpdToPickerItem(item: LinkedIpdProject): IpdProjectListItemVO {
  return {
    id: item.ipdProjectId,
    project_key: item.projectKey,
    work_item_id: item.workItemId,
    name: item.name,
    project_code: item.projectCode,
    brand: item.brand ?? '',
    brand_code: item.brandCode ?? '',
    status_state_key: '',
    status_text: '',
    npd_names: '',
    spec_count: 0,
  };
}

function mapLinkedSkusFromVo(spec: SpecScheduleVO): LinkedSpecSku[] {
  return (spec.linked_skus ?? []).map(item => {
    const rawIds = item.linked_ipd_work_item_ids
      ?? (item as { linkedIpdWorkItemIds?: number[] }).linkedIpdWorkItemIds
      ?? [];
    return {
      ipdSkuId: item.ipd_sku_id,
      skuWorkItemId: item.sku_work_item_id,
      skuCode: item.sku_code ?? '',
      specText: item.spec_text ?? '',
      name: item.name ?? '',
      linkedIpdWorkItemIds: rawIds.map(Number).filter(n => Number.isFinite(n) && n > 0),
    };
  });
}

function mapSkuMilestonesFromVo(spec: SpecScheduleVO): ScheduleSkuMilestone[] {
  return (spec.sku_milestones ?? []).map(item => ({
    id: item.id,
    milestoneId: String(item.milestone_id),
    ipdSkuId: item.ipd_sku_id,
    skuWorkItemId: item.sku_work_item_id,
    status: (item.status as ScheduleMilestone['status'] | undefined) || undefined,
    actualStartDate: item.actual_start_date ?? undefined,
    actualEndDate: item.actual_end_date ?? undefined,
  }));
}

type CellSkuDisplayRow = {
  key: string;
  specText: string;
  status: ScheduleMilestone['status'];
  actualEndDate?: string;
};

/** 自动节点多 SKU 展示：未完成在前，已完成按实际结束时间升序 */
function buildAutoNodeSkuDisplayRows(
  spec: SpecSchedule,
  milestoneId: string,
): CellSkuDisplayRow[] {
  const linked = spec.linkedSkus ?? [];
  if (linked.length <= 1) return [];

  const byWorkItem = new Map<number, ScheduleSkuMilestone>();
  (spec.skuMilestones ?? [])
    .filter(m => String(m.milestoneId) === String(milestoneId))
    .forEach(m => {
      const prev = byWorkItem.get(m.skuWorkItemId);
      if (!prev || m.id > prev.id) byWorkItem.set(m.skuWorkItemId, m);
    });

  const rows: CellSkuDisplayRow[] = linked.map(sku => {
    const override = byWorkItem.get(sku.skuWorkItemId);
    const status = (override?.status as ScheduleMilestone['status'] | undefined) || 'in_progress';
    return {
      key: String(sku.skuWorkItemId || sku.ipdSkuId),
      specText: (sku.specText || sku.skuCode || sku.name || '—').trim() || '—',
      status,
      actualEndDate: override?.actualEndDate,
    };
  });

  const incomplete = rows.filter(r => r.status !== 'completed');
  const completed = rows
    .filter(r => r.status === 'completed')
    .sort((a, b) => {
      const ta = a.actualEndDate ? Date.parse(a.actualEndDate) : Number.POSITIVE_INFINITY;
      const tb = b.actualEndDate ? Date.parse(b.actualEndDate) : Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      return a.specText.localeCompare(b.specText, 'zh-CN');
    });
  return [...incomplete, ...completed];
}

/** 最新版本 + 已发布版本中仍引用 SKU 时，对应 IPD 不可移除（二者相同则只算一次） */
function collectIpdIdsLockedByLatestSkus(
  schedules: ScheduleVersion[],
  linkedIpds: LinkedIpdProject[],
): number[] {
  if (!schedules.length || !linkedIpds.length) return [];
  const latest = [...schedules].sort((a, b) => b.version - a.version)[0];
  const published = schedules.find(s => s.status === 'published');
  const targets = new Map<string, ScheduleVersion>();
  if (latest) targets.set(latest.id, latest);
  if (published) targets.set(published.id, published);

  // 用字符串比较，避免大 work_item_id 超出 JS 安全整数导致匹配失败
  const lockedWorkItems = new Set<string>();
  for (const schedule of targets.values()) {
    for (const spec of schedule.specSchedules ?? []) {
      for (const sku of spec.linkedSkus ?? []) {
        for (const wi of sku.linkedIpdWorkItemIds ?? []) {
          if (wi > 0) lockedWorkItems.add(String(wi));
        }
      }
    }
  }
  if (lockedWorkItems.size === 0) return [];
  return linkedIpds
    .filter(p => lockedWorkItems.has(String(p.workItemId)))
    .map(p => p.ipdProjectId);
}

function hasLinkedIpd(spec: SpecSchedule): boolean {
  return (spec.linkedSkus?.length ?? 0) > 0;
}

function formatSpecRowSkuCodes(spec: SpecSchedule): string {
  return (spec.linkedSkus ?? [])
    .map(s => s.skuCode?.trim())
    .filter((x): x is string => !!x)
    .join(' - ');
}

function linkedSkuToPickerItem(item: LinkedSpecSku): IpdSkuListItemVO {
  return {
    id: item.ipdSkuId,
    project_key: '',
    work_item_id: item.skuWorkItemId,
    name: item.name ?? '',
    sku_code: item.skuCode,
    spec: item.specText,
    status_state_key: '',
    status_text: '',
    source_project_work_item_id: 0,
    source_project_code: '',
    source_project_name: '',
  };
}

function openIpdStory(item: LinkedIpdProject, event?: { stopPropagation: () => void }) {
  event?.stopPropagation();
  const url = ipdStoryDetailUrl(item.projectKey, item.workItemId);
  window.open(url, '_blank', 'noopener,noreferrer');
}

function LinkedIpdCell({
  items,
  formatLabel = linkedIpdLabel,
  maxVisible = 1,
}: {
  items: LinkedIpdProject[];
  formatLabel?: (item: LinkedIpdProject) => string;
  /** 折叠前最多展示条数，超出显示 +N */
  maxVisible?: number;
}) {
  if (!items.length) return <span>—</span>;
  const visibleCount = Math.max(1, maxVisible);
  const visible = items.slice(0, visibleCount);
  const extra = items.length - visible.length;
  const popover = (
    <div className="linked-ipd-popover">
      <ul className="linked-ipd-popover__list">
        {items.map(item => (
          <li key={`${item.projectKey}-${item.workItemId}`} className="linked-ipd-popover__item">
            <span className="linked-ipd-popover__text" title={formatLabel(item)}>
              {formatLabel(item)}
            </span>
            <button
              type="button"
              className="linked-ipd-popover__link"
              title="在飞书打开"
              aria-label={`打开 ${formatLabel(item)}`}
              onClick={e => openIpdStory(item, e)}
            >
              <ExternalLink size={12} className="linked-ipd-popover__icon" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <Popover
      content={popover}
      title={null}
      trigger="hover"
      placement="bottomLeft"
      mouseEnterDelay={0.15}
      overlayClassName="linked-ipd-popover-overlay"
    >
      <div className="linked-ipd-cell" onClick={e => e.stopPropagation()}>
        {visible.map(item => (
          <span key={`${item.projectKey}-${item.workItemId}`} className="linked-ipd-tag-hit">
            <IpcTag variant="primary" title={formatLabel(item)}>{formatLabel(item)}</IpcTag>
          </span>
        ))}
        {extra > 0 ? (
          <>
            <span className="linked-ipd-ellipsis" aria-hidden>…</span>
            <span className="linked-ipd-more">+{extra}</span>
          </>
        ) : null}
      </div>
    </Popover>
  );
}

/** 有关联 IPD 时展示 IPD 编码；多条时 (n)首条 + 悬浮列出全部（可点图标跳转）；无关联时回退 IPC 项目编码 */
function ProjectCodeCell({ project }: { project: Project }) {
  const linked = project.linkedIpdProjects ?? [];
  const withCode = linked.filter(item => !!(item.projectCode?.trim()));
  if (withCode.length === 0) {
    return <span>{project.code?.trim() || '-'}</span>;
  }
  // 同编码只展示一次，保留首条用于跳转
  const uniqueByCode: LinkedIpdProject[] = [];
  const seen = new Set<string>();
  for (const item of withCode) {
    const code = item.projectCode.trim();
    if (seen.has(code)) continue;
    seen.add(code);
    uniqueByCode.push(item);
  }
  if (uniqueByCode.length === 1) {
    const only = uniqueByCode[0];
    const code = only.projectCode.trim();
    const popover = (
      <div className="project-code-popover">
        <ul className="project-code-popover__list">
          <li className="project-code-popover__item">
            <span className="project-code-popover__text" title={code}>{code}</span>
            <button
              type="button"
              className="project-code-popover__link"
              title="在飞书打开"
              aria-label={`打开 ${code}`}
              onClick={e => openIpdStory(only, e)}
            >
              <ExternalLink size={12} className="project-code-popover__icon" aria-hidden />
            </button>
          </li>
        </ul>
      </div>
    );
    return (
      <Popover
        content={popover}
        title={null}
        trigger="hover"
        placement="bottomLeft"
        mouseEnterDelay={0.12}
        overlayClassName="project-code-popover-overlay"
      >
        <span className="project-code-multi" onClick={e => e.stopPropagation()}>
          {code}
        </span>
      </Popover>
    );
  }
  const popover = (
    <div className="project-code-popover">
      <ul className="project-code-popover__list">
        {uniqueByCode.map(item => {
          const code = item.projectCode.trim();
          return (
            <li key={`${item.projectKey}-${item.workItemId}-${code}`} className="project-code-popover__item">
              <span className="project-code-popover__text" title={code}>{code}</span>
              <button
                type="button"
                className="project-code-popover__link"
                title="在飞书打开"
                aria-label={`打开 ${code}`}
                onClick={e => openIpdStory(item, e)}
              >
                <ExternalLink size={12} className="project-code-popover__icon" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
  return (
    <Popover
      content={popover}
      title={null}
      trigger="hover"
      placement="bottomLeft"
      mouseEnterDelay={0.12}
      overlayClassName="project-code-popover-overlay"
    >
      <span
        className="project-code-multi"
        onClick={e => e.stopPropagation()}
      >
        ({uniqueByCode.length}){uniqueByCode[0].projectCode.trim()}
      </span>
    </Popover>
  );
}

/** 将项目 VO 转为页面使用的 Project（brand 为 brand_code，展示时用品牌名） */
function projectVOToProject(vo: ProjectVO, brandName?: string): Project {
  return {
    id: String(vo.id),
    code: vo.code,
    name: vo.name,
    brand: brandName ?? vo.brand,
    category: vo.category,
    status: vo.status,
    launchDate: vo.launch_date ?? vo.expected_launch_date,
    templateId: vo.template_id != null && vo.template_id > 0 ? vo.template_id : undefined,
    templateName: vo.template_name?.trim() || undefined,
    scheduleCount: vo.schedule_count,
    currentScheduleId: vo.current_schedule_id != null ? String(vo.current_schedule_id) : undefined,
    currentScheduleName: vo.current_schedule_name,
    currentScheduleVersion: vo.current_schedule_version,
    currentScheduleStatus: vo.current_schedule_status,
    currentScheduleExpectedLaunchDate: vo.current_schedule_expected_launch_date ?? vo.expected_launch_date,
    currentScheduleSpecCount: vo.current_schedule_spec_count,
    createdAt: vo.current_schedule_create_time ?? vo.create_time ?? format(new Date(), 'yyyy-MM-dd'),
    updatedAt: vo.current_schedule_update_time ?? vo.update_time ?? format(new Date(), 'yyyy-MM-dd'),
    createdBy: vo.current_schedule_create_user_name,
    updatedBy: vo.current_schedule_update_user_name,
    creatorId: vo.current_schedule_create_user_id,
    updaterId: vo.current_schedule_update_user_id,
    linkedIpdProjects: (vo.ipd_projects ?? []).map(item => ({
      ipdProjectId: item.ipd_project_id,
      projectKey: item.project_key ?? '',
      workItemId: item.work_item_id,
      projectCode: item.project_code ?? '',
      name: item.name ?? '',
      brand: item.brand ?? '',
      brandCode: item.brand_code ?? '',
    })),
    npdNames: vo.npd_names?.trim() || '',
  };
}

/** 项目更新接口 body（品牌为 brand_code）；有 templateId 时一并带上以免被后端忽略 */
function buildProjectUpdatePayload(p: Project): ProjectUpdateDTO {
  const body: ProjectUpdateDTO = {
    name: p.name,
    brand: p.brand,
    category: p.category ?? '',
    status: p.status ?? 'planning',
    launchDate: p.launchDate,
  };
  if (p.templateId != null && p.templateId > 0) {
    body.templateId = p.templateId;
  }
  return body;
}

interface DragColumnState {
  versionId: string | null;
  dragIndex: number | null;
  dropIndex: number | null;
}

const MIN_COLUMN_WIDTH = 80;
const DEFAULT_COLUMN_WIDTH = 150;
/** 列内出现 yyyy-MM-dd 时的最小列宽：标签 + 两段完整日期 + 分隔符 + 内边距 */
const YMD_COLUMN_MIN_WIDTH = 210;
const DEFAULT_PROJECT_LIST_PAGE_SIZE = 20;
/** 项目列表默认状态：待开始、进行中 */
const DEFAULT_PROJECT_LIST_STATUSES = ['planning', 'in_progress'];

type RelationDependencyDirection = 'parent' | 'next';
type RelationConfigStatus = 'manual' | 'configured' | 'pending';

type ProjectListFilters = {
  filterName: string;
  filterCode: string;
  filterBrand: string;
  filterLaunchDateRange: [string, string] | null;
  filterScheduleCreatorIds: number[];
  filterStatuses: string[];
};

const EMPTY_PROJECT_LIST_FILTERS: ProjectListFilters = {
  filterName: '',
  filterCode: '',
  filterBrand: '',
  filterLaunchDateRange: null,
  filterScheduleCreatorIds: [],
  filterStatuses: [],
};

const DEFAULT_PROJECT_LIST_FILTERS: ProjectListFilters = {
  ...EMPTY_PROJECT_LIST_FILTERS,
  filterStatuses: [...DEFAULT_PROJECT_LIST_STATUSES],
};

interface ProjectListFilterOverrides {
  filterName?: string;
  filterCode?: string;
  filterBrand?: string;
  filterLaunchDateRange?: [string, string] | null;
  filterScheduleCreatorIds?: number[];
  filterStatuses?: string[];
}

function yearFromDateString(s: string | undefined | null): number | null {
  if (s == null || typeof s !== 'string') return null;
  const t = s.trim();
  if (!t) return null;
  const d = parseISO(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

/** 收集排期中出现的年份，并向外扩 2 年，供工作日历接口预加载（自动推算可能跨年） */
function collectYearsFromSchedules(
  schedules: ScheduleVersion[],
  extraDate?: string | undefined,
): number[] {
  const ys = new Set<number>();
  const addY = (y: number | null) => {
    if (y != null && Number.isFinite(y)) ys.add(y);
  };
  addY(yearFromDateString(extraDate));
  for (const sch of schedules) {
    addY(yearFromDateString(sch.expectedLaunchDate));
    for (const sp of sch.specSchedules ?? []) {
      for (const m of sp.milestones ?? []) {
        addY(yearFromDateString(m.startDate));
        addY(yearFromDateString(m.endDate));
        addY(yearFromDateString(m.actualStartDate));
        addY(yearFromDateString(m.actualEndDate));
      }
    }
  }
  const cy = new Date().getFullYear();
  addY(cy - 1);
  addY(cy);
  addY(cy + 1);
  if (ys.size === 0) {
    return [cy - 2, cy - 1, cy, cy + 1, cy + 2];
  }
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const out = new Set<number>();
  for (let y = min - 2; y <= max + 2; y++) out.add(y);
  return [...out].sort((a, b) => a - b);
}

const ProjectSchedule: FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const scheduleIdFromQuery = searchParams.get('scheduleId');
  useIpcStatusEnums();

  const routeProjectId = (() => {
    const pid = projectId ? Number(projectId) : NaN;
    return Number.isFinite(pid) && pid > 0 ? pid : null;
  })();
  const isDetailRoute = routeProjectId != null;

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [detailProjectLoading, setDetailProjectLoading] = useState(isDetailRoute);
  const projectListFetchedRef = useRef(false);

  // 明细页左上角：项目名称/品牌/预计入库时间的内联编辑
  const [editingProjectField, setEditingProjectField] = useState<'name' | 'brand' | 'launchDate' | null>(null);
  const [projectNameDraft, setProjectNameDraft] = useState<string>('');
  const [projectBrandDraft, setProjectBrandDraft] = useState<string>('');
  const [projectLaunchDateDraft, setProjectLaunchDateDraft] = useState<string | undefined>(undefined);
  const [projectSaving, setProjectSaving] = useState(false);

  const [projectSchedules, setProjectSchedules] = useState<ScheduleVersion[]>([]);
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editingSpecId, setEditingSpecId] = useState<string | null>(null);
  const [showSkuPickerModal, setShowSkuPickerModal] = useState(false);
  const [skuPickerScheduleId, setSkuPickerScheduleId] = useState<string | null>(null);
  const [skuPickerSpecId, setSkuPickerSpecId] = useState<string | null>(null);
  /** edit=改关联；copy=复制规格前选 IPD SKU；view=只读查看 */
  const [skuPickerMode, setSkuPickerMode] = useState<'edit' | 'copy' | 'view'>('edit');
  const [skuPickerCopySourceSpecId, setSkuPickerCopySourceSpecId] = useState<string | null>(null);
  const [skuPickerSelected, setSkuPickerSelected] = useState<IpdSkuListItemVO[]>([]);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pauseTarget, setPauseTarget] = useState<{
    scheduleId: string;
    spec: SpecSchedule;
    title: string;
    milestones: Milestone[];
    cols: TerminateMatrixCol[];
    cells: Record<string, TerminateMatrixCell>;
  } | null>(null);
  const [projectTerminateOpen, setProjectTerminateOpen] = useState(false);
  const [projectTerminateLoading, setProjectTerminateLoading] = useState(false);
  const [projectTerminateTarget, setProjectTerminateTarget] = useState<{
    scheduleId: string;
    milestones: Milestone[];
    cols: TerminateMatrixCol[];
    cells: Record<string, TerminateMatrixCell>;
  } | null>(null);
  /** 各版本是否显示已终止规格行（默认不显示） */
  const [showTerminatedByScheduleId, setShowTerminatedByScheduleId] = useState<Record<string, boolean>>({});
  const [editingCell, setEditingCell] = useState<{
    versionId: string;
    specId: string;
    milestoneId: string;
    field: 'start' | 'end' | 'actualStart' | 'actualEnd';
  } | null>(null);
  const [editingCellNote, setEditingCellNote] = useState<{versionId: string, specId: string, milestoneId: string} | null>(null);
  const [columnConfigVersionId, setColumnConfigVersionId] = useState<string | null>(null);
  const [tempSelectedMilestones, setTempSelectedMilestones] = useState<string[]>([]);
  // 配置列弹框数据源：所有里程碑节点（来自 MilestoneNode 接口）
  const [allMilestoneNodes, setAllMilestoneNodes] = useState<MilestoneNodeVO[]>([]);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  
  // 里程碑关联配置状态
  const [relationConfigVersionId, setRelationConfigVersionId] = useState<string | null>(null);
  const [tempRelations, setTempRelations] = useState<MilestoneRelation[]>([]);
  const [activeMilestoneId, setActiveMilestoneId] = useState<string | null>(null);
  const [relationDirectionDrafts, setRelationDirectionDrafts] = useState<Record<string, RelationDependencyDirection>>({});
  /** 保存校验失败后，在表单上高亮必填项（不弹 toast） */
  const [relationShowFieldErrors, setRelationShowFieldErrors] = useState(false);
  
  // 新建项目弹框
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [createProjectForm, setCreateProjectForm] = useState<{
    code: string;
    name: string;
    brand: string;
    launchDate?: string;
    templateId: string | number;
  }>({ code: '', name: '', brand: '', launchDate: undefined, templateId: '' });
  const [createProjectErrors, setCreateProjectErrors] = useState<{
    ipd?: string;
    code?: string;
    name?: string;
    brand?: string;
    launchDate?: string;
    templateId?: string;
  }>({});
  const [selectedIpdProjects, setSelectedIpdProjects] = useState<IpdProjectListItemVO[]>([]);
  const [showIpdPickerModal, setShowIpdPickerModal] = useState(false);
  const [ipdPickerMode, setIpdPickerMode] = useState<'create' | 'replace'>('create');
  const [templates, setTemplates] = useState<MilestoneTemplate[]>([]);
  const [brands, setBrands] = useState<BrandVO[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [createProjectLoading, setCreateProjectLoading] = useState(false);
  // 基于生效版本调整时的版本名称弹框
  const [showVersionNameModal, setShowVersionNameModal] = useState(false);
  const [versionNameInput, setVersionNameInput] = useState('');
  const [versionLaunchDateInput, setVersionLaunchDateInput] = useState<string | undefined>(undefined);
  const [pendingEditVersionId, setPendingEditVersionId] = useState<string | null>(null);
  /** 项目与排期的增删改：可编辑或系统管理 */
  const [canMutateSchedule, setCanMutateSchedule] = useState(false);
  /** 有编辑权限且当前选中项目未终止（终止为终态，仅允许删除项目） */
  const canMutateProject =
    canMutateSchedule && (selectedProject?.status ?? '').trim().toLowerCase() !== 'terminated';
  const [refreshingMilestoneStatus, setRefreshingMilestoneStatus] = useState(false);

  const [columnWidths, setColumnWidths] = useState<Record<string, Record<string, number>>>({});
  
  // 列拖拽排序状态
  const [dragColumn, setDragColumn] = useState<DragColumnState>({
    versionId: null,
    dragIndex: null,
    dropIndex: null
  });
  
  // 用于滚动定位的ref
  const versionCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const mainContentRef = useRef<HTMLDivElement>(null);
  /** 「数据」Tab 内滚动容器，用于左侧点击版本时滚动定位 */
  const scheduleDataTabScrollRef = useRef<HTMLDivElement>(null);
  /** 项目详情右侧主区 Tab：数据 | 甘特图 | 操作日志 */
  const [scheduleDetailTab, setScheduleDetailTab] = useState<'data' | 'gantt' | 'oplog'>('data');
  /** 详情页左侧版本导航是否收起（默认收起，让出主表空间） */
  const [versionSidebarCollapsed, setVersionSidebarCollapsed] = useState(true);
  // 保存时用最新 state，避免闭包拿到旧数据导致接口传参不是修改后的值
  const projectSchedulesRef = useRef<ScheduleVersion[]>([]);
  projectSchedulesRef.current = projectSchedules;
  // 导入配置弹窗
  const [showImportModal, setShowImportModal] = useState(false);
  // 记录当前版本中被改过名称/状态的规格（避免“完成”时无差别调用更新接口）
  const dirtySpecNameIdsRef = useRef<Set<string>>(new Set());
  // 进入编辑时保存的快照，用于“完成”时判断是否有改动，无改动则不调接口
  const editSnapshotRef = useRef<string | null>(null);
  /** 进度同步聚焦时的原文，失焦时有变化才保存 */
  const versionNotesFocusRef = useRef<Record<string, string>>({});

  /** 日历日期 -> 是否工作日（与 ipc_work_calendars_tbl：0=工作日，1=非工作日 一致） */
  const workCalendarByDateRef = useRef<Map<string, boolean>>(new Map());
  const loadedCalendarYearsRef = useRef<Set<number>>(new Set());

  /** 甘特图时间轴：仅当工作日历中有该日且标记为非工作日时置灰 */
  const isGanttNonWorkingDay = useCallback((d: Date) => {
    const key = format(d, 'yyyy-MM-dd');
    const map = workCalendarByDateRef.current;
    if (!map.has(key)) return false;
    return map.get(key) !== true;
  }, []);

  /** 按缺失年份的闭区间一次请求工作日历（列表页不触发，见 loadProjectSchedules / 编辑排期 / 加载版本明细） */
  const ensureWorkCalendarsLoadedForYears = useCallback(async (years: number[]) => {
    const uniq = [...new Set(years)].filter(y => Number.isFinite(y)).sort((a, b) => a - b);
    if (uniq.length === 0) return;
    const missing = uniq.filter(y => !loadedCalendarYearsRef.current.has(y));
    if (missing.length === 0) return;
    const fetchMin = Math.min(...missing);
    const fetchMax = Math.max(...missing);
    try {
      const list = await calendarApi.getList({ year: fetchMin, toYear: fetchMax });
      const map = workCalendarByDateRef.current;
      for (const row of list) {
        const key = format(parseISO(row.calendar_date), 'yyyy-MM-dd');
        map.set(key, row.is_working_day === 0);
      }
      for (let y = fetchMin; y <= fetchMax; y++) {
        loadedCalendarYearsRef.current.add(y);
      }
    } catch (e) {
      console.error('工作日历加载失败', fetchMin, fetchMax, e);
    }
  }, []);

  // 筛选条件（applied 写入当前 Tab 列表会话）
  const {
    draft: projectListDraft,
    patchDraft: patchProjectListDraft,
    applied: projectListApplied,
    applyIfChanged: applyProjectListIfChanged,
    patchAndApply: patchAndApplyProjectList,
    reset: resetProjectListQuery,
    clearAll: clearProjectListQuery,
    commitApplied: commitProjectListApplied,
    page: projectListPage,
    pageSize: projectListPageSize,
    setPagination: setProjectListPagination,
  } = useListViewQuery<ProjectListFilters>({
    listViewKey: LIST_VIEW_KEYS.schedule,
    defaultFilters: DEFAULT_PROJECT_LIST_FILTERS,
    emptyFilters: EMPTY_PROJECT_LIST_FILTERS,
    defaultPageSize: DEFAULT_PROJECT_LIST_PAGE_SIZE,
  });
  const [scheduleCreatorUsers, setScheduleCreatorUsers] = useState<UserOptionVO[]>([]);
  const [projectListTotal, setProjectListTotal] = useState(0);
  const [projectListLoading, setProjectListLoading] = useState(false);

  // 品牌列表（来自 brand_tbl，新建项目下拉用）
  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const list = await brandApi.getList();
      setBrands(list ?? []);
    } catch (e) {
      console.error('加载品牌列表失败', e);
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  // 里程碑模板列表（按需加载：新建项目 / 列表关联模板下拉打开时）
  const templatesFetchedRef = useRef(false);
  const templatesCacheRef = useRef<MilestoneTemplate[]>([]);
  const templatesLoadPromiseRef = useRef<Promise<MilestoneTemplate[]> | null>(null);
  const loadTemplates = useCallback(async (): Promise<MilestoneTemplate[]> => {
    if (templatesFetchedRef.current) {
      return templatesCacheRef.current;
    }
    if (templatesLoadPromiseRef.current) {
      return templatesLoadPromiseRef.current;
    }
    templatesLoadPromiseRef.current = (async () => {
      try {
        const res = await templateApi.getList({ pageNum: 1, pageSize: 9999 });
        const list = (res?.list ?? []) as MilestoneTemplateVO[];
        // 只用于下拉和默认模板选择，这里不需要里程碑明细
        const mapped: MilestoneTemplate[] = list.map(vo => ({
          id: vo.id,
          name: vo.name ?? '',
          description: vo.description ?? '',
          milestones: [],
          createdAt: vo.create_time,
          updatedAt: vo.update_time,
        }));
        templatesCacheRef.current = mapped;
        templatesFetchedRef.current = true;
        setTemplates(mapped);
        return mapped;
      } catch (e) {
        console.error('加载里程碑模板失败', e);
        setTemplates([]);
        return [];
      } finally {
        templatesLoadPromiseRef.current = null;
      }
    })();
    return templatesLoadPromiseRef.current;
  }, []);

  // 加载所有里程碑节点（按需：打开「配置列」/暂停弹窗时）
  const milestoneNodesFetchedRef = useRef(false);
  const milestoneNodesLoadPromiseRef = useRef<Promise<MilestoneNodeVO[]> | null>(null);
  const loadAllMilestoneNodes = useCallback(async (): Promise<MilestoneNodeVO[]> => {
    if (milestoneNodesFetchedRef.current && allMilestoneNodes.length > 0) {
      return allMilestoneNodes;
    }
    if (milestoneNodesLoadPromiseRef.current) {
      return milestoneNodesLoadPromiseRef.current;
    }
    milestoneNodesLoadPromiseRef.current = (async () => {
      try {
        const res = await milestoneNodeApi.getList({ pageNum: 1, pageSize: 9999 });
        const list = res?.list ?? [];
        setAllMilestoneNodes(list);
        milestoneNodesFetchedRef.current = true;
        return list;
      } catch (e) {
        console.error('加载里程碑节点失败', e);
        setAllMilestoneNodes([]);
        return [];
      } finally {
        milestoneNodesLoadPromiseRef.current = null;
      }
    })();
    return milestoneNodesLoadPromiseRef.current;
  }, [allMilestoneNodes]);

  /** completion_method=auto：完成只能由自动逻辑驱动，禁止任何手动改为已完成 */
  const isAutoCompletionNode = useCallback(
    (milestoneId: string | number | undefined | null): boolean => {
      if (milestoneId == null || milestoneId === '') return false;
      const id = String(milestoneId);
      const node = allMilestoneNodes.find(n => String(n.id) === id);
      if (!node) return false;
      const method = (node.completion_method ?? node.completionMethod ?? 'manual')
        .toString()
        .trim()
        .toLowerCase();
      return method === 'auto';
    },
    [allMilestoneNodes],
  );

  /** 拉取项目列表（分页 + 筛选）；filterOverrides 用于重置筛选后立即请求，避免 setState 异步导致条件未清空 */
  const fetchProjectList = useCallback(
    async (
      pageNum: number,
      pageSize: number,
      filterOverrides?: ProjectListFilterOverrides
    ) => {
      const name = filterOverrides?.filterName !== undefined ? filterOverrides.filterName : projectListApplied.filterName;
      const code = filterOverrides?.filterCode !== undefined ? filterOverrides.filterCode : projectListApplied.filterCode;
      const brand = filterOverrides?.filterBrand !== undefined ? filterOverrides.filterBrand : projectListApplied.filterBrand;
      const launchRange =
        filterOverrides?.filterLaunchDateRange !== undefined
          ? filterOverrides.filterLaunchDateRange
          : projectListApplied.filterLaunchDateRange;
      const creatorIds =
        filterOverrides?.filterScheduleCreatorIds !== undefined
          ? filterOverrides.filterScheduleCreatorIds
          : projectListApplied.filterScheduleCreatorIds;
      const statuses =
        filterOverrides?.filterStatuses !== undefined
          ? filterOverrides.filterStatuses
          : projectListApplied.filterStatuses;

      setProjectListLoading(true);
      try {
        const params: Parameters<typeof projectApi.getList>[0] = { pageNum, pageSize };
        if (name.trim()) params.keyword = name.trim();
        if (code.trim()) params.code = code.trim();
        if (brand) params.brand = brand;
        if (launchRange?.[0] && launchRange?.[1]) {
          params.launchDateStart = launchRange[0];
          params.launchDateEnd = launchRange[1];
        }
        if (creatorIds.length) params.creatorIds = creatorIds;
        if (statuses.length) params.status = statuses.join(',');
        const res = await projectApi.getList(params);
        const list = res.list ?? [];
        setProjects(list.map(vo => projectVOToProject(vo)));
        setProjectListTotal(res.total ?? 0);
        setProjectListPagination(res.pageNum ?? pageNum, res.pageSize ?? pageSize);
        projectListFetchedRef.current = true;
      } catch (e) {
        console.error('加载项目列表失败', e);
        message.error('加载项目列表失败');
      } finally {
        setProjectListLoading(false);
      }
    },
    [projectListApplied, setProjectListPagination]
  );

  // 列表页才拉品牌和项目列表；直达详情不挡首屏
  useEffect(() => {
    if (projectId) return;
    const loadData = async () => {
      await loadBrands();
      await fetchProjectList(projectListPage, projectListPageSize);
    };
    void loadData();
    // fetchProjectList 勿加入依赖：否则筛选 state 变化会重复跑整套初始化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBrands]);

  const fetchProjectListIfApplied = useCallback((
    coords: { applied: ProjectListFilters; page: number; pageSize: number } | null,
  ) => {
    if (!coords) return;
    void fetchProjectList(coords.page, coords.pageSize, coords.applied);
  }, [fetchProjectList]);

  const handleFilterSearch = useCallback(() => {
    fetchProjectListIfApplied(applyProjectListIfChanged());
  }, [applyProjectListIfChanged, fetchProjectListIfApplied]);

  const clearAllProjectListFilters = useCallback(() => {
    fetchProjectListIfApplied(clearProjectListQuery());
  }, [clearProjectListQuery, fetchProjectListIfApplied]);

  const resetProjectListFiltersToDefault = useCallback(() => {
    fetchProjectListIfApplied(resetProjectListQuery());
  }, [resetProjectListQuery, fetchProjectListIfApplied]);

  const handleRefreshMilestoneStatus = useCallback(async () => {
    if (!canMutateSchedule || refreshingMilestoneStatus) return;
    setRefreshingMilestoneStatus(true);
    try {
      const updatedCount = await scheduleApi.refreshMilestoneStatus();
      message.success(`刷新完成，更新了 ${updatedCount ?? 0} 条节点状态`);
      await fetchProjectList(projectListPage, projectListPageSize);
    } catch (e) {
      console.error('刷新节点状态失败', e);
      message.error(e instanceof Error ? e.message : '刷新节点状态失败');
    } finally {
      setRefreshingMilestoneStatus(false);
    }
  }, [canMutateSchedule, refreshingMilestoneStatus, fetchProjectList, projectListPage, projectListPageSize]);

  // 从路由参数恢复“项目明细”视图（刷新后仍停留在明细页）
  useEffect(() => {
    const pid = projectId ? Number(projectId) : NaN;
    if (!projectId || !Number.isFinite(pid) || pid <= 0) {
      setDetailProjectLoading(false);
      // 回到列表页时确保状态清空（避免旧 state 误显示明细）
      if (!projectId) {
        setSelectedProject(null);
        setProjectSchedules([]);
        setActiveVersionId(null);
        setEditingVersionId(null);
        if (!projectListFetchedRef.current) {
          void loadBrands();
          void fetchProjectList(projectListPage, projectListPageSize);
        }
      }
      return;
    }

    if (selectedProject && Number(selectedProject.id) === pid) {
      setDetailProjectLoading(false);
      return;
    }

    let cancelled = false;
    setDetailProjectLoading(true);
    const loadProject = async () => {
      try {
        const vo = await projectApi.getById(pid);
        if (cancelled) return;
        setSelectedProject(projectVOToProject(vo));
        void loadBrands();
      } catch (e) {
        if (cancelled) return;
        console.error('加载项目失败', e);
        message.error('加载项目失败');
        navigate('/schedule', { replace: true });
      } finally {
        if (!cancelled) setDetailProjectLoading(false);
      }
    };

    void loadProject();
    return () => {
      cancelled = true;
    };
    // selectedProject 仅用于直达详情时跳过重复 getById（从列表点入已有本地项目）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, navigate, loadBrands, fetchProjectList, projectListPage, projectListPageSize]);

  useEffect(() => {
    if (!selectedProject) setScheduleDetailTab('data');
  }, [selectedProject]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const codes = await permissionConfigApi.getMyPermissionCodes();
        if (!cancelled) setCanMutateSchedule(hasEditableOrSystemAdminPermission(codes));
      } catch (e) {
        console.error('加载排期操作权限失败', e);
        if (!cancelled) setCanMutateSchedule(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!canMutateProject) {
      setEditingVersionId(null);
      setEditingSpecId(null);
      setEditingCell(null);
      setEditingCellNote(null);
      setEditingProjectField(null);
      setShowVersionNameModal(false);
      setPendingEditVersionId(null);
      setColumnConfigVersionId(null);
      setRelationConfigVersionId(null);
      setActiveMilestoneId(null);
      setRelationShowFieldErrors(false);
      setShowIpdPickerModal(false);
      setShowSkuPickerModal(false);
      setShowImportModal(false);
      setProjectTerminateOpen(false);
      setPauseModalOpen(false);
    }
  }, [canMutateProject]);

  // ========== 项目基础信息编辑（名称/品牌/预计入库时间） ==========
  const startEditProjectField = (field: 'name' | 'brand' | 'launchDate') => {
    if (!canMutateProject || !selectedProject) return;

    setProjectNameDraft(selectedProject.name ?? '');
    setProjectBrandDraft(selectedProject.brand ?? '');
    setProjectLaunchDateDraft(selectedProject.launchDate ?? undefined);
    setEditingProjectField(field);
  };

  const exitEditProjectField = () => {
    setEditingProjectField(null);
    setProjectSaving(false);
  };

  const saveProjectField = async (patch: {
    name?: string;
    brand?: string;
    launchDate?: string | undefined;
  }) => {
    if (!canMutateProject || !selectedProject || projectSaving) return;
    setProjectSaving(true);
    try {
      const name = patch.name !== undefined ? patch.name : selectedProject.name;
      const brand = patch.brand !== undefined ? patch.brand : selectedProject.brand;
      const launchDate =
        patch.launchDate !== undefined ? patch.launchDate : selectedProject.launchDate;

      const next: Project = {
        ...selectedProject,
        name,
        brand,
        launchDate: launchDate ?? undefined,
      };
      await projectApi.update(Number(selectedProject.id), buildProjectUpdatePayload(next));

      setSelectedProject(prev =>
        prev
          ? {
              ...prev,
              name,
              brand,
              launchDate: launchDate ?? undefined,
            }
          : prev
      );
      setProjects(prev =>
        prev.map(p =>
          p.id === selectedProject.id
            ? { ...p, name, brand, launchDate: launchDate ?? undefined }
            : p
        )
      );
      message.success('保存成功');
      exitEditProjectField();
    } catch (e) {
      console.error('保存项目基础信息失败', e);
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setProjectSaving(false);
    }
  };

  const handleAssociateProjectTemplate = useCallback(async (project: Project, templateId: number) => {
    if (!canMutateSchedule) return;
    if ((project.status ?? '').trim().toLowerCase() === 'terminated') {
      message.warning('已终止项目不可操作');
      return;
    }
    try {
      await projectApi.update(Number(project.id), {
        ...buildProjectUpdatePayload(project),
        templateId,
      });
      const templateName = templates.find(t => Number(t.id) === templateId)?.name;
      setProjects(prev => prev.map(p => (p.id === project.id ? { ...p, templateId, templateName } : p)));
      setSelectedProject(prev => (prev && prev.id === project.id ? { ...prev, templateId, templateName } : prev));
      message.success('已关联模板');
    } catch (e) {
      console.error('关联模板失败', e);
      message.error(e instanceof Error ? e.message : '关联模板失败');
    }
  }, [canMutateSchedule, templates]);

  // 加载项目排期列表（IncludeDetail 一次带出全部版本明细，避免 N 次 detail）
  useEffect(() => {
    const loadProjectSchedules = async () => {
      if (!selectedProject) return;
      try {
        const list = await scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true });
        const detailList = [...list]
          .sort((a, b) => b.version - a.version)
          .map(vo => mapDetailToScheduleVersion(vo as ScheduleDetailVO));

        const calendarYears = collectYearsFromSchedules(detailList, selectedProject.launchDate);
        await ensureWorkCalendarsLoadedForYears(calendarYears);
        setProjectSchedules(detailList);
        void loadAllMilestoneNodes();

        // 默认选中 URL 指定版本，否则选最新版本
        if (detailList.length > 0) {
          const preferredId = scheduleIdFromQuery && detailList.some(s => s.id === scheduleIdFromQuery)
            ? scheduleIdFromQuery
            : detailList[0].id;
          setActiveVersionId(preferredId);
        } else {
          setActiveVersionId(null);
        }
      } catch (e) {
        console.error('加载项目排期失败', e);
        message.error('加载项目排期失败');
        setProjectSchedules([]);
      }
    };
    loadProjectSchedules();
  }, [selectedProject, scheduleIdFromQuery, ensureWorkCalendarsLoadedForYears]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const users = await permissionConfigApi.getMutableUsers();
        if (!cancelled) {
          setScheduleCreatorUsers(users ?? []);
        }
      } catch (e) {
        console.error('加载创建人筛选用户列表失败', e);
        if (!cancelled) {
          setScheduleCreatorUsers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const scheduleCreatorOptions = useMemo(() => {
    return scheduleCreatorUsers.map(user => ({
      value: user.id,
      label: user.user_name || '未知用户',
    }));
  }, [scheduleCreatorUsers]);

  const projectListAppliedChips = [
    projectListApplied.filterName.trim()
      ? { key: 'name', label: `项目名称: ${projectListApplied.filterName.trim()}` }
      : null,
    projectListApplied.filterCode.trim()
      ? { key: 'code', label: `项目编码: ${projectListApplied.filterCode.trim()}` }
      : null,
    projectListApplied.filterBrand
      ? {
          key: 'brand',
          label: `品牌: ${brands.find(b => b.code === projectListApplied.filterBrand)?.name ?? projectListApplied.filterBrand}`,
        }
      : null,
    projectListApplied.filterLaunchDateRange?.[0] && projectListApplied.filterLaunchDateRange?.[1]
      ? {
          key: 'launchDate',
          label: `入库: ${projectListApplied.filterLaunchDateRange[0]} ~ ${projectListApplied.filterLaunchDateRange[1]}`,
        }
      : null,
    ...projectListApplied.filterScheduleCreatorIds.map(id => {
      const label = scheduleCreatorOptions.find(o => o.value === id)?.label ?? String(id);
      return { key: `creator:${id}`, label: `创建人: ${label}` };
    }),
    ...projectListApplied.filterStatuses.map(status => ({
      key: `status:${status}`,
      label: `状态: ${projectStatusLabel(status)}`,
    })),
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeProjectListAppliedChip = (key: string) => {
    const next: ProjectListFilters = { ...projectListApplied };
    if (key === 'name') next.filterName = '';
    else if (key === 'code') next.filterCode = '';
    else if (key === 'brand') next.filterBrand = '';
    else if (key === 'launchDate') next.filterLaunchDateRange = null;
    else if (key.startsWith('creator:')) {
      const id = Number(key.slice('creator:'.length));
      next.filterScheduleCreatorIds = next.filterScheduleCreatorIds.filter(x => x !== id);
    } else if (key.startsWith('status:')) {
      const status = key.slice('status:'.length);
      next.filterStatuses = next.filterStatuses.filter(x => x !== status);
    } else {
      return;
    }
    fetchProjectListIfApplied(commitProjectListApplied(next));
  };

  // 当选中的版本发生变化时，加载该版本的排期明细（里程碑、规格、关系等）
  useEffect(() => {
    const loadScheduleDetail = async () => {
      if (!activeVersionId) return;
      // 若该版本明细已加载（有列或有规格），则不重复请求
      const existing = projectSchedulesRef.current.find(s => s.id === activeVersionId);
      if (existing && ((existing.milestoneColumns?.length ?? 0) > 0 || (existing.specSchedules?.length ?? 0) > 0)) {
        return;
      }
      try {
        const detail = await scheduleApi.getById(Number(activeVersionId));
        const mapped = mapDetailToScheduleVersion(detail);
        const merged = projectSchedulesRef.current.map(s => (s.id === activeVersionId ? mapped : s));
        const calendarYears = collectYearsFromSchedules(merged, selectedProject?.launchDate);
        await ensureWorkCalendarsLoadedForYears(calendarYears);
        setProjectSchedules(prev =>
          prev.map(s => (s.id === activeVersionId ? { ...mapped } : s)),
        );
      } catch (e) {
        console.error('加载排期明细失败', e);
        message.error('加载排期明细失败');
      }
    };
    loadScheduleDetail();
  }, [activeVersionId, selectedProject?.launchDate, ensureWorkCalendarsLoadedForYears]);

  // 获取指定版本的里程碑列配置
  const getVersionMilestoneColumns = (schedule: ScheduleVersion): Milestone[] => {
    if (schedule.milestoneColumns && schedule.milestoneColumns.length > 0) {
      // 列位置始终按配置顺序显示；正排/倒排只影响自动计算方向，不改变列顺序
      return [...schedule.milestoneColumns].sort((a, b) => a.order - b.order);
    }
    return [];
  };

  const formatYMD = (value?: string) => {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : value;
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const d = dayjs(value);
    if (!d.isValid()) return '-';
    if (d.year() < 2000) return '-';
    return d.format('YYYY-MM-DD HH:mm');
  };

  // 获取列宽（用户保存值；缺省用默认；规格列略宽以展示双行）
  const getColumnWidth = (versionId: string, columnId: string): number => {
    const stored = columnWidths[versionId]?.[columnId];
    if (stored) return stored;
    if (columnId === 'specName') return 200;
    if (columnId === 'specStatus') return 118;
    return DEFAULT_COLUMN_WIDTH;
  };

  /** 里程碑列是否含跨年日期（需展示 yyyy-MM-dd） */
  const milestoneColumnHasCrossYearDate = useCallback((schedule: ScheduleVersion, milestoneId: string): boolean => {
    const currentYear = new Date().getFullYear();
    for (const spec of schedule.specSchedules ?? []) {
      const m = spec.milestones?.find(item => String(item.milestoneId) === String(milestoneId));
      if (!m) continue;
      for (const raw of [m.startDate, m.endDate, m.actualStartDate, m.actualEndDate]) {
        if (!raw) continue;
        try {
          if (parseISO(raw).getFullYear() !== currentYear) return true;
        } catch {
          // ignore invalid
        }
      }
    }
    return false;
  }, []);

  /** 展示用列宽：有 yyyy-MM-dd 时抬高整列，避免日期换行 */
  const getMilestoneColumnWidth = useCallback((schedule: ScheduleVersion, milestoneId: string): number => {
    const stored = getColumnWidth(schedule.id, milestoneId);
    if (milestoneColumnHasCrossYearDate(schedule, milestoneId)) {
      return Math.max(stored, YMD_COLUMN_MIN_WIDTH);
    }
    return stored;
  }, [columnWidths, milestoneColumnHasCrossYearDate]);

  // 将后端明细返回的列宽同步到页面状态（以版本为维度）
  useEffect(() => {
    const widths: Record<string, Record<string, number>> = {};
    projectSchedules.forEach(s => {
      if (s.columnWidths) {
        widths[s.id] = s.columnWidths;
      }
    });
    setColumnWidths(widths);
  }, [projectSchedules]);

  const handleSpecColumnWidthsChange = useCallback((versionId: string, widths: Record<string, number>) => {
    setColumnWidths(prev => ({ ...prev, [versionId]: widths }));
  }, []);

  const handleSpecColumnWidthCommit = useCallback(async (versionId: string, columnKey: string, width: number) => {
    // specName / specStatus 列宽仅前端保存，不落库
    if (columnKey === 'specName' || columnKey === 'specStatus') return;
    if (!canMutateProject) return;
    try {
      await columnWidthApi.save(Number(versionId), {
        widths: [{ milestone_id: Number(columnKey), column_width: width }],
      });
    } catch (e) {
      console.error('保存列宽失败', e);
      message.error(e instanceof Error ? e.message : '保存列宽失败');
    }
  }, [canMutateProject]);

  // 滚动到指定版本（在「数据」Tab 内滚动）
  const scrollToVersion = (versionId: string) => {
    setActiveVersionId(versionId);
    if (scheduleDetailTab !== 'data') return;
    requestAnimationFrame(() => {
      const element = versionCardRefs.current.get(versionId);
      const scrollRoot = scheduleDataTabScrollRef.current ?? mainContentRef.current;
      if (!element || !scrollRoot) return;
      const rootRect = scrollRoot.getBoundingClientRect();
      const elRect = element.getBoundingClientRect();
      const nextTop = scrollRoot.scrollTop + (elRect.top - rootRect.top) - 20;
      scrollRoot.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
    });
  };

  const openCreateProjectModal = async () => {
    if (!canMutateProject) return;
    await loadTemplates();
    setCreateProjectForm({
      code: '',
      name: '',
      brand: '',
      launchDate: undefined,
      templateId: '',
    });
    setSelectedIpdProjects([]);
    setCreateProjectErrors({});
    setIpdPickerMode('create');
    setShowIpdPickerModal(false);
    setShowCreateProjectModal(true);
  };

  const applySelectedIpdProjects = (rows: IpdProjectListItemVO[]) => {
    setSelectedIpdProjects(rows);
    setShowIpdPickerModal(false);
    setCreateProjectForm(f => ({
      ...f,
      code: joinIpdField(rows, r => r.project_code ?? ''),
      name: joinIpdField(rows, r => r.name ?? ''),
      brand: resolveIpcBrandCodeFromIpd(rows, brands),
      launchDate: firstIpdReceiptIso(rows),
    }));
    setCreateProjectErrors(prev => ({
      ...prev,
      ipd: undefined,
      code: undefined,
      name: undefined,
      brand: undefined,
      launchDate: undefined,
    }));
  };

  const applyLinkedIpdToProjectState = (vo: ProjectVO) => {
    const mapped = projectVOToProject(vo);
    setSelectedProject(prev => (prev
      ? {
        ...prev,
        code: mapped.code,
        name: mapped.name,
        linkedIpdProjects: mapped.linkedIpdProjects,
        npdNames: mapped.npdNames,
      }
      : mapped));
    setProjects(prev => prev.map(p => (
      p.id === String(vo.id)
        ? {
          ...p,
          code: mapped.code,
          name: mapped.name,
          linkedIpdProjects: mapped.linkedIpdProjects,
          npdNames: mapped.npdNames,
        }
        : p
    )));
  };

  const openReplaceIpdPicker = () => {
    if (!canMutateProject || !selectedProject) return;
    setIpdPickerMode('replace');
    setSelectedIpdProjects((selectedProject.linkedIpdProjects ?? []).map(linkedIpdToPickerItem));
    setShowIpdPickerModal(true);
  };

  const replaceIpdLockedIds = useMemo(() => {
    if (ipdPickerMode !== 'replace' || !selectedProject) return [];
    return collectIpdIdsLockedByLatestSkus(
      projectSchedules,
      selectedProject.linkedIpdProjects ?? [],
    );
  }, [ipdPickerMode, selectedProject, projectSchedules]);

  const handleReplaceIpdProjects = async (rows: IpdProjectListItemVO[]) => {
    if (!selectedProject) return;
    try {
      const vo = await projectApi.replaceIpdProjects(
        Number(selectedProject.id),
        rows.map(r => r.id),
      );
      applyLinkedIpdToProjectState(vo);
      setShowIpdPickerModal(false);
      message.success('已更换关联 IPD');
    } catch (e) {
      console.error('更换关联 IPD 失败', e);
      message.error(e instanceof Error ? e.message : '更换关联 IPD 失败');
    }
  };

  const handleIpdPickerOk = async (rows: IpdProjectListItemVO[]) => {
    if (ipdPickerMode === 'replace') {
      await handleReplaceIpdProjects(rows);
      return;
    }
    applySelectedIpdProjects(rows);
  };

  /** 将后端 ScheduleDetailVO 转为页面用的 ScheduleVersion 结构（列、规格、里程碑、关系等全部走接口） */
  const mapDetailToScheduleVersion = (detail: ScheduleDetailVO): ScheduleVersion => {
    const d: any = detail;

    // 后端 JSON 字段名兼容：spec_schedules/specs，milestone_relations/relations
    const columns: ScheduleColumnVO[] = (d.columns ?? []) as ScheduleColumnVO[];
    const specList: SpecScheduleVO[] = (d.spec_schedules ?? d.specs ?? []) as SpecScheduleVO[];
    const relationList: MilestoneRelationVO[] = (d.milestone_relations ?? d.relations ?? []) as MilestoneRelationVO[];

    const milestoneColumns: Milestone[] = columns
      .sort((a, b) => a.milestone_order - b.milestone_order)
      .map((c: ScheduleColumnVO) => {
        const rel = relationList.find(r => String(r.milestone_id) === String(c.milestone_id));
        return {
          id: String(c.milestone_id),
          name: c.milestone_name ?? `节点${c.milestone_order}`,
          phase: '',
          order: c.milestone_order,
          defaultLeadDays: 0,
          mergeAcrossSpecs: !!rel?.merge_across_specs,
        };
      });

    const columnWidthsFromBackend: Record<string, number> = {};
    columns.forEach(c => {
      columnWidthsFromBackend[String(c.milestone_id)] = c.column_width ?? DEFAULT_COLUMN_WIDTH;
    });

    const mapSpec = (spec: SpecScheduleVO): SpecSchedule => {
      const sourceMilestones = (spec.milestones ?? []) as ScheduleMilestoneVO[];
      const milestones: ScheduleMilestone[] = sourceMilestones.map((m: ScheduleMilestoneVO) => ({
        milestoneId: String(m.milestone_id),
        startDate: m.start_date ?? '',
        endDate: m.end_date ?? '',
        actualStartDate: m.actual_start_date ?? undefined,
        actualEndDate: m.actual_end_date ?? undefined,
        status: (m.status as ScheduleMilestone['status']) || 'pending',
      }));
      // 从接口返回的 milestones[].note 填充 cellNotes，供 getCellNote 展示
      const cellNotes = sourceMilestones.map((m: ScheduleMilestoneVO) => ({
        milestoneId: String(m.milestone_id),
        note: m.note ?? '',
      }));
      return {
        id: String(spec.id),
        specName: spec.spec_name,
        status: normalizeSpecScheduleStatus(spec.status),
        milestones,
        cellNotes,
        linkedSkus: mapLinkedSkusFromVo(spec),
        skuMilestones: mapSkuMilestonesFromVo(spec),
      };
    };

    const mapRelation = (r: MilestoneRelationVO): MilestoneRelation => ({
      id: String(r.id),
      milestoneId: String(r.milestone_id),
      parentMilestoneId: r.parent_milestone_id != null ? String(r.parent_milestone_id) : undefined,
      nextMilestoneId: r.next_milestone_id != null ? String(r.next_milestone_id) : undefined,
      durationDays: r.duration_days ?? undefined,
      timeOffsetDays: r.time_offset_days ?? 1,
      excludeWeekends: !!r.exclude_weekends,
      mergeAcrossSpecs: !!r.merge_across_specs,
      calculationMode: (r.time_method === 'auto' ? 'auto' : 'manual'),
    });

    const specSchedules: SpecSchedule[] = specList.map(mapSpec);
    const relations: MilestoneRelation[] = relationList.map(mapRelation);

    return {
      id: String(detail.id),
      projectId: String(detail.project_id),
      version: detail.version,
      name: detail.name,
      milestoneSortOrder: d.milestone_sort_order === 'desc' ? 'desc' : 'asc',
      expectedLaunchDate: detail.expected_launch_date,
      status: (detail.status as ScheduleVersion['status']) || 'draft',
      specSchedules,
      createdAt: detail.create_time,
      createdBy: detail.create_user_name,
      updatedAt: detail.update_time,
      updatedBy: detail.update_user_name,
      versionNotes: detail.version_notes,
      customMilestoneIds: undefined,
      milestoneColumns,
      columnWidths: columnWidthsFromBackend,
      milestoneRelations: relations,
    };
  };

  const handleConfirmCreateProject = async () => {
    if (!canMutateSchedule) return;
    const { code, name, brand, launchDate, templateId } = createProjectForm;
    const codeStr = code ? String(code).trim() : '';
    const nameStr = typeof name === 'string' ? name.trim() : '';
    const brandCode = typeof brand === 'string' ? brand.trim() : (Array.isArray(brand) ? String(brand[0] ?? '').trim() : '');
    const templateIdStr = templateId != null ? String(templateId).trim() : '';
    const errors: { ipd?: string; code?: string; name?: string; brand?: string; launchDate?: string; templateId?: string } = {};
    if (selectedIpdProjects.length === 0) errors.ipd = '请选择 IPD 项目';
    if (codeStr.length > 512) errors.code = '项目编码不能超过512个字符';
    if (!nameStr) errors.name = '请填写项目名称';
    if (nameStr && nameStr.length > 1024) errors.name = '项目名称不能超过1024个字符';
    if (!brandCode) errors.brand = '请选择品牌';
    if (!launchDate) errors.launchDate = '入库日期不能为空';
    if (!templateIdStr) errors.templateId = '请选择排期模板';
    if (Object.keys(errors).length > 0) {
      setCreateProjectErrors(errors);
      return;
    }
    setCreateProjectErrors({});
    setCreateProjectLoading(true);
    try {
      const created = await projectApi.create({
        code: codeStr,
        name: nameStr,
        brand: brandCode,
        launchDate: launchDate ? dayjs(launchDate).format('YYYY-MM-DD') : undefined,
        templateId: templateIdStr ? Number(templateIdStr) : undefined,
        ipdProjectIds: selectedIpdProjects.map(p => p.id),
      });
      message.success('项目创建成功');
      setShowCreateProjectModal(false);
      setSelectedIpdProjects([]);
      setCreateProjectForm({ code: '', name: '', brand: '', launchDate: undefined, templateId: '' });
      await fetchProjectList(1, projectListPageSize);
      if (created?.id != null) {
        setSelectedProject(projectVOToProject(created));
        navigate(`/schedule/${created.id}`);
      }
    } catch (e) {
      console.error('新建项目失败', e);
      message.error(e instanceof Error ? e.message : '新建项目失败');
    } finally {
      setCreateProjectLoading(false);
    }
  };

  // 删除项目（带二次确认）
  const handleDeleteProject = (project: Project) => {
    if (!canMutateSchedule || projectSaving) return;

    ipcConfirm({
      title: '确认删除项目？',
      content: '删除后该项目将从列表移除，相关排期数据也会被删除。是否继续？',
      okText: '确认删除',
      cancelText: '取消',
      danger: true,
      onOk: async () => {
        try {
          const ok = await projectApi.delete(Number(project.id));
          if (!ok) {
            message.error('删除失败');
            return;
          }

          setProjectSchedules(prev => prev.filter(s => s.projectId !== project.id));

          if (selectedProject?.id === project.id) {
            setSelectedProject(null);
            setActiveVersionId(null);
            setEditingVersionId(null);
            setProjectSchedules([]);
            navigate('/schedule');
          }

          const nextPage =
            projects.length <= 1 && projectListPage > 1 ? projectListPage - 1 : projectListPage;
          await fetchProjectList(nextPage, projectListPageSize);

          message.success('删除成功');
        } catch (e) {
          console.error('删除项目失败', e);
          message.error(e instanceof Error ? e.message : '删除失败');
        }
      },
    });
  };

  // 编辑生效版本 - 先弹出版本名称，再生成新版（新建版本的入库日期默认取项目的预计入库日期）
  const handleEditPublishedVersion = (versionId: string) => {
    if (!canMutateProject) return;
    const s = projectSchedulesRef.current.find(x => x.id === versionId);
    setPendingEditVersionId(versionId);
    setVersionNameInput(`调整版-${format(new Date(), 'MMdd')}`);
    setVersionLaunchDateInput(selectedProject?.launchDate ?? s?.expectedLaunchDate);
    setShowVersionNameModal(true);
  };

  // 草稿版本 - 打开弹窗修改版本名称
  const handleEditDraftVersionName = (schedule: ScheduleVersion) => {
    if (!canMutateProject) return;
    setPendingEditVersionId(schedule.id);
    setVersionNameInput(schedule.name);
    setVersionLaunchDateInput(schedule.expectedLaunchDate);
    setShowVersionNameModal(true);
  };

  const handleConfirmNewVersionName = async () => {
    if (!canMutateProject || !selectedProject || !pendingEditVersionId) return;
    const currentSchedule = projectSchedulesRef.current.find(s => s.id === pendingEditVersionId);
    if (!currentSchedule) return;
    const name = versionNameInput.trim() || (currentSchedule.status === 'draft' ? currentSchedule.name : `调整版-${format(new Date(), 'MMdd')}`);
    const expectedLaunchDate = versionLaunchDateInput ?? currentSchedule.expectedLaunchDate;

    // 草稿版本：仅修改名称（对接后端 update 接口）
    if (currentSchedule.status === 'draft') {
      try {
        await scheduleApi.update(Number(currentSchedule.id), {
          name,
          expected_launch_date: expectedLaunchDate,
          version_notes: currentSchedule.versionNotes || '',
        });
        setProjectSchedules(prev => prev.map(s => (s.id === currentSchedule.id ? { ...s, name, expectedLaunchDate } : s)));
        message.success('版本名称已更新');
        setShowVersionNameModal(false);
        setPendingEditVersionId(null);
      } catch (e) {
        console.error('更新版本名称失败', e);
        message.error(e instanceof Error ? e.message : '更新版本名称失败');
      }
      return;
    }

    // 生效版本：基于 source_schedule_id 生成新的调整版（对接后端 create 接口）
    try {
      const created = await scheduleApi.create({
        project_id: Number(selectedProject.id),
        name,
        expected_launch_date: expectedLaunchDate,
        source_schedule_id: Number(currentSchedule.id),
      });
      const newId = created?.id;
      if (newId == null) {
        throw new Error('创建调整版本成功，但未返回新版本ID');
      }

      // 刷新版本列表（含明细）
      const list = await scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true });
      const mappedVersions = [...list]
        .sort((a, b) => b.version - a.version)
        .map(vo => mapDetailToScheduleVersion(vo as ScheduleDetailVO));
      setProjectSchedules(mappedVersions);
      setActiveVersionId(String(newId));
      setEditingVersionId(String(newId));

      message.success('调整版本已创建');
      setShowVersionNameModal(false);
      setPendingEditVersionId(null);

      // 同步更新项目列表的版本数量
      setProjects(prev =>
        prev.map(p => (p.id === selectedProject.id ? { ...p, scheduleCount: list.length } : p))
      );
    } catch (e) {
      console.error('创建调整版本失败', e);
      message.error(e instanceof Error ? e.message : '创建调整版本失败');
    }
  };

  // 复制规格：先选关联 IPD SKU，确认后再创建
  const handleCopySpec = (versionId: string, specId: string) => {
    if (!canMutateProject || !selectedProject) return;
    const schedule = projectSchedulesRef.current.find(s => s.id === versionId);
    if (!schedule) return;
    if (schedule.status !== 'draft') {
      message.warning('只有草稿版本可以复制规格');
      return;
    }

    const specToCopy = schedule.specSchedules.find(s => s.id === specId);
    if (!specToCopy) return;

    setSkuPickerMode('copy');
    setSkuPickerCopySourceSpecId(specId);
    setSkuPickerScheduleId(versionId);
    setSkuPickerSpecId(null);
    setSkuPickerSelected([]);
    setShowSkuPickerModal(true);
  };

  // 删除规格行（对接后端接口）
  const handleDeleteSpec = async (versionId: string, specId: string) => {
    if (!canMutateProject) return;

    ipcConfirm({
      title: '确认删除规格？',
      content: '删除后该规格将从当前版本中移除。是否继续？',
      okText: '确认删除',
      cancelText: '取消',
      danger: true,
      onOk: async () => {
        try {
          await specApi.delete(Number(versionId), Number(specId));
          setProjectSchedules(prev =>
            prev.map(s => (s.id === versionId ? { ...s, specSchedules: s.specSchedules.filter(sp => sp.id !== specId) } : s)),
          );
          message.success('规格已删除');
        } catch (e) {
          console.error('删除规格失败', e);
          message.error(e instanceof Error ? e.message : '删除规格失败');
        }
      },
    });
  };

  // 更新规格名称：仅更新前端状态（不在失焦时调用接口），真正保存放在版本“完成”按钮
  const handleUpdateSpecName = (versionId: string, specId: string, newName: string) => {
    if (!canMutateProject) return;
    const name = (newName ?? '').trim();
    if (!name) {
      message.error('规格名称不能为空');
      return;
    }
    dirtySpecNameIdsRef.current.add(String(specId));
    setProjectSchedules(prev =>
      prev.map(s =>
        s.id === versionId
          ? {
          ...s,
          specSchedules: s.specSchedules.map(sp => (sp.id === specId ? { ...sp, specName: name } : sp)),
            }
          : s,
      ),
    );
    setEditingSpecId(null);
  };

  const pickProjectTerminateSchedule = (schedules: ScheduleVersion[]): ScheduleVersion | null => {
    const sorted = [...schedules].sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0));
    return sorted.find(s => s.status === 'published')
      ?? sorted.find(s => s.status === 'draft')
      ?? null;
  };

  const openTerminateSpecModal = async (schedule: ScheduleVersion, spec: SpecSchedule) => {
    if (!canMutateProject) return;
    if (schedule.status === 'archived') {
      message.warning('已归档版本不可终止规格');
      return;
    }
    const status = normalizeSpecScheduleStatus(spec.status);
    if (status === 'terminated') {
      message.warning('规格已处于终止状态');
      return;
    }
    if (status === 'completed') {
      message.warning('已完成的规格不可终止');
      return;
    }
    if (status !== 'in_progress') {
      message.warning('仅「进行中」的规格可以终止');
      return;
    }

    const cols = getVersionMilestoneColumns(schedule);
    const nodeList = await loadAllMilestoneNodes();
    const manDaysMap = new Map<string, number>();
    const autoCompletionIds = new Set<string>();
    for (const n of nodeList) {
      const id = String(n.id);
      const days = Number(n.default_man_days ?? n.defaultManDays ?? 0);
      manDaysMap.set(id, Number.isFinite(days) && days >= 0 ? days : 0);
      const method = (n.completion_method ?? n.completionMethod ?? 'manual')
        .toString()
        .trim()
        .toLowerCase();
      if (method === 'auto') autoCompletionIds.add(id);
    }
    const matrixCols = expandSpecsToTerminateCols([spec]);
    const cells = buildTerminateMatrixCells([spec], matrixCols, cols, manDaysMap, autoCompletionIds);
    setPauseTarget({
      scheduleId: schedule.id,
      spec,
      title: formatSpecRowTitle(spec),
      milestones: cols,
      cols: matrixCols,
      cells,
    });
    setPauseModalOpen(true);
  };

  const handleConfirmTerminateSpec = async (payload: {
    reason: string;
    remark: string;
    nodeStatuses: { ipdSkuId: number; milestoneId: number; status: string }[];
  }) => {
    if (!pauseTarget || !selectedProject) return;
    setPauseLoading(true);
    try {
      await specApi.terminate(Number(pauseTarget.scheduleId), Number(pauseTarget.spec.id), {
        reason: payload.reason,
        remark: payload.remark || undefined,
        node_statuses: payload.nodeStatuses.map(n => ({
          ipd_sku_id: n.ipdSkuId || undefined,
          milestone_id: n.milestoneId,
          status: n.status,
        })),
      });
      const list = await scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true });
      const mappedVersions = [...list]
        .sort((a, b) => b.version - a.version)
        .map(item => mapDetailToScheduleVersion(item as ScheduleDetailVO));
      setProjectSchedules(mappedVersions);

      const selectedSkuIds = new Set(
        payload.nodeStatuses.map(n => n.ipdSkuId).filter((id): id is number => id > 0),
      );
      const sourceSkuCount = pauseTarget.spec.linkedSkus?.length ?? 0;
      const partialSkuTerminate = sourceSkuCount > 1
        && selectedSkuIds.size > 0
        && selectedSkuIds.size < sourceSkuCount;

      let projectVoStatus = '';
      try {
        const projectVo = await projectApi.getById(Number(selectedProject.id));
        setSelectedProject(projectVOToProject(projectVo));
        projectVoStatus = (projectVo.status ?? '').trim().toLowerCase();
      } catch {
        /* 排期已刷新；项目状态回写失败不阻断 */
      }

      if (projectVoStatus === 'terminated') {
        message.success(partialSkuTerminate
          ? '已终止所选 SKU；对照版本规格均已终止，项目已同步为已终止'
          : '规格已终止；对照版本规格均已终止，项目已同步为已终止');
      } else if (partialSkuTerminate) {
        message.success(`已终止 ${selectedSkuIds.size} 个 SKU；未勾选的 ${sourceSkuCount - selectedSkuIds.size} 个 SKU 仍留在原规格行继续进行`);
      } else {
        message.success('规格已终止');
      }
      setPauseModalOpen(false);
      setPauseTarget(null);
      void fetchProjectList(projectListPage, projectListPageSize);
    } catch (e) {
      console.error('终止规格失败', e);
      message.error(e instanceof Error ? e.message : '终止规格失败');
      throw e;
    } finally {
      setPauseLoading(false);
    }
  };

  const openProjectTerminateModal = async () => {
    if (!canMutateProject || !selectedProject) return;
    const status = (selectedProject.status ?? '').trim().toLowerCase();
    if (status === 'terminated') {
      message.warning('项目已处于终止状态');
      return;
    }
    if (status === 'completed') {
      message.warning('已完成的项目不可终止');
      return;
    }
    const schedule = pickProjectTerminateSchedule(projectSchedulesRef.current);
    if (!schedule) {
      message.warning('项目下无可终止的排期版本');
      return;
    }
    const hasInProgress = (schedule.specSchedules ?? []).some(
      sp => normalizeSpecScheduleStatus(sp.status) === 'in_progress',
    );
    if (!hasInProgress) {
      message.warning('没有可终止的进行中规格');
      return;
    }
    try {
      const nodeList = await loadAllMilestoneNodes();
      const manDaysMap = new Map<string, number>();
      const autoCompletionIds = new Set<string>();
      for (const n of nodeList) {
        const days = Number(n.default_man_days ?? n.defaultManDays ?? 0);
        manDaysMap.set(String(n.id), Number.isFinite(days) && days >= 0 ? days : 0);
        const method = (n.completion_method ?? n.completionMethod ?? 'manual')
          .toString()
          .trim()
          .toLowerCase();
        if (method === 'auto') autoCompletionIds.add(String(n.id));
      }
      const cols = getVersionMilestoneColumns(schedule);
      const visibleSpecs = schedule.specSchedules.filter(
        sp => normalizeSpecScheduleStatus(sp.status) !== 'terminated',
      );
      const matrixCols = expandSpecsToTerminateCols(visibleSpecs);
      const cells = buildTerminateMatrixCells(visibleSpecs, matrixCols, cols, manDaysMap, autoCompletionIds);
      setProjectTerminateTarget({
        scheduleId: schedule.id,
        milestones: cols,
        cols: matrixCols,
        cells,
      });
      setProjectTerminateOpen(true);
    } catch (e) {
      console.error('打开项目终止弹窗失败', e);
      message.error(e instanceof Error ? e.message : '打开终止弹窗失败');
    }
  };

  const handleConfirmProjectTerminate = async (payload: {
    reason: string;
    remark: string;
    nodeStatuses: { specId: number; ipdSkuId: number; milestoneId: number; status: string }[];
  }) => {
    if (!selectedProject || !projectTerminateTarget) return;
    setProjectTerminateLoading(true);
    try {
      const vo = await projectApi.terminate(Number(selectedProject.id), {
        reason: payload.reason,
        remark: payload.remark || undefined,
        node_statuses: payload.nodeStatuses.map(n => ({
          spec_id: n.specId,
          ipd_sku_id: n.ipdSkuId || undefined,
          milestone_id: n.milestoneId,
          status: n.status,
        })),
      });
      setSelectedProject(projectVOToProject(vo));
      const list = await scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true });
      const mappedVersions = [...list]
        .sort((a, b) => b.version - a.version)
        .map(item => mapDetailToScheduleVersion(item as ScheduleDetailVO));
      setProjectSchedules(mappedVersions);
      const projectStatus = (vo.status ?? '').trim().toLowerCase();
      if (projectStatus === 'terminated') {
        message.success('项目已终止');
      } else if (projectStatus === 'completed') {
        message.success('已终止所选规格；项目下无进行中规格，状态已同步为已完成');
      } else {
        message.success('已终止所选规格/SKU；仍有进行中规格，项目保持进行中');
      }
      setProjectTerminateOpen(false);
      setProjectTerminateTarget(null);
      void fetchProjectList(projectListPage, projectListPageSize);
    } catch (e) {
      console.error('终止项目失败', e);
      message.error(e instanceof Error ? e.message : '终止项目失败');
      throw e;
    } finally {
      setProjectTerminateLoading(false);
    }
  };

  const openSkuPickerForSpec = (scheduleId: string, spec: SpecSchedule, opts?: { readOnly?: boolean }) => {
    if (!selectedProject) return;
    const readOnly = opts?.readOnly === true;
    if (!readOnly) {
      if (!canMutateProject) return;
      const schedule = projectSchedulesRef.current.find(s => s.id === scheduleId);
      if (!schedule || schedule.status !== 'draft') {
        message.warning('只有草稿版本可以配置规格');
        return;
      }
    }
    setSkuPickerMode(readOnly ? 'view' : 'edit');
    setSkuPickerCopySourceSpecId(null);
    setSkuPickerScheduleId(scheduleId);
    setSkuPickerSpecId(spec.id);
    setSkuPickerSelected((spec.linkedSkus ?? []).map(linkedSkuToPickerItem));
    setShowSkuPickerModal(true);
  };

  const closeSkuPicker = () => {
    setShowSkuPickerModal(false);
    setSkuPickerMode('edit');
    setSkuPickerCopySourceSpecId(null);
    setSkuPickerSpecId(null);
    setSkuPickerSelected([]);
  };

  const applySpecVoToSchedule = (versionId: string, vo: SpecScheduleVO, insertAfterSpecId?: string | null) => {
    const mapped: SpecSchedule = {
      id: String(vo.id),
      specName: vo.spec_name,
      status: normalizeSpecScheduleStatus(vo.status),
      milestones: (vo.milestones ?? []).map(m => ({
        milestoneId: String(m.milestone_id),
        startDate: m.start_date ?? '',
        endDate: m.end_date ?? '',
        actualStartDate: m.actual_start_date ?? undefined,
        actualEndDate: m.actual_end_date ?? undefined,
        status: (m.status as ScheduleMilestone['status']) || 'pending',
      })),
      cellNotes: (vo.milestones ?? []).map(m => ({
        milestoneId: String(m.milestone_id),
        note: m.note ?? '',
      })),
      linkedSkus: mapLinkedSkusFromVo(vo),
      skuMilestones: mapSkuMilestonesFromVo(vo),
    };

    setProjectSchedules(prev =>
      prev.map(s => {
        if (s.id !== versionId) return s;
        const exists = s.specSchedules.some(sp => sp.id === mapped.id);
        if (exists) {
          return {
            ...s,
            specSchedules: s.specSchedules.map(sp => (sp.id === mapped.id
              ? {
                ...sp,
                specName: mapped.specName,
                status: mapped.status,
                linkedSkus: mapped.linkedSkus,
                skuMilestones: mapped.skuMilestones,
                milestones: mapped.milestones.length ? mapped.milestones : sp.milestones,
                cellNotes: mapped.cellNotes.length ? mapped.cellNotes : sp.cellNotes,
              }
              : sp)),
          };
        }
        if (insertAfterSpecId) {
          const idx = s.specSchedules.findIndex(sp => sp.id === insertAfterSpecId);
          const nextSpecs = [...s.specSchedules];
          nextSpecs.splice(idx >= 0 ? idx + 1 : nextSpecs.length, 0, mapped);
          return { ...s, specSchedules: nextSpecs };
        }
        return { ...s, specSchedules: [...s.specSchedules, mapped] };
      }),
    );
  };

  const handleSkuPickerOk = async (rows: IpdSkuListItemVO[]) => {
    if (skuPickerMode === 'view') return;
    if (!skuPickerScheduleId) return;
    const scheduleId = Number(skuPickerScheduleId);

    if (skuPickerMode === 'copy') {
      if (!skuPickerCopySourceSpecId) return;
      if (rows.length === 0) {
        message.warning('复制规格必须选择关联 IPD SKU');
        return;
      }
      try {
        // 规格名由后端按所选 IPD SKU 的规格字段生成，勿再传「源名称 (复制)」
        const created = await specApi.create(scheduleId, {
          copy_from_spec_id: Number(skuPickerCopySourceSpecId),
          ipd_sku_ids: rows.map(r => r.id),
        });
        applySpecVoToSchedule(skuPickerScheduleId, created, skuPickerCopySourceSpecId);
        message.success('已复制规格');
        closeSkuPicker();
      } catch (e) {
        console.error('复制规格失败', e);
        message.error(e instanceof Error ? e.message : '复制规格失败');
        throw e;
      }
      return;
    }

    if (!skuPickerSpecId) return;
    try {
      const updated = await specApi.replaceSkus(scheduleId, Number(skuPickerSpecId), rows.map(r => r.id));
      applySpecVoToSchedule(skuPickerScheduleId, updated);
      message.success('已更新规格关联');
      closeSkuPicker();
    } catch (e) {
      console.error('保存规格关联失败', e);
      message.error(e instanceof Error ? e.message : '保存规格关联失败');
      throw e;
    }
  };

  const getSkuOccupancy = useCallback((row: IpdSkuListItemVO): IpdSkuOccupancy => {
    if (!skuPickerScheduleId) return 'available';
    const schedule = projectSchedulesRef.current.find(s => s.id === skuPickerScheduleId);
    if (!schedule) return 'available';
    for (const spec of schedule.specSchedules) {
      const hit = (spec.linkedSkus ?? []).some(
        s => s.ipdSkuId === row.id || s.skuWorkItemId === row.work_item_id,
      );
      if (!hit) continue;
      if (skuPickerSpecId && spec.id === skuPickerSpecId) {
        return 'current';
      }
      return 'other';
    }
    return 'available';
  }, [skuPickerScheduleId, skuPickerSpecId]);

  // 递归更新自动计算节点（正排按有效前序传播；倒排按有效后续传播）
  const updateDependentMilestones = (
    schedule: ScheduleVersion,
    anchorMilestoneId: string,
    visited: Set<string> = new Set()
  ): ScheduleVersion => {
    if (visited.has(anchorMilestoneId)) return schedule;
    visited.add(anchorMilestoneId);

    const isDesc = schedule.milestoneSortOrder === 'desc';
    const dependentRelations = schedule.milestoneRelations?.filter(
      r =>
        r.calculationMode === 'auto' &&
        (isDesc
          ? getEffectiveNextMilestoneId(schedule, r.milestoneId) === anchorMilestoneId
          : getEffectiveParentMilestoneId(schedule, r.milestoneId) === anchorMilestoneId)
    ) || [];

    let nextSchedule = schedule;
    for (const relation of dependentRelations) {
      // 用第一个包含锚点节点的规格计算（合并时各规格结果一致）
      const specForCalc = nextSchedule.specSchedules.find(s =>
        s.milestones.some(m => m.milestoneId === anchorMilestoneId)
      );
      const calculated = specForCalc
        ? calculateMilestoneDates(nextSchedule, specForCalc, relation.milestoneId, relation)
        : null;
      if (!calculated) continue;

      // 在所有规格行中更新该依赖节点的时间
      const newSpecs = nextSchedule.specSchedules.map(spec => {
        const idx = spec.milestones.findIndex(m => m.milestoneId === relation.milestoneId);
        if (idx === -1) return spec;
        const m = spec.milestones[idx];
        const newMilestone = { ...m, startDate: calculated.startDate, endDate: calculated.endDate };
        const newMilestones = [...spec.milestones];
        newMilestones[idx] = newMilestone;
        return { ...spec, milestones: newMilestones };
      });
      nextSchedule = { ...nextSchedule, specSchedules: newSpecs };
      // 递归更新子任务的子任务
      nextSchedule = updateDependentMilestones(nextSchedule, relation.milestoneId, visited);
    }
    return nextSchedule;
  };

  const buildMilestoneCellPayload = (spec: SpecSchedule, m: ScheduleMilestone) => {
    const noteMap = new Map((spec.cellNotes ?? []).map(n => [String(n.milestoneId), n.note]));
    return {
      spec_schedule_id: Number(spec.id),
      milestone_id: Number(m.milestoneId),
      start_date: (m.startDate && String(m.startDate).trim()) || null,
      end_date: (m.endDate && String(m.endDate).trim()) || null,
      actual_start_date: (m.actualStartDate && String(m.actualStartDate).trim()) || null,
      actual_end_date: (m.actualEndDate && String(m.actualEndDate).trim()) || null,
      status: m.status,
      note: noteMap.get(String(m.milestoneId)) ?? '',
    };
  };

  const persistDateValue = (v?: string | null): string => {
    const t = (v && String(v).trim()) || '';
    if (!t) return '';
    return t.length >= 10 ? t.slice(0, 10) : t;
  };

  const milestonePersistChanged = (before?: ScheduleMilestone, after?: ScheduleMilestone): boolean => {
    if (!after) return false;
    if (!before) return true;
    return persistDateValue(before.startDate) !== persistDateValue(after.startDate)
      || persistDateValue(before.endDate) !== persistDateValue(after.endDate)
      || persistDateValue(before.actualStartDate) !== persistDateValue(after.actualStartDate)
      || persistDateValue(before.actualEndDate) !== persistDateValue(after.actualEndDate)
      || (before.status || '') !== (after.status || '');
  };

  /** 失焦保存时一并落库自动计算/跨规格合并连带改动的节点 */
  const autoSaveChangedMilestoneCells = async (
    versionId: string,
    before: ScheduleVersion,
    after: ScheduleVersion,
  ) => {
    if (!canMutateProject) return;
    const beforeBySpec = new Map((before.specSchedules ?? []).map(s => [String(s.id), s]));
    const milestones = (after.specSchedules ?? []).flatMap(spec => {
      if (normalizeSpecScheduleStatus(spec.status) === 'terminated') return [];
      const prevSpec = beforeBySpec.get(String(spec.id));
      const prevByMid = new Map((prevSpec?.milestones ?? []).map(m => [String(m.milestoneId), m]));
      return (spec.milestones ?? [])
        .filter(m => milestonePersistChanged(prevByMid.get(String(m.milestoneId)), m))
        .map(m => buildMilestoneCellPayload(spec, m))
        .filter(x => x.spec_schedule_id > 0 && x.milestone_id > 0);
    });
    if (milestones.length === 0) return;
    try {
      if (milestones.length === 1) {
        const only = milestones[0];
        await milestoneApi.update(Number(versionId), only.spec_schedule_id, only.milestone_id, {
          start_date: only.start_date,
          end_date: only.end_date,
          actual_start_date: only.actual_start_date,
          actual_end_date: only.actual_end_date,
          status: only.status,
          note: only.note,
        });
        return;
      }
      await milestoneApi.batchUpdate(Number(versionId), { milestones });
    } catch (e) {
      console.error('自动保存失败', e);
      message.error(e instanceof Error ? e.message : '自动保存失败');
    }
  };

  // 更新里程碑时间（预计/实际，不可变更新；预计结束变更会触发自动计算依赖）
  const handleUpdateMilestoneDate = async (
    versionId: string,
    specId: string,
    milestoneId: string,
    field: 'start' | 'end' | 'actualStart' | 'actualEnd',
    newDate: string,
  ) => {
    if (!canMutateProject) return;
    const target = getMilestoneTarget(versionId, specId, milestoneId);
    if (!target) return;
    if (normalizeSpecScheduleStatus(target.spec.status) === 'terminated') {
      message.warning('规格已终止，节点不可修改');
      return;
    }
    const { scheduleIndex, schedule, specIndex, spec, milestoneIndex, milestone } = target;
    const dateKey =
      field === 'start'
        ? 'startDate'
        : field === 'end'
          ? 'endDate'
          : field === 'actualStart'
            ? 'actualStartDate'
            : 'actualEndDate';
    const base: ScheduleMilestone = milestone ?? {
      milestoneId,
      startDate: '',
      endDate: '',
      actualStartDate: undefined,
      actualEndDate: undefined,
      status: 'pending',
    };
    if (
      (field === 'actualStart' || field === 'actualEnd') &&
      !(newDate && String(newDate).trim()) &&
      base.status === 'completed'
    ) {
      message.warning('「已完成」状态下不能清空实际开始或实际结束时间');
      return;
    }

    const nextStart = field === 'start' ? newDate : (base.startDate ?? '');
    const nextEnd = field === 'end' ? newDate : (base.endDate ?? '');
    if (nextStart && nextEnd) {
      const startDateObj = parseISO(nextStart);
      const endDateObj = parseISO(nextEnd);
      if (
        !Number.isNaN(startDateObj.getTime()) &&
        !Number.isNaN(endDateObj.getTime()) &&
        startDateObj.getTime() > endDateObj.getTime()
      ) {
        message.warning('预计开始日期不能大于预计结束日期');
        return;
      }
    }

    const nextActualStart = field === 'actualStart' ? newDate : (base.actualStartDate ?? '');
    const nextActualEnd = field === 'actualEnd' ? newDate : (base.actualEndDate ?? '');
    if (nextActualStart && nextActualEnd) {
      const actualStartObj = parseISO(nextActualStart);
      const actualEndObj = parseISO(nextActualEnd);
      if (
        !Number.isNaN(actualStartObj.getTime()) &&
        !Number.isNaN(actualEndObj.getTime()) &&
        actualStartObj.getTime() > actualEndObj.getTime()
      ) {
        message.warning('实际开始日期不能大于实际结束日期');
        return;
      }
    }

    const newMilestone: ScheduleMilestone = {
      ...base,
      [dateKey]: newDate,
    };
    const newMilestones = [...spec.milestones];
    if (milestoneIndex >= 0) {
      newMilestones[milestoneIndex] = newMilestone;
    } else {
      newMilestones.push(newMilestone);
    }
    const newSpec = { ...spec, milestones: newMilestones };
    let newSpecs = [...schedule.specSchedules];
    newSpecs[specIndex] = newSpec;

    const relation = getMilestoneRelation(schedule, milestoneId);
    if (relation?.mergeAcrossSpecs) {
      newSpecs = newSpecs.map(s => {
        if (normalizeSpecScheduleStatus(s.status) === 'terminated') return s;
        const idx = s.milestones.findIndex(m => m.milestoneId === milestoneId);
        if (idx === -1) return s;
        const m = s.milestones[idx];
        const updated = { ...m, [dateKey]: newDate };
        const list = [...s.milestones];
        list[idx] = updated;
        return { ...s, milestones: list };
      });
    }

    let newSchedule: ScheduleVersion = { ...schedule, specSchedules: newSpecs };
    const shouldTriggerAutoRecalc = field === 'start' || field === 'end';
    if (shouldTriggerAutoRecalc) {
      const mergedSchedules = projectSchedulesRef.current.map((s, i) =>
        i === scheduleIndex ? { ...schedule, specSchedules: newSpecs } : s,
      );
      const calendarYears = collectYearsFromSchedules(mergedSchedules, selectedProject?.launchDate);
      await ensureWorkCalendarsLoadedForYears(calendarYears);
      // 编辑预计时间后也触发自动计算，并以当前编辑节点为起点
      newSchedule = recalculateAllAutoMilestoneDates(
        { ...schedule, specSchedules: newSpecs },
        milestoneId,
      );
      newSchedule = applyManualMilestoneStatusAndActualFromEstimatedDates(newSchedule);
    }

    const nextSchedules = [...projectSchedulesRef.current];
    nextSchedules[scheduleIndex] = newSchedule;
    setProjectSchedules(nextSchedules);

    // 草稿编辑中，或已发布版本改实际时间：立即自动保存（不克隆草稿）
    const shouldAutoSave =
      editingVersionId === versionId
      || (schedule.status === 'published' && (field === 'actualStart' || field === 'actualEnd'));
    if (shouldAutoSave) {
      void autoSaveChangedMilestoneCells(versionId, schedule, newSchedule);
    }
  };

  // 更新里程碑状态（立即调用后端接口同步保存）
  const handleUpdateMilestoneStatus = async (
    versionId: string,
    specId: string,
    milestoneId: string,
    status: 'pending' | 'in_progress' | 'completed',
  ) => {
    if (!canMutateProject) return;
    const preTarget = getMilestoneTarget(versionId, specId, milestoneId);
    if (preTarget && normalizeSpecScheduleStatus(preTarget.spec.status) === 'terminated') {
      message.warning('规格已终止，节点不可修改');
      return;
    }
    if (status === 'completed' && isAutoCompletionNode(milestoneId)) {
      message.warning('自动完成节点不可手动改为已完成');
      return;
    }
    const target = getMilestoneTarget(versionId, specId, milestoneId);
    if (!target || target.milestoneIndex === -1) return;
    const { scheduleIndex, schedule, specIndex, spec, milestoneIndex, milestone: sourceMilestone } = target;
    if (!sourceMilestone) return;
    let milestone = sourceMilestone;
    if (status === 'completed') {
      const planStart = (milestone.startDate && String(milestone.startDate).trim()) || '';
      let actualStart = (milestone.actualStartDate && String(milestone.actualStartDate).trim()) || '';
      let actualEnd = (milestone.actualEndDate && String(milestone.actualEndDate).trim()) || '';
      if (!actualStart && !planStart) {
        message.warning('状态设为「已完成」前，请先填写计划开始时间或实际开始时间');
        return;
      }
      if (!actualStart && planStart) actualStart = planStart;
      if (!actualEnd) actualEnd = dayjs().format('YYYY-MM-DD HH:mm:ss');
      milestone = { ...milestone, actualStartDate: actualStart, actualEndDate: actualEnd };
    }
    const prevStatus = milestone.status;
    const newMilestone = { ...milestone, status };
    const newMilestones = [...spec.milestones];
    newMilestones[milestoneIndex] = newMilestone;
    const newSpec = { ...spec, milestones: newMilestones };
    const newSpecs = [...schedule.specSchedules];
    newSpecs[specIndex] = newSpec;
    const newSchedule = { ...schedule, specSchedules: newSpecs };

    const nextSchedules = [...projectSchedules];
    nextSchedules[scheduleIndex] = newSchedule;
    setProjectSchedules(nextSchedules);

    try {
      // 后端更新接口要求 start/end/status 等字段可解析，这里发送当前单元格完整数据
      await milestoneApi.update(
        Number(versionId),
        Number(specId),
        Number(milestoneId),
        {
          start_date: (newMilestone.startDate && String(newMilestone.startDate).trim()) || null,
          end_date: (newMilestone.endDate && String(newMilestone.endDate).trim()) || null,
          actual_start_date: (newMilestone.actualStartDate && String(newMilestone.actualStartDate).trim()) || null,
          actual_end_date: (newMilestone.actualEndDate && String(newMilestone.actualEndDate).trim()) || null,
          status: newMilestone.status,
        },
      );
    } catch (e) {
      console.error('更新节点状态失败', e);
      message.error(e instanceof Error ? e.message : '更新节点状态失败');
      // 回滚本地状态
      setProjectSchedules(prev =>
        prev.map(s => {
          if (s.id !== versionId) return s;
          return {
            ...s,
            specSchedules: s.specSchedules.map(sp => {
              if (sp.id !== specId) return sp;
              return {
                ...sp,
                milestones: sp.milestones.map(m =>
                  m.milestoneId === milestoneId ? { ...m, status: prevStatus } : m,
                ),
              };
            }),
          };
        }),
      );
    }
  };

  // 更新单元格备注：内容有变化时才更新本地并落库；点击后原样失焦不调接口
  const handleUpdateCellNote = (versionId: string, specId: string, milestoneId: string, note: string) => {
    if (!canMutateProject) return;
    const sched = projectSchedulesRef.current.find(s => s.id === versionId);
    const spec = sched?.specSchedules.find(sp => sp.id === specId);
    if (normalizeSpecScheduleStatus(spec?.status) === 'terminated') {
      message.warning('规格已终止，节点不可修改');
      setEditingCellNote(null);
      return;
    }
    const mid = String(milestoneId);
    const nextNote = normalizeRichTextForSave(note ?? '');

    const prevNote = normalizeRichTextForSave(spec ? getCellNote(spec, mid) : '');
    // 内容未变：仅退出编辑，不请求接口
    if (nextNote === prevNote) {
      setEditingCellNote(null);
      return;
    }

    const hasContent = !isRichTextEmpty(nextNote);

    setProjectSchedules(prev =>
      prev.map(s => {
        if (s.id !== versionId) return s;

        const nextSpecs = s.specSchedules.map(sp => {
          if (sp.id !== specId) return sp;

          // 已发布版可能原先无该单元格记录，先补一条本地 milestone，避免保存时找不到行
          const nextMilestones = [...(sp.milestones ?? [])];
          if (!nextMilestones.some(m => String(m.milestoneId) === mid)) {
            nextMilestones.push({
              milestoneId: mid,
              startDate: '',
              endDate: '',
              actualStartDate: undefined,
              actualEndDate: undefined,
              status: 'pending',
            });
          }

          const existingNotes = sp.cellNotes ?? [];
          const nextNotes = [...existingNotes];
          const existingNoteIndex = nextNotes.findIndex(n => String(n.milestoneId) === mid);

          if (existingNoteIndex >= 0) {
            if (hasContent) {
              nextNotes[existingNoteIndex] = { ...nextNotes[existingNoteIndex], note: nextNote };
            } else {
              nextNotes.splice(existingNoteIndex, 1);
            }
          } else if (hasContent) {
            nextNotes.push({ milestoneId: mid, note: nextNote });
          }

          return { ...sp, milestones: nextMilestones, cellNotes: nextNotes };
        });

        return { ...s, specSchedules: nextSpecs };
      }),
    );

    setEditingCellNote(null);

    // 草稿编辑中，或已发布版本改备注：立即自动保存（不克隆草稿）
    const shouldAutoSave =
      editingVersionId === versionId
      || sched?.status === 'published';
    if (shouldAutoSave) {
      void (async () => {
        try {
          await noteApi.update(Number(versionId), Number(specId), Number(mid), { note: nextNote });
        } catch (e) {
          console.error('备注保存失败', e);
          message.error(e instanceof Error ? e.message : '备注保存失败');
        }
      })();
    }
  };

  // 更新进度同步：草稿编辑中仅改本地；已发布版本改本地并在失焦时自动保存（不克隆草稿）
  const handleUpdateVersionNotes = (versionId: string, newNotes: string) => {
    if (!canMutateProject) return;
    const sched = projectSchedulesRef.current.find(s => s.id === versionId);
    const canEdit =
      editingVersionId === versionId
      || sched?.status === 'published';
    if (!canEdit) return;

    setProjectSchedules(prev =>
      prev.map(s => (s.id === versionId ? { ...s, versionNotes: newNotes } : s)),
    );
  };

  const autoSaveVersionNotes = async (schedule: ScheduleVersion, previousNotes?: string) => {
    if (!canMutateProject || schedule.status !== 'published') return;
    const nextNotes = normalizeRichTextForSave(schedule.versionNotes || '');
    if (previousNotes !== undefined && normalizeRichTextForSave(previousNotes) === nextNotes) return;
    if (!schedule.expectedLaunchDate || !String(schedule.expectedLaunchDate).trim()) {
      message.error('预期入库日期不能为空，无法保存进度同步');
      return;
    }
    try {
      await scheduleApi.update(Number(schedule.id), {
        name: schedule.name,
        expected_launch_date: schedule.expectedLaunchDate,
        version_notes: nextNotes,
        milestone_sort_order: schedule.milestoneSortOrder ?? 'asc',
      });
    } catch (e) {
      console.error('进度同步保存失败', e);
      message.error(e instanceof Error ? e.message : '进度同步保存失败');
    }
  };

  // 生成版本可保存字段的签名，用于比较“完成”时是否有改动
  const buildEditSnapshot = (s: ScheduleVersion): string => {
    const cols = getVersionMilestoneColumns(s);
    const relations = s.milestoneRelations ?? [];
    const payload = {
      name: (s.name ?? '').trim(),
      expectedLaunchDate: (s.expectedLaunchDate ?? '').trim(),
      versionNotes: (s.versionNotes ?? '').trim(),
      specSchedules: (s.specSchedules ?? []).map(spec => ({
        id: spec.id,
        specName: (spec.specName ?? '').trim(),
        status: normalizeSpecScheduleStatus(spec.status),
        milestones: (spec.milestones ?? []).map(m => ({
          milestoneId: m.milestoneId,
          startDate: m.startDate ?? null,
          endDate: m.endDate ?? null,
          actualStartDate: m.actualStartDate ?? null,
          actualEndDate: m.actualEndDate ?? null,
          status: m.status,
          note: (spec.cellNotes ?? []).find(n => String(n.milestoneId) === String(m.milestoneId))?.note ?? '',
        })),
      })),
      columnOrder: cols.map(c => c.id),
      relations: cols.map(col => {
        const rel = relations.find(r => r.milestoneId === col.id);
        return {
          milestoneId: col.id,
          order: col.order ?? 0,
          parentMilestoneId: rel?.parentMilestoneId ?? null,
          nextMilestoneId: rel?.nextMilestoneId ?? null,
          durationDays: rel?.durationDays ?? null,
          excludeWeekends: !!rel?.excludeWeekends,
          mergeAcrossSpecs: !!rel?.mergeAcrossSpecs,
          timeMethod: rel?.calculationMode === 'auto' ? 'auto' : 'manual',
        };
      }),
    };
    return JSON.stringify(payload);
  };

  // 构建“完成”时批量保存里程碑的请求体（仅整理结构，不改变字段含义）
  const buildMilestoneBatchPayload = (schedule: ScheduleVersion) => {
    return {
      milestones: schedule.specSchedules.flatMap(spec => {
        // 已终止规格下的节点禁止任何写入
        if (normalizeSpecScheduleStatus(spec.status) === 'terminated') return [];
        const noteMap = new Map((spec.cellNotes ?? []).map(n => [String(n.milestoneId), n.note]));
        return spec.milestones.map(m => ({
          spec_schedule_id: Number(spec.id),
          milestone_id: Number(m.milestoneId),
          start_date: (m.startDate && String(m.startDate).trim()) || null,
          end_date: (m.endDate && String(m.endDate).trim()) || null,
          actual_start_date: (m.actualStartDate && String(m.actualStartDate).trim()) || null,
          actual_end_date: (m.actualEndDate && String(m.actualEndDate).trim()) || null,
          status: m.status,
          note: noteMap.get(String(m.milestoneId)) ?? '',
        }));
      }),
    };
  };

  // 切换版本编辑状态：进入编辑 / 完成并保存到后端
  const handleToggleEditVersion = async (schedule: ScheduleVersion) => {
    if (!canMutateProject) return;
    const isEditing = editingVersionId === schedule.id;

    // 进入编辑模式：记录快照，用于“完成”时判断是否有改动
    if (!isEditing) {
      const current = projectSchedulesRef.current.find(s => s.id === schedule.id);
      editSnapshotRef.current = current ? buildEditSnapshot(current) : null;
      dirtySpecNameIdsRef.current.clear();
      setEditingVersionId(schedule.id);
      return;
    }

    // 从 ref 取当前最新的排期数据，避免闭包导致传参是修改前的旧值
    const latest = projectSchedulesRef.current.find(s => s.id === schedule.id);
    if (!latest) return;

    // 无任何改动时直接退出编辑，不调用接口
    if (editSnapshotRef.current != null && buildEditSnapshot(latest) === editSnapshotRef.current) {
      editSnapshotRef.current = null;
      dirtySpecNameIdsRef.current.clear();
      setEditingVersionId(null);
      return;
    }
    editSnapshotRef.current = null;

    // 校验预期入库日期，前端先拦截，避免后端报“预期入库日期不能为空”
    if (!latest.expectedLaunchDate || !String(latest.expectedLaunchDate).trim()) {
      message.error('预期入库日期不能为空，请先在项目详情中补充入库日期');
      return;
    }

    // 保存前自愈：已完成但缺实际时间时，用预计时间补齐（兼容复制版本历史脏数据）
    const healedSpecs = latest.specSchedules.map(spec => ({
      ...spec,
      milestones: spec.milestones.map(m => {
        if (m.status !== 'completed') return m;
        const a = (m.actualStartDate && String(m.actualStartDate).trim()) || '';
        const b = (m.actualEndDate && String(m.actualEndDate).trim()) || '';
        if (a && b) return m;
        const planStart = (m.startDate && String(m.startDate).trim()) || '';
        const planEnd = (m.endDate && String(m.endDate).trim()) || '';
        return {
          ...m,
          actualStartDate: a || planStart || m.actualStartDate,
          actualEndDate: b || planEnd || planStart || m.actualEndDate,
        };
      }),
    }));
    const toSave: ScheduleVersion = { ...latest, specSchedules: healedSpecs };

    const milestoneNameById = new Map(
      getVersionMilestoneColumns(toSave).map(c => [String(c.id), c.name]),
    );
    const completedWithoutActual: string[] = [];
    for (const spec of toSave.specSchedules) {
      for (const m of spec.milestones) {
        if (m.status !== 'completed') continue;
        const a = (m.actualStartDate && String(m.actualStartDate).trim()) || '';
        const b = (m.actualEndDate && String(m.actualEndDate).trim()) || '';
        if (!a || !b) {
          const nodeName = milestoneNameById.get(String(m.milestoneId)) ?? `节点${m.milestoneId}`;
          completedWithoutActual.push(`${spec.specName} / ${nodeName}`);
        }
      }
    }
    if (completedWithoutActual.length > 0) {
      const sample = completedWithoutActual.slice(0, 5).join('；');
      message.error(
        `以下单元格为「已完成」但未填写实际开始或实际结束：${sample}${completedWithoutActual.length > 5 ? '…' : ''}`,
      );
      return;
    }
    // 自愈后的数据写回本地，后续批量保存使用 toSave
    {
      const idx = projectSchedulesRef.current.findIndex(s => s.id === toSave.id);
      if (idx >= 0) {
        const next = [...projectSchedulesRef.current];
        next[idx] = toSave;
        setProjectSchedules(next);
      }
    }

    // 结束编辑，调用后端保存当前版本的基础信息（名称、预计入库日期、版本备注）
    try {
      await scheduleApi.update(Number(toSave.id), {
        name: toSave.name,
        expected_launch_date: toSave.expectedLaunchDate,
        version_notes: toSave.versionNotes || '',
        milestone_sort_order: toSave.milestoneSortOrder ?? 'asc',
      });

      // 同步保存列宽和关联配置到后端
      const columns = getVersionMilestoneColumns(toSave);
      const relations = toSave.milestoneRelations ?? [];
      const relationsPayload = columns.map(col => {
        const rel = relations.find(r => r.milestoneId === col.id);
        return {
          milestone_id: Number(col.id),
          milestone_order: col.order ?? 0,
          // 列宽由单独接口维护，这里不保存
          column_width: 150,
          parent_milestone_id: rel?.parentMilestoneId ? Number(rel.parentMilestoneId) : null,
          next_milestone_id: rel?.nextMilestoneId ? Number(rel.nextMilestoneId) : null,
          duration_days: rel?.durationDays ?? null,
          time_offset_days: rel?.timeOffsetDays ?? 1,
          exclude_weekends: !!rel?.excludeWeekends,
          merge_across_specs: !!rel?.mergeAcrossSpecs,
          time_method: rel?.calculationMode === 'auto' ? 'auto' : 'manual',
        };
      });

      await relationApi.save(Number(toSave.id), { relations: relationsPayload });

      // 保存被修改过的规格名称（不在失焦时保存，统一在“完成”按钮同步）
      const dirtySpecIds = Array.from(dirtySpecNameIdsRef.current);
      if (dirtySpecIds.length > 0) {
        await Promise.all(
          dirtySpecIds.map(specId => {
            const sp = toSave.specSchedules.find(x => x.id === specId);
            const specName = (sp?.specName ?? '').trim();
            // 若 name 为空则跳过，避免触发后端“规格名称不能为空”
            if (!specName) return Promise.resolve(true);
            return specApi.update(Number(toSave.id), Number(specId), {
              spec_name: specName,
            });
          }),
        );
        dirtySpecNameIdsRef.current.clear();
      }

      // 批量保存当前版本的所有里程碑时间/状态/备注（含自愈后的实际时间）
      const milestoneBatchPayload = buildMilestoneBatchPayload(toSave);
      await milestoneApi.batchUpdate(Number(toSave.id), milestoneBatchPayload);

      message.success('排期版本已保存');
      setEditingVersionId(null);
    } catch (e) {
      console.error('保存排期版本失败', e);
      message.error(e instanceof Error ? e.message : '保存排期版本失败');
    }
  };

  // 发布版本（对接后端发布接口，成功后刷新列表与详情）
  const handlePublishVersion = async (versionId: string) => {
    if (!canMutateProject || !selectedProject) return;
    try {
      await scheduleApi.publish(Number(versionId));
      message.success('版本已发布');
      setEditingVersionId(null);
      // 刷新排期列表（含明细；后端会归档旧生效版并更新状态）
      const list = await scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true });
      setProjects(prev =>
        prev.map(p => (p.id === selectedProject.id ? { ...p, scheduleCount: list.length } : p))
      );
      setProjectSchedules(
        [...list]
          .sort((a, b) => b.version - a.version)
          .map(vo => mapDetailToScheduleVersion(vo as ScheduleDetailVO)),
      );
    } catch (e) {
      console.error('发布失败', e);
      message.error(e instanceof Error ? e.message : '发布失败');
    }
  };

  // 删除版本（仅草稿可删，带二次确认）
  const handleDeleteVersion = async (versionId: string) => {
    if (!canMutateProject || !selectedProject) return;

    // 仅当该项目只有一个版本（且为草稿）时禁止删除，避免项目下无任何版本
    if (projectSchedules.length <= 1) {
      message.warning('当前项目只有一个版本，禁止删除');
      return;
    }

    ipcConfirm({
      title: '确认删除该版本？',
      content: '删除后该版本将从当前项目中移除。是否继续？',
      okText: '确认删除',
      cancelText: '取消',
      danger: true,
      onOk: async () => {
        try {
          await scheduleApi.delete(Number(versionId));

          // 刷新版本列表（含明细）
          const list = await scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true });
          setProjects(prev =>
            prev.map(p => (p.id === selectedProject.id ? { ...p, scheduleCount: list.length } : p))
          );
          const mappedVersions = [...list]
            .sort((a, b) => b.version - a.version)
            .map(vo => mapDetailToScheduleVersion(vo as ScheduleDetailVO));
          setProjectSchedules(mappedVersions);

          // 如果刚删的是当前激活版本，则切换到新列表的第一个版本
          setActiveVersionId(prevActive => {
            if (prevActive && prevActive !== versionId) return prevActive;
            return mappedVersions[0]?.id ?? null;
          });
          setEditingVersionId(null);
          setEditingSpecId(null);
        } catch (e) {
          console.error('删除版本失败', e);
          message.error(e instanceof Error ? e.message : '删除版本失败');
        }
      },
    });
  };

  // 设置里程碑列排序方式（仅更新本地，点击“保存”时统一落库）；支持重复点击以重新触发自动计算
  const handleSetMilestoneSortOrder = async (schedule: ScheduleVersion, nextOrder: 'asc' | 'desc') => {
    if (!canMutateProject) return;
    if (editingVersionId !== schedule.id) {
      message.warning('请先进入编辑模式再切换排序方式');
      return;
    }

    const scheduleWithOrder: ScheduleVersion = { ...schedule, milestoneSortOrder: nextOrder };
    const mergedSchedules = projectSchedulesRef.current.map(s =>
      s.id === schedule.id ? scheduleWithOrder : s,
    );
    const calendarYears = collectYearsFromSchedules(mergedSchedules, selectedProject?.launchDate);
    await ensureWorkCalendarsLoadedForYears(calendarYears);

    const recalced = recalculateAllAutoMilestoneDates(scheduleWithOrder);
    const finalSchedule = applyManualMilestoneStatusAndActualFromEstimatedDates(recalced);
    setProjectSchedules(prev => prev.map(s => (s.id === schedule.id ? finalSchedule : s)));
  };

  // 打开列配置：按需拉取全部里程碑节点，勾选=当前版本已有的列
  const handleOpenColumnConfig = async (schedule: ScheduleVersion) => {
    if (!canMutateProject) return;
    await loadAllMilestoneNodes();
    setColumnConfigVersionId(schedule.id);
    const versionColumnIds = getVersionMilestoneColumns(schedule).map(c => c.id);
    // 若当前版本有自定义列配置则用其作为勾选，否则用当前版本已有列
    setTempSelectedMilestones(
      schedule.customMilestoneIds && schedule.customMilestoneIds.length > 0
        ? schedule.customMilestoneIds
        : versionColumnIds,
    );
  };

  // 保存列配置（对接后端 relations 接口，只保留勾选的列并更新顺序）
  const handleSaveColumnConfig = async () => {
    if (!canMutateProject) return;
    if (!columnConfigVersionId || tempSelectedMilestones.length === 0) {
      message.warning('请至少勾选一列');
      return;
    }
    const schedule = projectSchedulesRef.current.find(s => s.id === columnConfigVersionId);
    if (!schedule) return;
    const relations = schedule.milestoneRelations ?? [];
    const relationsPayload = tempSelectedMilestones.map((milestoneId, index) => {
      const existing = relations.find(r => r.milestoneId === milestoneId);
      const colWidth = columnWidths[schedule.id]?.[milestoneId] ?? DEFAULT_COLUMN_WIDTH;
      return {
        milestone_id: Number(milestoneId),
        milestone_order: index,
        column_width: colWidth,
        parent_milestone_id: existing?.parentMilestoneId ? Number(existing.parentMilestoneId) : null,
        next_milestone_id: existing?.nextMilestoneId ? Number(existing.nextMilestoneId) : null,
        duration_days: existing?.durationDays ?? null,
        time_offset_days: existing?.timeOffsetDays ?? 1,
        exclude_weekends: !!existing?.excludeWeekends,
        merge_across_specs: !!existing?.mergeAcrossSpecs,
        time_method: existing?.calculationMode === 'auto' ? 'auto' : 'manual',
      };
    });
    try {
      await relationApi.save(Number(schedule.id), { relations: relationsPayload });
      message.success('列配置已保存');
      setColumnConfigVersionId(null);
      // 刷新当前版本明细，使表格列更新
      const detail = await scheduleApi.getById(Number(schedule.id));
      const mapped = mapDetailToScheduleVersion(detail);
      setProjectSchedules(prev => prev.map(s => (s.id === schedule.id ? mapped : s)));
    } catch (e) {
      console.error('保存列配置失败', e);
      message.error(e instanceof Error ? e.message : '保存列配置失败');
    }
  };

  // 列拖拽开始
  const handleColumnDragStart = (versionId: string, index: number) => {
    if (!canMutateProject) return;
    // 非草稿状态不允许调整列顺序
    const schedule = projectSchedulesRef.current.find(s => s.id === versionId);
    if (!schedule || schedule.status !== 'draft') return;

    setDragColumn({
      versionId,
      dragIndex: index,
      dropIndex: null
    });
  };

  // 列拖拽经过
  const handleColumnDragOver = (e: React.DragEvent, versionId: string, index: number) => {
    if (!canMutateProject) return;
    e.preventDefault();
    // 非草稿状态不允许调整列顺序
    const schedule = projectSchedulesRef.current.find(s => s.id === versionId);
    if (!schedule || schedule.status !== 'draft') return;
    if (dragColumn.versionId !== versionId || dragColumn.dragIndex === null) return;
    
    setDragColumn(prev => ({
      ...prev,
      dropIndex: index
    }));
  };

  // 列拖拽放置（根据后端 columns 配置更新 milestone_order）
  const handleColumnDrop = async (e: React.DragEvent, versionId: string, dropIndex: number) => {
    if (!canMutateProject) return;
    e.preventDefault();
    if (dragColumn.versionId !== versionId || dragColumn.dragIndex === null || dragColumn.dragIndex === dropIndex) {
      setDragColumn({ versionId: null, dragIndex: null, dropIndex: null });
      return;
    }

    const schedule = projectSchedulesRef.current.find(s => s.id === versionId);
    if (!schedule || schedule.status !== 'draft' || !schedule.milestoneColumns || schedule.milestoneColumns.length === 0) {
      setDragColumn({ versionId: null, dragIndex: null, dropIndex: null });
      return;
    }

    const { dragIndex } = dragColumn;

    const currentIds = [...schedule.milestoneColumns].sort((a, b) => a.order - b.order).map(c => c.id);
    const newIds = [...currentIds];
    const [draggedId] = newIds.splice(dragIndex, 1);
    newIds.splice(dropIndex, 0, draggedId);

    // 用已有关系配置补齐 parent/duration/time_method，只有 milestone_order 发生变化
    const relations = schedule.milestoneRelations ?? [];
    const relationsPayload = newIds.map((milestoneId, index) => {
      const existing = relations.find(r => r.milestoneId === milestoneId);
      return {
        milestone_id: Number(milestoneId),
        milestone_order: index,
        // 列宽不由这里更新（由 /column-widths 独立接口维护）
        column_width: columnWidths[schedule.id]?.[milestoneId] ?? DEFAULT_COLUMN_WIDTH,
        parent_milestone_id: existing?.parentMilestoneId ? Number(existing.parentMilestoneId) : null,
        next_milestone_id: existing?.nextMilestoneId ? Number(existing.nextMilestoneId) : null,
        duration_days: existing?.durationDays ?? null,
        time_offset_days: existing?.timeOffsetDays ?? 1,
        exclude_weekends: !!existing?.excludeWeekends,
        merge_across_specs: !!existing?.mergeAcrossSpecs,
        time_method: existing?.calculationMode === 'auto' ? 'auto' : 'manual',
      };
    });

    try {
      await relationApi.save(Number(schedule.id), { relations: relationsPayload });
      // 拉取最新 columns 顺序
      const detail = await scheduleApi.getById(Number(schedule.id));
      const mapped = mapDetailToScheduleVersion(detail);
      setProjectSchedules(prev => prev.map(s => (s.id === schedule.id ? mapped : s)));
      // 静默保存
    } catch (err) {
      console.error('保存列顺序失败', err);
      message.error(err instanceof Error ? err.message : '保存列顺序失败');
    }

    setDragColumn({ versionId: null, dragIndex: null, dropIndex: null });
  };

  // 列拖拽结束
  const handleColumnDragEnd = () => {
    setDragColumn({ versionId: null, dragIndex: null, dropIndex: null });
  };

  // 获取单元格背景色（与 --ipc-status-* 同源）
  const getCellBackgroundColor = (status: string) => nodeStatusCellBackground(status);

  // 获取单元格备注
  const getCellNote = (spec: SpecSchedule, milestoneId: string): string => {
    return spec.cellNotes?.find(n => String(n.milestoneId) === String(milestoneId))?.note || '';
  };

  // 判断日期是否跨年
  const isCrossYear = (dateStr: string): boolean => {
    if (!dateStr) return false;
    try {
      const date = parseISO(dateStr);
      const currentYear = new Date().getFullYear();
      return date.getFullYear() !== currentYear;
    } catch {
      return false;
    }
  };

  // 格式化日期显示（当年MM-DD，跨年yyyy-MM-dd）
  const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = parseISO(dateStr);
      const currentYear = new Date().getFullYear();
      const dateYear = date.getFullYear();
      if (dateYear !== currentYear) {
        return format(date, 'yyyy-MM-dd');
      }
      return format(date, 'MM-dd');
    } catch {
      return dateStr;
    }
  };

  type MilestoneDateField = 'start' | 'end' | 'actualStart' | 'actualEnd';
  const renderMilestoneDateCell = (
    scheduleId: string,
    specId: string,
    milestoneId: string,
    isEditing: boolean,
    isEditingField: boolean,
    field: MilestoneDateField,
    value?: string,
    spanClassName?: string,
    placeholder?: string,
  ) => {
    // yyyy-MM-dd 用宽槽；MM-dd 保持 40px，展示/编辑同宽避免点选撑开
    const useYmdWidth = Boolean(value && isCrossYear(value));
    const slot = (child: ReactNode) => (
      <span className={`milestone-date-slot${useYmdWidth ? ' milestone-date-slot--ymd' : ''}`}>
        {child}
      </span>
    );

    if (isEditing && isEditingField) {
      return slot(
        <IpcDatePicker
          variant="cell"
          value={value}
          open
          allowClear
          autoFocus
          format={useYmdWidth ? 'YYYY-MM-DD' : 'MM-DD'}
          placeholder={placeholder}
          placement="bottomLeft"
          onOpenChange={open => {
            if (!open) setEditingCell(null);
          }}
          onChange={next => {
            void handleUpdateMilestoneDate(
              scheduleId,
              specId,
              milestoneId,
              field,
              next ?? '',
            ).finally(() => setEditingCell(null));
          }}
        />,
      );
    }

    const spanPlaceholder = placeholder || '';
    return slot(
      <span
        className={`${spanClassName ?? ''}${useYmdWidth ? ' cross-year' : ''}`}
        onClick={() => {
          if (!isEditing) return;
          setEditingCell({
            versionId: scheduleId,
            specId,
            milestoneId,
            field,
          });
        }}
      >
        {formatDateDisplay(value ?? '') || (isEditing ? spanPlaceholder : '')}
      </span>,
    );
  };

  const renderMilestoneStatusControl = (
    schedule: ScheduleVersion,
    specId: string,
    milestoneId: string,
    status: 'pending' | 'in_progress' | 'completed' | 'terminated',
    planStartDate?: string,
    planEndDate?: string,
  ) => {
    const raw = (status || 'pending').toLowerCase();
    if (raw === 'terminated') {
      return (
        <IpcNodeStatus
          progress="terminated"
          compact
          showLabel={false}
          title={milestoneStatusLabel('terminated')}
          className="milestone-node-status milestone-node-status--readonly"
        />
      );
    }

    const progress = normalizeNodeProgress(status) as NodeProgressStatus;
    const time = resolveNodeTimeStatus({ progress, planStartDate, planEndDate });
    const spec = schedule.specSchedules.find(s => s.id === specId);
    const specTerminated = normalizeSpecScheduleStatus(spec?.status) === 'terminated';

    if (!canMutateProject || schedule.status === 'archived' || specTerminated) {
      const title = specTerminated
        ? '规格已终止，节点状态不可修改'
        : !canMutateProject
          ? '无编辑权限'
          : '已归档版本不可修改';
      return (
        <IpcNodeStatus
          progress={progress}
          time={time}
          compact
          showLabel={false}
          title={title}
          className="milestone-node-status milestone-node-status--readonly"
        />
      );
    }

    return (
      <IpcNodeStatusPicker
        progress={progress}
        time={time}
        showLabel={false}
        className="milestone-node-status"
        excludeProgress={isAutoCompletionNode(milestoneId) ? ['completed'] : undefined}
        onChange={next => handleUpdateMilestoneStatus(schedule.id, specId, milestoneId, next)}
      />
    );
  };

  const getMilestoneTarget = (versionId: string, specId: string, milestoneId: string) => {
    const scheduleIndex = projectSchedules.findIndex(s => s.id === versionId);
    if (scheduleIndex === -1) return null;

    const schedule = projectSchedules[scheduleIndex];
    const specIndex = schedule.specSchedules.findIndex(s => s.id === specId);
    if (specIndex === -1) return null;

    const spec = schedule.specSchedules[specIndex];
    const milestoneIndex = spec.milestones.findIndex(m => m.milestoneId === milestoneId);
    const milestone = milestoneIndex >= 0 ? spec.milestones[milestoneIndex] : undefined;

    return { scheduleIndex, schedule, specIndex, spec, milestoneIndex, milestone };
  };

  // ========== 里程碑关联功能 ==========
  
  // 获取里程碑的关联关系
  const getMilestoneRelation = (schedule: ScheduleVersion, milestoneId: string): MilestoneRelation | undefined => {
    return schedule.milestoneRelations?.find(r => r.milestoneId === milestoneId);
  };

  // 获取节点的“有效前序节点”：优先显式前序；否则从其他节点的“后续节点=当前节点”反推
  const getEffectiveParentMilestoneId = (schedule: ScheduleVersion, milestoneId: string): string | undefined => {
    const relation = getMilestoneRelation(schedule, milestoneId);
    if (relation?.parentMilestoneId) return relation.parentMilestoneId;

    const inferred = schedule.milestoneRelations?.find(r =>
      r.calculationMode === 'auto' && r.nextMilestoneId === milestoneId
    );
    return inferred?.milestoneId;
  };

  // 获取节点的“有效后续节点”：优先显式后续；否则从其他节点的“前序节点=当前节点”反推
  const getEffectiveNextMilestoneId = (schedule: ScheduleVersion, milestoneId: string): string | undefined => {
    const relation = getMilestoneRelation(schedule, milestoneId);
    if (relation?.nextMilestoneId) return relation.nextMilestoneId;

    const inferred = schedule.milestoneRelations?.find(r =>
      r.calculationMode === 'auto' && r.parentMilestoneId === milestoneId
    );
    return inferred?.milestoneId;
  };

  /** 是否工作日：以 ipc 工作日历为准；该日无记录时视为工作日（不按周六日推断） */
  const isConfiguredWorkingDay = (d: Date): boolean => {
    const key = format(d, 'yyyy-MM-dd');
    const map = workCalendarByDateRef.current;
    if (map.has(key)) return map.get(key) === true;
    return true;
  };

  // 计算工作日天数（排除非工作日，按日历配置）
  const calculateWorkDays = (startDate: Date, durationDays: number): Date => {
    let currentDate = new Date(startDate);
    let daysAdded = 0;

    while (daysAdded < durationDays) {
      currentDate = addDays(currentDate, 1);
      if (isConfiguredWorkingDay(currentDate)) {
        daysAdded++;
      }
    }

    return currentDate;
  };

  // 反向计算工作日天数（排除非工作日，按日历配置）
  const calculateWorkDaysBackward = (endDate: Date, durationDays: number): Date => {
    let currentDate = new Date(endDate);
    let daysSubtracted = 0;

    while (daysSubtracted < durationDays) {
      currentDate = addDays(currentDate, -1);
      if (isConfiguredWorkingDay(currentDate)) {
        daysSubtracted++;
      }
    }

    return currentDate;
  };

  // 自动计算里程碑日期
  const calculateMilestoneDates = (
    schedule: ScheduleVersion, 
    spec: SpecSchedule, 
    milestoneId: string,
    relationOverride?: MilestoneRelation
  ): { startDate: string; endDate: string } | null => {
    const relation = relationOverride ?? getMilestoneRelation(schedule, milestoneId);
    if (!relation || relation.calculationMode === 'manual' || !relation.durationDays) {
      return null;
    }
    const offsetDays = relation.timeOffsetDays ?? 1;
    const isDesc = schedule.milestoneSortOrder === 'desc';
    if (!isDesc) {
      const effectiveParentMilestoneId = getEffectiveParentMilestoneId(schedule, milestoneId);
      if (!effectiveParentMilestoneId) return null;

      // 正排：当前开始 = 前序结束 + 时间偏移量
      const parentMilestone = spec.milestones.find(m => m.milestoneId === effectiveParentMilestoneId);
      if (!parentMilestone) return null;

      const parentEndStr = parentMilestone.endDate;
      if (!parentEndStr || typeof parentEndStr !== 'string') return null;
      const parentEndDate = parseISO(parentEndStr);
      if (Number.isNaN(parentEndDate.getTime())) return null;

      const startDate = addDays(parentEndDate, offsetDays);
      let endDate: Date;
      if (relation.excludeWeekends) {
        endDate = calculateWorkDays(startDate, relation.durationDays);
      } else {
        endDate = addDays(startDate, relation.durationDays);
      }

      return {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };
    }

    const effectiveNextMilestoneId = getEffectiveNextMilestoneId(schedule, milestoneId);
    if (!effectiveNextMilestoneId) return null;

    // 倒排：当前结束 = 后续开始 - 时间偏移量
    const nextMilestone = spec.milestones.find(m => m.milestoneId === effectiveNextMilestoneId);
    if (!nextMilestone) return null;

    const nextStartStr = nextMilestone.startDate;
    if (!nextStartStr || typeof nextStartStr !== 'string') return null;
    const nextStartDate = parseISO(nextStartStr);
    if (Number.isNaN(nextStartDate.getTime())) return null;

    const endDate = addDays(nextStartDate, -offsetDays);
    const startDate = relation.excludeWeekends
      ? calculateWorkDaysBackward(endDate, relation.durationDays)
      : addDays(endDate, -relation.durationDays);

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    };
  };

  /** 依赖关系排序：先按图拓扑，再按列顺序兜底，确保自动计算有稳定顺序 */
  const getMilestoneIdsByDependencyOrder = (schedule: ScheduleVersion): string[] => {
    const columns = getVersionMilestoneColumns(schedule);
    const allIds = columns.map(c => c.id);
    const orderMap = new Map<string, number>(columns.map(c => [c.id, c.order ?? 0]));
    const inDegree = new Map<string, number>(allIds.map(id => [id, 0]));
    const childrenMap = new Map<string, string[]>();
    allIds.forEach(id => childrenMap.set(id, []));

    for (const id of allIds) {
      const parentId = getEffectiveParentMilestoneId(schedule, id);
      if (!parentId || !childrenMap.has(parentId)) continue;
      childrenMap.get(parentId)?.push(id);
      inDegree.set(id, (inDegree.get(id) ?? 0) + 1);
    }

    const queue = allIds
      .filter(id => (inDegree.get(id) ?? 0) === 0)
      .sort((a, b) => (orderMap.get(a) ?? 0) - (orderMap.get(b) ?? 0));
    const result: string[] = [];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) break;
      result.push(current);

      const children = childrenMap.get(current) ?? [];
      for (const childId of children) {
        const nextIn = (inDegree.get(childId) ?? 0) - 1;
        inDegree.set(childId, nextIn);
        if (nextIn === 0) {
          queue.push(childId);
          queue.sort((a, b) => (orderMap.get(a) ?? 0) - (orderMap.get(b) ?? 0));
        }
      }
    }

    // 有环或孤立异常时，补齐未被拓扑到的节点，避免丢节点
    for (const id of allIds) {
      if (!result.includes(id)) result.push(id);
    }
    return result;
  };

  /**
   * 手动模式（或无关联配置视为手动）：不推算预计时间，仅按预计开始/结束与「今天」的关系
   * 更新状态，并按状态补全空的实际时间；规则与自动计算中的第 2/3 步一致。
   */
  const applyManualMilestoneStatusAndActualFromEstimatedDates = (
    inputSchedule: ScheduleVersion,
  ): ScheduleVersion => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return {
      ...inputSchedule,
      specSchedules: inputSchedule.specSchedules.map(spec => {
        if (normalizeSpecScheduleStatus(spec.status) === 'terminated') return spec;
        return {
          ...spec,
          milestones: spec.milestones.map(m => {
            const rel = getMilestoneRelation(inputSchedule, m.milestoneId);
            if (rel?.calculationMode === 'auto') return m;
            const current = { ...m };
            const actualStartEmpty =
              !current.actualStartDate || String(current.actualStartDate).trim() === '';
            const actualEndEmpty =
              !current.actualEndDate || String(current.actualEndDate).trim() === '';
            // 已完成但缺实际时间（如复制版本脏数据）：用预计时间补齐后返回
            if (current.status === 'completed') {
              if (actualStartEmpty) current.actualStartDate = current.startDate;
              if (actualEndEmpty) current.actualEndDate = current.endDate || current.startDate;
              return current;
            }
            if (current.status === 'terminated') return current;
            const forbidManualComplete = isAutoCompletionNode(m.milestoneId);
            let nextStatus: ScheduleMilestone['status'] = 'pending';
            if (current.endDate && current.endDate < today) {
              nextStatus = forbidManualComplete
                ? (current.startDate && current.startDate < today ? 'in_progress' : 'pending')
                : 'completed';
            } else if (current.startDate && current.startDate < today) {
              nextStatus = 'in_progress';
            }
            current.status = nextStatus;
            if (nextStatus === 'completed') {
              if (actualStartEmpty) current.actualStartDate = current.startDate;
              if (actualEndEmpty) current.actualEndDate = current.endDate;
            } else if (nextStatus === 'in_progress') {
              if (actualStartEmpty) current.actualStartDate = current.startDate;
            }
            return current;
          }),
        };
      }),
    };
  };

  /**
   * 自动计算三步：
   * 1) 按依赖排序后，从“第一个未完成的自动节点”开始更新预计时间（倒排反向）
   * 2) 按预计时间更新状态（开始<今天=>进行中；结束<今天=>已完成）
   * 3) 按状态补齐实际时间（若原有实际开始，则保持不变）
   */
  const recalculateAllAutoMilestoneDates = (
    inputSchedule: ScheduleVersion,
    startFromMilestoneId?: string,
  ): ScheduleVersion => {
    const dependencyOrderedIds = getMilestoneIdsByDependencyOrder(inputSchedule);
    const isDesc = inputSchedule.milestoneSortOrder === 'desc';
    const traversalIds = isDesc ? [...dependencyOrderedIds].reverse() : dependencyOrderedIds;
    const today = format(new Date(), 'yyyy-MM-dd');

    // 先复制一份可变结构，便于按顺序逐步计算并让后续节点读取到最新值
    const next: ScheduleVersion = {
      ...inputSchedule,
      specSchedules: inputSchedule.specSchedules.map(spec => ({
        ...spec,
        milestones: spec.milestones.map(m => ({ ...m })),
      })),
    };

    next.specSchedules = next.specSchedules.map(spec => {
      if (normalizeSpecScheduleStatus(spec.status) === 'terminated') return spec;
      const byId = new Map(spec.milestones.map(m => [m.milestoneId, m]));
      const autoIds = traversalIds.filter(id => {
        const rel = getMilestoneRelation(next, id);
        return !!rel && rel.calculationMode === 'auto' && !!byId.get(id);
      });
      if (autoIds.length === 0) return spec;

      // 默认：第一个“未完成”自动节点起算；
      // 若指定起点（编辑时间触发），从该节点起遍历：
      // - 当前节点仅自动更新状态/实际时间
      // - 后续节点继续执行预计时间+状态+实际时间
      const startIndex = startFromMilestoneId
        ? autoIds.indexOf(startFromMilestoneId)
        : autoIds.findIndex(id => {
            const status = byId.get(id)?.status ?? 'pending';
            return status !== 'completed' && status !== 'terminated';
          });
      if (startIndex === -1) return spec;

      for (let i = startIndex; i < autoIds.length; i++) {
        const milestoneId = autoIds[i];
        const rel = getMilestoneRelation(next, milestoneId);
        if (!rel || rel.calculationMode !== 'auto') continue;
        const current = byId.get(milestoneId);
        if (!current) continue;
        if (current.status === 'terminated') continue;

        const isEditedCurrent =
          !!startFromMilestoneId &&
          milestoneId === startFromMilestoneId &&
          i === startIndex;

        // 第一步：自动修改预计时间（编辑触发时保留当前编辑节点的手工时间）
        if (!isEditedCurrent) {
          const calculated = calculateMilestoneDates(next, spec, milestoneId, rel);
          if (!calculated) continue;
          current.startDate = calculated.startDate;
          current.endDate = calculated.endDate;
        }

        // 第二步：自动修改状态（completion_method=auto 禁止经此路径写成已完成）
        const forbidManualComplete = isAutoCompletionNode(milestoneId);
        let nextStatus: ScheduleMilestone['status'] = 'pending';
        if (current.endDate && current.endDate < today) {
          nextStatus = forbidManualComplete
            ? (current.startDate && current.startDate < today ? 'in_progress' : 'pending')
            : 'completed';
        } else if (current.startDate && current.startDate < today) {
          nextStatus = 'in_progress';
        }
        current.status = nextStatus;

        // 第三步：自动修改实际时间（按状态分别补空值，其余保持不变；已完成缺实际结束时回退预计开始）
        const actualStartEmpty = !current.actualStartDate || String(current.actualStartDate).trim() === '';
        const actualEndEmpty = !current.actualEndDate || String(current.actualEndDate).trim() === '';
        if (nextStatus === 'completed') {
          if (actualStartEmpty) current.actualStartDate = current.startDate;
          if (actualEndEmpty) current.actualEndDate = current.endDate || current.startDate;
        } else if (nextStatus === 'in_progress') {
          if (actualStartEmpty) current.actualStartDate = current.startDate;
        }
      }

      return {
        ...spec,
        milestones: spec.milestones.map(m => byId.get(m.milestoneId) ?? m),
      };
    });

    return next;
  };

  // 检查日期是否与自动计算冲突
  const hasDateConflict = (
    schedule: ScheduleVersion, 
    spec: SpecSchedule, 
    milestoneId: string,
    relationOverride?: MilestoneRelation,
    milestoneOverride?: ScheduleMilestone
  ): boolean => {
    const relation = relationOverride ?? getMilestoneRelation(schedule, milestoneId);
    if (!relation || relation.calculationMode === 'manual') return false;

    const calculated = calculateMilestoneDates(schedule, spec, milestoneId, relation);
    if (!calculated) return false;

    const milestone = milestoneOverride ?? spec.milestones.find(m => m.milestoneId === milestoneId);
    if (!milestone) return false;

    return milestone.startDate !== calculated.startDate || milestone.endDate !== calculated.endDate;
  };

  // 获取里程碑的层级深度（用于缩进显示）
  const getMilestoneDepth = (schedule: ScheduleVersion, milestoneId: string, visited: Set<string> = new Set()): number => {
    if (visited.has(milestoneId)) return 0; // 防止循环依赖
    
    const parentMilestoneId = getEffectiveParentMilestoneId(schedule, milestoneId);
    if (!parentMilestoneId) return 0;
    
    visited.add(milestoneId);
    return 1 + getMilestoneDepth(schedule, parentMilestoneId, visited);
  };

  // 打开关联配置弹窗
  const handleOpenRelationConfig = (schedule: ScheduleVersion) => {
    if (!canMutateProject) return;
    setRelationConfigVersionId(schedule.id);
    setTempRelations(schedule.milestoneRelations || []);
    setActiveMilestoneId(null);
    setRelationDirectionDrafts({});
    setRelationShowFieldErrors(false);
  };

  const handleOpenRelationConfigForMilestone = (schedule: ScheduleVersion, milestoneId: string) => {
    if (!canMutateProject) return;
    setRelationConfigVersionId(schedule.id);
    setTempRelations(schedule.milestoneRelations || []);
    setActiveMilestoneId(milestoneId);
    setRelationDirectionDrafts({});
    setRelationShowFieldErrors(false);
  };

  // 更新临时关联配置
  const updateTempRelation = (milestoneId: string, updates: Partial<MilestoneRelation>) => {
    if (!canMutateProject) return;
    setTempRelations(prev => {
      const existing = prev.find(r => r.milestoneId === milestoneId);
      if (existing) {
        return prev.map(r => r.milestoneId === milestoneId ? { ...r, ...updates } : r);
      } else {
        return [...prev, {
          id: `rel-${Date.now()}-${milestoneId}`,
          milestoneId,
          calculationMode: 'manual',
          timeOffsetDays: 1,
          ...updates
        }];
      }
    });
  };

  // 获取可用于作为父里程碑的选项（排除当前里程碑及其子里程碑）
  const getAvailableParentMilestones = (schedule: ScheduleVersion, currentMilestoneId: string): Milestone[] => {
    const columns = getVersionMilestoneColumns(schedule);
    const currentMilestone = columns.find(m => m.id === currentMilestoneId);
    const currentOrder = currentMilestone?.order ?? 0;
    
    // 获取当前里程碑的所有子里程碑（避免循环依赖）
    const getChildren = (parentId: string, visited: Set<string> = new Set()): Set<string> => {
      if (visited.has(parentId)) return visited;
      visited.add(parentId);
      
      schedule.milestoneRelations?.forEach(r => {
        if (r.parentMilestoneId === parentId) {
          getChildren(r.milestoneId, visited);
        }
      });
      
      return visited;
    };
    
    const children = getChildren(currentMilestoneId);
    
    // 自动计算模式下：依赖节点只能选择 order 小于当前节点的节点
    return columns.filter(m => m.id !== currentMilestoneId && !children.has(m.id) && (m.order ?? 0) < currentOrder);
  };

  // 获取可用于作为后续里程碑的选项（排除当前里程碑及其祖先里程碑）
  const getAvailableNextMilestones = (schedule: ScheduleVersion, currentMilestoneId: string): Milestone[] => {
    const columns = getVersionMilestoneColumns(schedule);
    const currentMilestone = columns.find(m => m.id === currentMilestoneId);
    const currentOrder = currentMilestone?.order ?? 0;

    const getAncestors = (childId: string, visited: Set<string> = new Set()): Set<string> => {
      if (visited.has(childId)) return visited;
      visited.add(childId);
      const rel = schedule.milestoneRelations?.find(r => r.milestoneId === childId);
      if (rel?.parentMilestoneId) {
        getAncestors(rel.parentMilestoneId, visited);
      }
      return visited;
    };

    const ancestors = getAncestors(currentMilestoneId);
    return columns.filter(m => m.id !== currentMilestoneId && !ancestors.has(m.id) && (m.order ?? 0) > currentOrder);
  };

  const getTempRelation = (milestoneId: string): MilestoneRelation | undefined =>
    tempRelations.find(r => r.milestoneId === milestoneId);

  const getRelationDirection = (
    relation?: MilestoneRelation,
    schedule?: ScheduleVersion,
    milestone?: Milestone,
  ): RelationDependencyDirection => {
    if (relation?.nextMilestoneId) return 'next';
    if (relation?.parentMilestoneId) return 'parent';
    if (milestone && relationDirectionDrafts[milestone.id]) return relationDirectionDrafts[milestone.id];
    if (!relation?.parentMilestoneId && schedule && milestone) {
      const parentOptions = getAvailableParentMilestones(schedule, milestone.id);
      const nextOptions = getAvailableNextMilestones(schedule, milestone.id);
      if (parentOptions.length === 0 && nextOptions.length > 0) return 'next';
    }
    return 'parent';
  };

  const getRelationConfigState = (
    schedule: ScheduleVersion,
    milestone: Milestone,
  ): { status: RelationConfigStatus; label: string; message?: string } => {
    const relation = getTempRelation(milestone.id);
    if (!relation || relation.calculationMode !== 'auto') {
      return { status: 'manual', label: '手动' };
    }

    const hasParent = relation.parentMilestoneId != null && String(relation.parentMilestoneId).trim() !== '';
    const hasNext = relation.nextMilestoneId != null && String(relation.nextMilestoneId).trim() !== '';
    const hasDuration = relation.durationDays != null && Number(relation.durationDays) > 0;
    const dependencyId = relation.parentMilestoneId ?? relation.nextMilestoneId;
    const columns = getVersionMilestoneColumns(schedule);
    const dependencyExists = !!dependencyId && columns.some(col => col.id === String(dependencyId));

    if (!(hasParent !== hasNext)) {
      return { status: 'pending', label: '待完善', message: '自动模式下请选择一个依赖方向和节点' };
    }

    if (!dependencyExists) {
      return { status: 'pending', label: '待完善', message: '依赖节点不存在或已不在当前版本里程碑列中' };
    }

    if (!hasDuration) {
      return { status: 'pending', label: '待完善', message: '自动模式下请填写持续天数' };
    }

    return { status: 'configured', label: '已配置' };
  };

  /** 与 getRelationConfigState 同一套规则，用于表单字段高亮（不改保存业务） */
  const getRelationFieldErrors = (
    schedule: ScheduleVersion,
    milestone: Milestone,
  ): { dependency?: string; duration?: string } => {
    const relation = getTempRelation(milestone.id);
    if (!relation || relation.calculationMode !== 'auto') return {};

    const hasParent = relation.parentMilestoneId != null && String(relation.parentMilestoneId).trim() !== '';
    const hasNext = relation.nextMilestoneId != null && String(relation.nextMilestoneId).trim() !== '';
    const hasDuration = relation.durationDays != null && Number(relation.durationDays) > 0;
    const dependencyId = relation.parentMilestoneId ?? relation.nextMilestoneId;
    const columns = getVersionMilestoneColumns(schedule);
    const dependencyExists = !!dependencyId && columns.some(col => col.id === String(dependencyId));

    const errors: { dependency?: string; duration?: string } = {};
    if (!(hasParent !== hasNext)) {
      errors.dependency = '请选择节点';
    } else if (!dependencyExists) {
      errors.dependency = '依赖节点不存在或已不在当前版本里程碑列中';
    }
    if (!hasDuration) {
      errors.duration = '请填写持续天数';
    }
    return errors;
  };

  const getDefaultRelationMilestoneId = (schedule: ScheduleVersion): string | null => {
    const columns = getVersionMilestoneColumns(schedule);
    const firstPending = columns.find(milestone => getRelationConfigState(schedule, milestone).status === 'pending');
    return firstPending?.id ?? columns[0]?.id ?? null;
  };

  const handleRelationModeChange = (schedule: ScheduleVersion, milestone: Milestone, mode: 'auto' | 'manual') => {
    const relation = getTempRelation(milestone.id);
    if (mode === 'manual') {
      setRelationDirectionDrafts(prev => {
        const next = { ...prev };
        delete next[milestone.id];
        return next;
      });
      updateTempRelation(milestone.id, {
        calculationMode: 'manual',
        parentMilestoneId: undefined,
        nextMilestoneId: undefined,
        durationDays: undefined,
      });
      return;
    }

    const parentOptions = getAvailableParentMilestones(schedule, milestone.id);
    const nextOptions = getAvailableNextMilestones(schedule, milestone.id);
    const validParentId = parentOptions.some(option => option.id === relation?.parentMilestoneId)
      ? relation?.parentMilestoneId
      : undefined;
    const validNextId = nextOptions.some(option => option.id === relation?.nextMilestoneId)
      ? relation?.nextMilestoneId
      : undefined;
    const shouldUseParentDirection = !!validParentId || (!validNextId && parentOptions.length > 0);
    setRelationDirectionDrafts(prev => ({
      ...prev,
      [milestone.id]: shouldUseParentDirection ? 'parent' : 'next',
    }));

    updateTempRelation(milestone.id, {
      calculationMode: 'auto',
      parentMilestoneId: shouldUseParentDirection ? validParentId : undefined,
      nextMilestoneId: shouldUseParentDirection ? undefined : validNextId,
      durationDays: relation?.durationDays,
      timeOffsetDays: relation?.timeOffsetDays ?? 1,
    });
  };

  const handleRelationDirectionChange = (milestoneId: string, direction: RelationDependencyDirection) => {
    setRelationDirectionDrafts(prev => ({ ...prev, [milestoneId]: direction }));
    updateTempRelation(milestoneId, {
      parentMilestoneId: undefined,
      nextMilestoneId: undefined,
    });
  };

  // 弹窗打开后默认选中第一个异常节点；从具体节点入口打开时保留指定节点
  useEffect(() => {
    if (!relationConfigVersionId) return;
    const schedule = projectSchedules.find(s => s.id === relationConfigVersionId);
    if (!schedule) return;

    const columns = getVersionMilestoneColumns(schedule);
    if (columns.length === 0) {
      if (activeMilestoneId) setActiveMilestoneId(null);
      return;
    }

    if (activeMilestoneId && columns.some(milestone => milestone.id === activeMilestoneId)) {
      return;
    }

    setActiveMilestoneId(getDefaultRelationMilestoneId(schedule));
  }, [relationConfigVersionId, activeMilestoneId, projectSchedules, tempRelations]);

  // 保存关联配置
  const handleSaveRelationConfig = async () => {
    if (!canMutateProject || !relationConfigVersionId) return;

    const schedule = projectSchedules.find(s => s.id === relationConfigVersionId);
    if (!schedule) return;

    const columns = getVersionMilestoneColumns(schedule);
    const invalidMilestone = columns.find(milestone => getRelationConfigState(schedule, milestone).status === 'pending');
    if (invalidMilestone) {
      setActiveMilestoneId(invalidMilestone.id);
      setRelationShowFieldErrors(true);
      return;
    }

    setRelationShowFieldErrors(false);

    // 校验通过后再更新内存中的关系（便于 UI 立即生效）
    schedule.milestoneRelations = tempRelations;

    // 构造后端需要的保存结构
    const relationsPayload = tempRelations.map(r => {
      const col = columns.find(c => c.id === r.milestoneId);
      const milestoneOrder = col ? col.order ?? 0 : 0;

      return {
        milestone_id: Number(r.milestoneId),
        milestone_order: milestoneOrder,
        column_width: DEFAULT_COLUMN_WIDTH,
        parent_milestone_id: r.parentMilestoneId ? Number(r.parentMilestoneId) : null,
        next_milestone_id: r.nextMilestoneId ? Number(r.nextMilestoneId) : null,
        duration_days: r.durationDays ?? null,
        time_offset_days: r.timeOffsetDays ?? 1,
        exclude_weekends: !!r.excludeWeekends,
        merge_across_specs: !!r.mergeAcrossSpecs,
        time_method: r.calculationMode === 'auto' ? 'auto' : 'manual',
      };
    });

    try {
      await relationApi.save(Number(schedule.id), { relations: relationsPayload });
      // 同步到 React state，确保弹框关闭后页面也能立即反映合并配置
      setProjectSchedules(prev =>
        prev.map(s => (s.id === schedule.id ? { ...s, milestoneRelations: tempRelations } : s))
      );
      message.success('关联配置已保存');
    } catch (e) {
      console.error('保存关联配置失败', e);
      message.error(e instanceof Error ? e.message : '保存关联配置失败');
    } finally {
      setRelationConfigVersionId(null);
      setActiveMilestoneId(null);
      setRelationDirectionDrafts({});
      setRelationShowFieldErrors(false);
    }
  };

  const brandOptions = brands.map(b => ({ value: b.code, label: b.name }));
  const getBrandDisplayName = (brandCodeOrName: string) => brands.find(b => b.code === brandCodeOrName)?.name ?? brandCodeOrName;
  const versionNameModalSchedule = showVersionNameModal && pendingEditVersionId ? projectSchedules.find(s => s.id === pendingEditVersionId) : null;
  const projectTableColumns = useMemo<ColumnsType<Project>>(() => [
    {
      title: '项目名称',
      dataIndex: 'name',
      width: 180,
      className: 'project-cell-name',
      render: (value: string | undefined) => {
        const text = value?.trim() || '-';
        return (
          <span className="project-cell-name-text" title={text === '-' ? undefined : text}>
            {text}
          </span>
        );
      },
    },
    {
      title: '项目编码',
      dataIndex: 'code',
      width: 140,
      className: 'project-cell-code',
      onCell: () => ({
        onClick: event => event.stopPropagation(),
      }),
      render: (_, project) => <ProjectCodeCell project={project} />,
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      width: 110,
      className: 'project-cell-brand',
      render: value => getBrandDisplayName(value),
    },
    {
      title: '关联IPD',
      key: 'linkedIpd',
      width: 220,
      className: 'project-cell-linked-ipd',
      onCell: () => ({
        onClick: event => event.stopPropagation(),
      }),
      render: (_, project) => <LinkedIpdCell items={project.linkedIpdProjects ?? []} />,
    },
    {
      title: 'NPD',
      dataIndex: 'npdNames',
      width: 120,
      className: 'project-cell-npd',
      ellipsis: true,
      render: (value: string | undefined) => value?.trim() || '—',
    },
    {
      title: '模板',
      dataIndex: 'templateId',
      width: 170,
      className: 'project-cell-template',
      onCell: () => ({
        onClick: event => event.stopPropagation(),
      }),
      render: (_, project) => (
        canMutateSchedule
          && (project.status ?? '').trim().toLowerCase() !== 'terminated'
          && (project.templateId == null || project.templateId <= 0) ? (
          <Select
            placeholder="选择模板"
            options={templates.map(t => ({ value: Number(t.id), label: t.name }))}
            style={{ width: '100%', minWidth: 140 }}
            onDropdownVisibleChange={open => {
              if (open) void loadTemplates();
            }}
            onChange={value => {
              if (value != null) void handleAssociateProjectTemplate(project, Number(value));
            }}
            getPopupContainer={() => document.body}
          />
        ) : (
          <span className="project-template-readonly">
            {project.templateName
              || (project.templateId != null && project.templateId > 0 ? `模板#${project.templateId}` : '-')}
          </span>
        )
      ),
    },
    {
      title: '入库日期',
      dataIndex: 'launchDate',
      width: 130,
      className: 'project-cell-launch',
      render: (_, project) => {
        const currentSched = projectSchedules.find(schedule => schedule.projectId === project.id && schedule.status === 'published');
        if (project.launchDate) return format(new Date(project.launchDate), 'yyyy-MM-dd');
        return currentSched ? currentSched.expectedLaunchDate : '-';
      },
    },
    {
      title: '版本数量',
      dataIndex: 'scheduleCount',
      width: 100,
      align: 'center',
      className: 'project-cell-versions',
      render: value => value ?? 0,
    },
    {
      title: '项目状态',
      dataIndex: 'status',
      width: 120,
      className: 'project-cell-status',
      render: value => (
        <IpcStatusBadge variant={projectStatusBadgeVariant(value)}>
          {projectStatusDisplayText(value)}
        </IpcStatusBadge>
      ),
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      width: 120,
      className: 'project-cell-audit project-cell-creator',
      render: value => <span title={value || '-'}>{value || '-'}</span>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 150,
      className: 'project-cell-audit project-cell-created-time',
      render: formatDateTime,
    },
    {
      title: '最近操作人',
      dataIndex: 'updatedBy',
      width: 130,
      className: 'project-cell-audit project-cell-modifier',
      render: value => <span title={value || '-'}>{value || '-'}</span>,
    },
    {
      title: '修改时间',
      dataIndex: 'updatedAt',
      width: 150,
      className: 'project-cell-audit project-cell-modified-time',
      render: formatDateTime,
    },
    {
      title: '操作',
      key: 'actions',
      width: 156,
      fixed: 'right',
      className: 'project-cell-action',
      onHeaderCell: () => ({ className: 'project-col-action' }),
      onCell: () => ({
        onClick: event => event.stopPropagation(),
      }),
      render: (_, project) => (
        <IpcRowActions
          maxVisible={3}
          actions={[
            {
              key: 'enter',
              label: '进入',
              onClick: () => {
                setSelectedProject(project);
                navigate(`/schedule/${project.id}`);
              },
            },
            ...(canMutateSchedule
              ? [{ key: 'delete', label: '删除', danger: true, onClick: () => handleDeleteProject(project) }]
              : []),
          ]}
        />
      ),
    },
  ], [
    canMutateSchedule,
    canMutateProject,
    formatDateTime,
    getBrandDisplayName,
    handleAssociateProjectTemplate,
    handleDeleteProject,
    loadTemplates,
    navigate,
    projectSchedules,
    templates,
  ]);

  return (
    <div
      className={`project-schedule ipc-page${isDetailRoute ? ' project-schedule-detail' : ' project-schedule-list'}`}
      style={{ width: '100%' }}
    >
      {/* 新建项目弹框 */}
      <IpcModal
        title="新建项目"
        open={showCreateProjectModal && canMutateSchedule}
        onOk={handleConfirmCreateProject}
        onCancel={() => {
          setShowCreateProjectModal(false);
          setShowIpdPickerModal(false);
        }}
        okText="创建"
        cancelText="取消"
        width={520}
        confirmLoading={createProjectLoading}
      >
        <div className="ipc-modal-form">
          <div>
            <label className="form-label"><span className="required">*</span> 选择 IPD 项目</label>
            <button
              type="button"
              className={`ipd-project-trigger${createProjectErrors.ipd ? ' ipd-project-trigger--error' : ''}`}
              onClick={() => {
                setIpdPickerMode('create');
                setShowIpdPickerModal(true);
              }}
            >
              {selectedIpdProjects.length === 0 ? (
                <span className="ipd-project-trigger__placeholder">请选择 IPD 项目 (编码 + 名称)</span>
              ) : (
                <span className="ipd-project-trigger__value">
                  <span className="ipd-project-trigger__text">
                    {formatIpdProjectLabel(selectedIpdProjects[0])}
                    {selectedIpdProjects.length > 1 ? ` 等${selectedIpdProjects.length}项` : ''}
                  </span>
                  <IpcTag variant="primary">✓ 已选</IpcTag>
                </span>
              )}
              <ChevronDown size={16} className="ipd-project-trigger__arrow" />
            </button>
            {createProjectErrors.ipd
              ? <div className="form-field-error">{createProjectErrors.ipd}</div>
              : <div className="form-field-helper">选择后自动填入编码与品牌（不可改）；名称、入库日期可修改</div>}
          </div>
          <div className="ipd-create-divider" />
          <div className={selectedIpdProjects.length > 0 ? 'ipc-ipd-autofill' : undefined}>
            <label className="form-label">项目编码</label>
            <Input
              value={createProjectForm.code}
              disabled
              placeholder={selectedIpdProjects.length > 0 ? '由所选 IPD 项目自动填入，可为空' : '选择 IPD 项目后自动填入'}
              status={createProjectErrors.code ? 'error' : undefined}
            />
            {createProjectErrors.code && <div className="form-field-error">{createProjectErrors.code}</div>}
          </div>
          <div>
            <label className="form-label">项目名称</label>
            <Input
              value={createProjectForm.name}
              onChange={e => { setCreateProjectForm(f => ({ ...f, name: e.target.value })); setCreateProjectErrors(prev => ({ ...prev, name: undefined })); }}
              placeholder={selectedIpdProjects.length > 0 ? '选择 IPD 项目后自动填入，可修改' : '请输入项目名称'}
              status={createProjectErrors.name ? 'error' : undefined}
            />
            {createProjectErrors.name && <div className="form-field-error">{createProjectErrors.name}</div>}
          </div>
          <div>
            <label className="form-label">入库日期</label>
            <DatePicker
              style={{ width: '100%' }}
              placeholder={selectedIpdProjects.length > 0 ? '选择 IPD 项目后自动填入，可修改' : '请选择入库日期'}
              value={createProjectForm.launchDate ? dayjs(createProjectForm.launchDate) : undefined}
              onChange={d => {
                setCreateProjectForm(f => ({ ...f, launchDate: d ? d.toISOString() : undefined }));
                setCreateProjectErrors(prev => ({ ...prev, launchDate: undefined }));
              }}
              getPopupContainer={() => document.body}
            />
            {createProjectErrors.launchDate && <div className="form-field-error">{createProjectErrors.launchDate}</div>}
          </div>
          <div className={selectedIpdProjects.length > 0 ? 'ipc-ipd-autofill' : undefined}>
            <label className="form-label">品牌</label>
            <Select
              style={{ width: '100%' }}
              placeholder={selectedIpdProjects.length > 0 ? '由所选 IPD 项目自动填入' : '选择 IPD 项目后自动填入'}
              value={createProjectForm.brand || undefined}
              options={brandOptions}
              disabled
              showSearch
              optionFilterProp="label"
              getPopupContainer={() => document.body}
              status={createProjectErrors.brand ? 'error' : undefined}
              loading={brandsLoading}
              notFoundContent={brandsLoading ? '加载中...' : '暂无品牌数据'}
            />
            {createProjectErrors.brand && <div className="form-field-error">{createProjectErrors.brand}</div>}
          </div>
          <div>
            <label className="form-label"><span className="required">*</span> 排期模板</label>
            <Select
              style={{ width: '100%' }}
              placeholder="选择用于创建项目排期的模板"
              value={createProjectForm.templateId ?? undefined}
              onChange={v => {
                setCreateProjectForm(f => ({ ...f, templateId: v ?? '' }));
                setCreateProjectErrors(prev => ({ ...prev, templateId: undefined }));
              }}
              options={templates.map(t => ({ value: t.id, label: t.name }))}
              allowClear
              getPopupContainer={() => document.body}
              status={createProjectErrors.templateId ? 'error' : undefined}
            />
            {createProjectErrors.templateId && <div className="form-field-error">{createProjectErrors.templateId}</div>}
          </div>
        </div>
      </IpcModal>

      <IpdProjectPickerModal
        open={showIpdPickerModal && canMutateProject}
        selected={selectedIpdProjects}
        defaultBrand={ipdPickerMode === 'replace' ? (selectedProject?.brand ?? '') : ''}
        lockBrand={ipdPickerMode === 'replace'}
        lockedIds={ipdPickerMode === 'replace' ? replaceIpdLockedIds : []}
        title={ipdPickerMode === 'replace' ? '更换 IPD 项目' : '选择 IPD 项目'}
        requireSelection={ipdPickerMode === 'replace'}
        onCancel={() => setShowIpdPickerModal(false)}
        onOk={handleIpdPickerOk}
      />

      <IpdSkuPickerModal
        open={showSkuPickerModal && !!selectedProject}
        ipcProjectId={Number(selectedProject?.id || 0)}
        scheduleId={skuPickerScheduleId ? Number(skuPickerScheduleId) : undefined}
        currentSpecId={(skuPickerMode === 'edit' || skuPickerMode === 'view') && skuPickerSpecId ? Number(skuPickerSpecId) : undefined}
        titlePrefix={
          skuPickerMode === 'copy'
            ? '复制规格'
            : skuPickerMode === 'view'
              ? '查看规格关联'
              : '添加规格'
        }
        currentRowLabel={
          skuPickerMode === 'copy'
            ? '请为复制后的规格选择关联 IPD SKU'
            : undefined
        }
        readOnly={skuPickerMode === 'view'}
        selected={skuPickerSelected}
        getOccupancy={getSkuOccupancy}
        onCancel={closeSkuPicker}
        onOk={handleSkuPickerOk}
      />

      <SpecTerminateModal
        open={pauseModalOpen && !!pauseTarget}
        specTitle={pauseTarget?.title ?? ''}
        milestones={pauseTarget?.milestones ?? []}
        cols={pauseTarget?.cols ?? []}
        cells={pauseTarget?.cells ?? {}}
        confirmLoading={pauseLoading}
        onCancel={() => {
          if (pauseLoading) return;
          setPauseModalOpen(false);
          setPauseTarget(null);
        }}
        onConfirm={handleConfirmTerminateSpec}
      />

      <ProjectTerminateModal
        open={projectTerminateOpen && !!projectTerminateTarget}
        milestones={projectTerminateTarget?.milestones ?? []}
        cols={projectTerminateTarget?.cols ?? []}
        cells={projectTerminateTarget?.cells ?? {}}
        confirmLoading={projectTerminateLoading}
        onCancel={() => {
          if (projectTerminateLoading) return;
          setProjectTerminateOpen(false);
          setProjectTerminateTarget(null);
        }}
        onConfirm={handleConfirmProjectTerminate}
      />

      {/* 修改版本名称（草稿直接改）/ 新建调整版本（从生效版） */}
      <IpcModal
        title={versionNameModalSchedule?.status === 'draft' ? '修改版本名称' : '新建调整版本'}
        open={showVersionNameModal && canMutateProject}
        onOk={handleConfirmNewVersionName}
        onCancel={() => { setShowVersionNameModal(false); setPendingEditVersionId(null); }}
        okText="确定"
        cancelText="取消"
        width={400}
      >
        <div className="ipc-modal-form ipc-modal-form--compact">
          <div>
            <label className="form-label">版本名称</label>
            <Input
              value={versionNameInput}
              onChange={e => setVersionNameInput(e.target.value)}
              placeholder="如：调整版-0305"
            />
          </div>
          <div>
            <label className="form-label">入库日期</label>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择入库日期"
              value={versionLaunchDateInput ? dayjs(versionLaunchDateInput) : undefined}
              onChange={d => setVersionLaunchDateInput(d ? d.toISOString() : undefined)}
              getPopupContainer={() => document.body}
            />
          </div>
        </div>
      </IpcModal>

      {isDetailRoute && (detailProjectLoading || !selectedProject || Number(selectedProject.id) !== routeProjectId) ? (
        <div className="project-schedule-detail-loading">
          <Spin size="large" />
          <span className="project-schedule-detail-loading__text">正在加载项目排期...</span>
        </div>
      ) : !selectedProject ? (
        // 项目列表视图
        <>
          <IpcFilterToolbar
            onReset={resetProjectListFiltersToDefault}
            onSubmit={handleFilterSearch}
            chipItems={projectListAppliedChips}
            onRemoveChip={removeProjectListAppliedChip}
            onClearChips={clearAllProjectListFilters}
            extra={
              canMutateProject ? (
                <>
                  <IpcButton
                    variant="secondary"
                    onClick={handleRefreshMilestoneStatus}
                    disabled={refreshingMilestoneStatus}
                  >
                    {refreshingMilestoneStatus ? '刷新中...' : '刷新节点状态'}
                  </IpcButton>
                  <IpcButton variant="primary" icon={<Plus size={14} />} onClick={openCreateProjectModal}>
                    新建项目
                  </IpcButton>
                </>
              ) : undefined
            }
          >
            <Input
              placeholder="项目名称"
              value={projectListDraft.filterName}
              onChange={e => {
                patchProjectListDraft({ filterName: e.target.value });
              }}
              allowClear
              className="ipc-filter-bar__field"
            />
            <Input
              placeholder="项目编码"
              value={projectListDraft.filterCode}
              onChange={e => patchProjectListDraft({ filterCode: e.target.value })}
              allowClear
              className="ipc-filter-bar__field"
            />
            <Select
              placeholder="全部品牌"
              value={projectListDraft.filterBrand || undefined}
              onChange={v => fetchProjectListIfApplied(patchAndApplyProjectList({ filterBrand: v ?? '' }))}
              allowClear
              className="ipc-filter-bar__field"
              options={brands.map(b => ({ value: b.code, label: b.name }))}
              getPopupContainer={() => document.body}
            />
            <IpcDateRangePicker
              variant="filter"
              value={projectListDraft.filterLaunchDateRange ?? [undefined, undefined]}
              onChange={([start, end]) => {
                const next = !start && !end ? null : ([start ?? '', end ?? ''] as [string, string]);
                fetchProjectListIfApplied(patchAndApplyProjectList({ filterLaunchDateRange: next }));
              }}
              className="ipc-filter-bar__field ipc-filter-bar__field--range"
              placeholder={['入库开始', '入库结束']}
            />
            <Select
              mode="multiple"
              placeholder="项目状态"
              value={projectListDraft.filterStatuses}
              onChange={values => fetchProjectListIfApplied(patchAndApplyProjectList({ filterStatuses: values ?? [] }))}
              allowClear
              optionFilterProp="label"
              {...IPC_FILTER_MULTI_SELECT_PROPS}
              className="ipc-filter-bar__field"
              options={projectStatusOptions()}
              getPopupContainer={() => document.body}
            />
            <Select
              mode="multiple"
              placeholder="排期创建人"
              value={projectListDraft.filterScheduleCreatorIds}
              onChange={values => fetchProjectListIfApplied(patchAndApplyProjectList({ filterScheduleCreatorIds: values }))}
              allowClear
              showSearch
              optionFilterProp="label"
              {...IPC_FILTER_MULTI_SELECT_PROPS}
              className="ipc-filter-bar__field"
              options={scheduleCreatorOptions}
              getPopupContainer={() => document.body}
            />
          </IpcFilterToolbar>

          <IpcTable<Project>
            listViewKey={LIST_VIEW_KEYS.schedule}
            compact
            frameless
            resizable
            rowKey={project => project.id}
            columns={projectTableColumns}
            dataSource={projects}
            loading={projectListLoading}
            pagination={projectListTotal > 0 ? {
              className: 'ipc-table-pagination',
              current: projectListPage,
              pageSize: projectListPageSize,
              total: projectListTotal,
              onChange: (page, pageSize) => {
                const coords = setProjectListPagination(page, pageSize);
                void fetchProjectList(coords.page, coords.pageSize);
              },
            } : false}
            size="small"
            tableLayout="fixed"
            autoHeight
            autoHeightDeps={[projects.length, projectListPage, projectListPageSize, projectListLoading]}
            scroll={{ x: 1646 }}
            panelClassName="projects-table-panel"
            scrollClassName={[
              'projects-table-scroll',
              projects.length === 0 && !projectListLoading ? 'ipc-empty-host' : '',
            ].filter(Boolean).join(' ')}
            className="projects-table"
            rowClassName="project-table-row"
            onRow={project => ({
              onClick: () => {
                setSelectedProject(project);
                navigate(`/schedule/${project.id}`);
              },
            })}
            emptyTitle="暂无项目"
            emptyDescription="请调整筛选条件或新建项目"
          />
        </>
      ) : (
        // 项目详情视图 - 左右布局（P0 壳：A2 左栏返回+版本 / 右栏页头 meta）
        <div className="project-detail-layout">
          <aside
            className={`version-sidebar${versionSidebarCollapsed ? ' version-sidebar--collapsed' : ''}`}
            aria-label="版本导航"
          >
            <div className="version-sidebar-top">
              {!versionSidebarCollapsed && (
                <div className="version-sidebar-brand">项目排期</div>
              )}
              <button
                type="button"
                className="version-sidebar-toggle"
                title={versionSidebarCollapsed ? '展开版本导航' : '收起版本导航'}
                aria-expanded={!versionSidebarCollapsed}
                aria-label={versionSidebarCollapsed ? '展开版本导航' : '收起版本导航'}
                onClick={() => setVersionSidebarCollapsed(prev => !prev)}
              >
                {versionSidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
              </button>
            </div>

            {!versionSidebarCollapsed && (
              <div className="version-nav-list">
                {projectSchedules.length === 0 ? (
                  <div className="version-nav-empty">暂无版本</div>
                ) : (
                  projectSchedules.map(schedule => (
                    <button
                      key={schedule.id}
                      type="button"
                      className={`version-nav-item ${activeVersionId === schedule.id ? 'active' : ''} ${schedule.status === 'published' ? 'published' : ''}`}
                      onClick={() => scrollToVersion(schedule.id)}
                    >
                      <div className="version-nav-main">
                        <span className="version-nav-number">v{schedule.version}</span>
                        <IpcStatusBadge
                          variant={
                            schedule.status === 'published'
                              ? 'success'
                              : schedule.status === 'draft'
                                ? 'warning'
                                : 'muted'
                          }
                        >
                          {schedule.status === 'published' ? '生效中' : schedule.status === 'draft' ? '草稿' : '已归档'}
                        </IpcStatusBadge>
                      </div>
                      <div className="version-nav-name">{schedule.name}</div>
                      <div className="version-nav-meta">
                        <span>{formatYMD(schedule.createdAt) || formatYMD(schedule.expectedLaunchDate)}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </aside>

          <div className="schedule-main-content schedule-main-with-tabs" ref={mainContentRef}>
            <header className="schedule-detail-header">
              <div className="schedule-detail-header__main">
                <div className="schedule-detail-header__title-row">
                  {editingProjectField === 'name' ? (
                    <Input
                      className="schedule-detail-header__title-input"
                      size="middle"
                      value={projectNameDraft}
                      onChange={e => setProjectNameDraft(e.target.value)}
                      onBlur={() => {
                        const next = projectNameDraft.trim();
                        if (!selectedProject) return;
                        if (next === selectedProject.name) {
                          exitEditProjectField();
                          return;
                        }
                        if (!next) {
                          message.error('项目名称不能为空');
                          return;
                        }
                        saveProjectField({ name: next });
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                        if (e.key === 'Escape') exitEditProjectField();
                      }}
                      autoFocus
                    />
                  ) : (
                    <>
                      <h1 className="schedule-detail-header__title">{selectedProject.name}</h1>
                      {canMutateProject && !projectSaving && (
                        <button
                          type="button"
                          className="schedule-detail-header__edit"
                          title="编辑项目名称"
                          onClick={() => startEditProjectField('name')}
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="schedule-detail-header__meta">
                  <span className="schedule-detail-header__meta-slot schedule-detail-header__meta-slot--brand">
                    <span className="schedule-detail-header__meta-static">
                      {getBrandDisplayName(selectedProject.brand)}
                    </span>
                  </span>
                  <span className="schedule-detail-header__meta-slot schedule-detail-header__meta-slot--status">
                    <IpcStatusBadge variant={projectStatusBadgeVariant(selectedProject.status)}>
                      {projectStatusDisplayText(selectedProject.status)}
                    </IpcStatusBadge>
                  </span>
                  <span className="schedule-detail-header__meta-slot schedule-detail-header__meta-slot--launch">
                    {editingProjectField === 'launchDate' ? (
                      <IpcDatePicker
                        variant="inline"
                        value={projectLaunchDateDraft}
                        onChange={next => {
                          saveProjectField({ launchDate: next });
                        }}
                        onOpenChange={open => {
                          if (!open) exitEditProjectField();
                        }}
                        disabled={projectSaving}
                      />
                    ) : (
                      <button
                        type="button"
                        className="schedule-detail-header__meta-btn"
                        disabled={!canMutateProject || projectSaving}
                        onClick={() => canMutateProject && !projectSaving && startEditProjectField('launchDate')}
                      >
                        <Calendar size={12} aria-hidden />
                        入库:{' '}
                        {selectedProject.launchDate
                          ? format(new Date(selectedProject.launchDate), 'yyyy-MM-dd')
                          : '—'}
                      </button>
                    )}
                  </span>
                  <span className="schedule-detail-header__meta-static">
                    <GitBranch size={12} aria-hidden />
                    {projectSchedules.length} 个版本
                  </span>
                  <span className="schedule-detail-header__ipd">
                    <span className="schedule-detail-header__ipd-label">关联 IPD:</span>
                    <span className="schedule-detail-header__ipd-tags">
                      <LinkedIpdCell
                        items={selectedProject.linkedIpdProjects ?? []}
                        formatLabel={linkedIpdDetailLabel}
                        maxVisible={3}
                      />
                    </span>
                    {canMutateProject && (
                      <button
                        type="button"
                        className="schedule-detail-header__ipd-replace"
                        disabled={projectSaving}
                        onClick={openReplaceIpdPicker}
                      >
                        查看/编辑
                      </button>
                    )}
                  </span>
                </div>
              </div>
              {canMutateProject && (
                <div className="schedule-detail-header__actions">
                  <IpcButton
                    variant="secondary"
                    id="btnImportAction"
                    icon={<Upload size={14} />}
                    onClick={() => setShowImportModal(true)}
                  >
                    导入配置
                  </IpcButton>
                  {(selectedProject.status ?? '').trim().toLowerCase() !== 'completed' && (
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'terminate',
                            label: '终止',
                            danger: true,
                            onClick: () => void openProjectTerminateModal(),
                          },
                        ],
                      }}
                      trigger={['click']}
                    >
                      <span className="project-ops-dropdown-trigger">
                        <IpcButton variant="secondary" id="btnProjectOps">
                          操作
                          <ChevronDown size={14} aria-hidden />
                        </IpcButton>
                      </span>
                    </Dropdown>
                  )}
                </div>
              )}
            </header>

            <IpcModuleTabs
              className="schedule-detail-tabs"
              activeKey={scheduleDetailTab}
              onChange={key => {
                if (key === 'gantt') setScheduleDetailTab('gantt');
                else if (key === 'oplog') setScheduleDetailTab('oplog');
                else setScheduleDetailTab('data');
              }}
              destroyInactiveTabPane={false}
              items={[
                {
                  key: 'data',
                  label: (
                    <span className="schedule-detail-tab-label">
                      <Table2 size={16} aria-hidden />
                      数据
                    </span>
                  ),
                  children: (
            <div className="schedule-data-tab-scroll" ref={scheduleDataTabScrollRef}>
            <div className="version-cards-container">
              {projectSchedules.map(schedule => {
                const isEditing = editingVersionId === schedule.id;
                const isPublished = schedule.status === 'published';
                // 已发布版本可直接改实际时间、节点备注与进度同步，无需进入「编辑」克隆草稿
                const canEditActualOrNote = isEditing || (isPublished && canMutateProject);
                const canEditProgressSync = canEditActualOrNote;
                const milestoneColumns = getVersionMilestoneColumns(schedule);
                const relationMap = new Map<string, MilestoneRelation>((schedule.milestoneRelations ?? []).map(r => [r.milestoneId, r]));
                const specNameWidth = getColumnWidth(schedule.id, 'specName');
                const specStatusWidth = getColumnWidth(schedule.id, 'specStatus');
                const specTableColumns: ColumnsType<SpecSchedule> = [
                  {
                    title: (
                      <span className="ipc-table-header-title ipc-table-header-title--start spec-name-header">
                        <span className="ipc-table-header-title__text">
                          规格名称{isEditing ? ' (点击可添加)' : ' (点击可查看)'}
                        </span>
                      </span>
                    ),
                    key: 'specName',
                    fixed: 'left',
                    width: specNameWidth,
                    render: (_, spec) => (
                      <button
                        type="button"
                        className="spec-name-card spec-name-card--editable"
                        onClick={() => openSkuPickerForSpec(schedule.id, spec, { readOnly: !isEditing })}
                        title={
                          isEditing
                            ? formatSpecRowTitle(spec)
                            : `${formatSpecRowTitle(spec)}（点击查看）`
                        }
                      >
                        <span className="spec-name-card__title-row">
                          <span className="spec-name-card__title">{formatSpecRowTitle(spec)}</span>
                          {hasLinkedIpd(spec) ? (
                            <span className="spec-name-card__ipd-mark" title="已关联 IPD">
                              IPD
                            </span>
                          ) : null}
                        </span>
                        {formatSpecRowSkuCodes(spec) ? (
                          <span className="spec-name-card__skus">{formatSpecRowSkuCodes(spec)}</span>
                        ) : null}
                      </button>
                    ),
                  },
                  {
                    title: (
                      <span className="ipc-table-header-title ipc-table-header-title--start">
                        <span className="ipc-table-header-title__text">状态</span>
                      </span>
                    ),
                    key: 'specStatus',
                    fixed: 'left',
                    width: specStatusWidth,
                    render: (_, spec) => (
                      <IpcStatusBadge variant={specStatusBadgeVariant(spec.status)}>
                        {specScheduleStatusDisplayText(spec.status)}
                      </IpcStatusBadge>
                    ),
                  },
                  ...milestoneColumns.map((milestone, index) => {
                    const isAutoCalculated = relationMap.get(milestone.id)?.calculationMode === 'auto';
                    const colWidth = getMilestoneColumnWidth(schedule, milestone.id);
                    return {
                      title: (
                        <span className="ipc-table-header-title ipc-table-header-title--center">
                          <span className="ipc-table-header-title__text">{milestone.name}</span>
                          {isEditing && (
                            <button
                              type="button"
                              className="ipc-table-header-inline-action"
                              title={`${isAutoCalculated ? '自动' : '手动'}模式，点击配置关联`}
                              onClick={() => handleOpenRelationConfigForMilestone(schedule, milestone.id)}
                            >
                              {isAutoCalculated ? <Calculator size={10} /> : <Link2 size={10} />}
                            </button>
                          )}
                        </span>
                      ),
                      key: milestone.id,
                      align: 'center',
                      width: colWidth,
                      onHeaderCell: () => ({
                        className: [
                          dragColumn.versionId === schedule.id && dragColumn.dragIndex === index ? 'ipc-table-header-cell--dragging' : '',
                          dragColumn.versionId === schedule.id && dragColumn.dropIndex === index && dragColumn.dragIndex !== index ? 'ipc-table-header-cell--drop-target' : '',
                        ].filter(Boolean).join(' ') || undefined,
                        draggable: schedule.status === 'draft' && canMutateProject,
                        onDragStart: schedule.status === 'draft' && canMutateProject ? () => handleColumnDragStart(schedule.id, index) : undefined,
                        onDragOver: schedule.status === 'draft' && canMutateProject ? (e: React.DragEvent<HTMLTableCellElement>) => handleColumnDragOver(e, schedule.id, index) : undefined,
                        onDrop: schedule.status === 'draft' && canMutateProject ? (e: React.DragEvent<HTMLTableCellElement>) => handleColumnDrop(e, schedule.id, index) : undefined,
                        onDragEnd: handleColumnDragEnd,
                      }),
                      render: () => null,
                    };
                  }),
                  ...(canMutateProject && schedule.status !== 'archived' ? [{
                    title: '操作',
                    key: 'actions',
                    fixed: 'right' as const,
                    width: 88,
                    render: () => null,
                  }] : []),
                ];

                const showTerminated = !!showTerminatedByScheduleId[schedule.id];
                const visibleSpecSchedules = showTerminated
                  ? schedule.specSchedules
                  : schedule.specSchedules.filter(
                      sp => normalizeSpecScheduleStatus(sp.status) !== 'terminated',
                    );
                
                return (
                  <div 
                    key={schedule.id}
                    ref={el => {
                      if (el) versionCardRefs.current.set(schedule.id, el);
                    }}
                    className={`version-card ${isPublished ? 'published' : ''} ${activeVersionId === schedule.id ? 'active' : ''}`}
                  >
                    {/* 卡片头部：标题/状态/元信息同一行（对齐原型 schedule-data） */}
                    <div className="version-card-header">
                      <div className="version-info">
                        <div className="version-title">
                          <span className="version-number">V{schedule.version}</span>
                          {schedule.status === 'draft' ? (
                            canMutateProject ? (
                              <button
                                type="button"
                                className="version-name version-name-editable"
                                onClick={() => handleEditDraftVersionName(schedule)}
                                title="点击修改版本名称"
                              >
                                {schedule.name}
                              </button>
                            ) : (
                              <span className="version-name">{schedule.name}</span>
                            )
                          ) : (
                            <span className="version-name">{schedule.name}</span>
                          )}
                        </div>
                        <IpcStatusBadge variant={isPublished ? 'success' : schedule.status === 'draft' ? 'warning' : 'muted'}>
                          {isPublished ? '生效中' : schedule.status === 'draft' ? '草稿' : '已归档'}
                        </IpcStatusBadge>
                        <span className="meta-item">
                          <Calendar size={12} />
                          入库日期: {formatYMD(schedule.expectedLaunchDate)}
                        </span>
                        <span className="meta-item">
                          {schedule.specSchedules.length}个规格 · {milestoneColumns.length}个里程碑
                        </span>
                      </div>
                      
                      <div className="version-actions">
                        {canMutateProject && (
                          <>
                            {/* 已归档/已发布版本不显示配置列、配置关联 */}
                            {schedule.status !== 'archived' && schedule.status !== 'published' && (
                              <>
                                <div className="sort-order-toggle" title={isEditing ? '切换里程碑列排序方式' : '仅编辑模式可切换'}>
                                  <button
                                    className={`sort-order-btn sort-order-asc ${(schedule.milestoneSortOrder ?? 'asc') === 'asc' ? 'active' : ''}`}
                                    onClick={() => void handleSetMilestoneSortOrder(schedule, 'asc')}
                                    disabled={!isEditing}
                                  >
                                    正排
                                  </button>
                                  <button
                                    className={`sort-order-btn sort-order-desc ${(schedule.milestoneSortOrder ?? 'asc') === 'desc' ? 'active' : ''}`}
                                    onClick={() => void handleSetMilestoneSortOrder(schedule, 'desc')}
                                    disabled={!isEditing}
                                  >
                                    倒排
                                  </button>
                                </div>
                                <IpcButton
                                  size="small"
                                  variant="secondary"
                                  icon={<Settings size={12} />}
                                  onClick={() => handleOpenColumnConfig(schedule)}
                                  title="配置此版本的显示列"
                                >
                                  配置列
                                </IpcButton>
                                <IpcButton
                                  size="small"
                                  variant="secondary"
                                  icon={<Link2 size={12} />}
                                  onClick={() => handleOpenRelationConfig(schedule)}
                                  title="配置里程碑关联关系"
                                >
                                  配置关联
                                </IpcButton>
                              </>
                            )}

                            {/* 已归档版本不显示编辑按钮 */}
                            {schedule.status !== 'archived' && (
                              <>
                                {isPublished && !isEditing && (
                                  <IpcButton
                                    size="small"
                                    variant="primary"
                                    onClick={() => handleEditPublishedVersion(schedule.id)}
                                  >
                                    编辑
                                  </IpcButton>
                                )}
                                {schedule.status === 'draft' && (
                                  <>
                                    <IpcButton
                                      size="small"
                                      variant="primary"
                                      onClick={() => handlePublishVersion(schedule.id)}
                                    >
                                      发布
                                    </IpcButton>
                                    {schedule.status === 'draft' && projectSchedules.length > 1 && (
                                      <IpcButton
                                        size="small"
                                        variant="destructive"
                                        onClick={() => handleDeleteVersion(schedule.id)}
                                      >
                                        删除
                                      </IpcButton>
                                    )}
                                    <IpcButton
                                      size="small"
                                      variant={isEditing ? 'primary' : 'secondary'}
                                      onClick={() => handleToggleEditVersion(schedule)}
                                    >
                                      {isEditing ? '保存' : '编辑'}
                                    </IpcButton>
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* 规格表格 */}
                    <div className="version-card-body">
                      <div className="spec-row-display-filter">
                        <span className="spec-row-display-filter__label">规格行显示:</span>
                        <Checkbox
                          checked={showTerminated}
                          onChange={e => {
                            const checked = e.target.checked;
                            setShowTerminatedByScheduleId(prev => ({
                              ...prev,
                              [schedule.id]: checked,
                            }));
                          }}
                        >
                          显示已终止 SKU
                        </Checkbox>
                      </div>
                      <IpcTable<SpecSchedule>
                        frameless
                        compact
                        resizable
                        sortable={false}
                        resizableMinWidth={MIN_COLUMN_WIDTH}
                        resizableWidths={{
                          ...(columnWidths[schedule.id] ?? {}),
                          ...Object.fromEntries(
                            milestoneColumns.map(m => [m.id, getMilestoneColumnWidth(schedule, m.id)]),
                          ),
                        }}
                        onResizableWidthsChange={widths => handleSpecColumnWidthsChange(schedule.id, widths)}
                        onResizableWidthCommit={(columnKey, width) => {
                          void handleSpecColumnWidthCommit(schedule.id, columnKey, width);
                        }}
                        isColumnResizable={columnKey => canMutateProject && columnKey !== 'actions'}
                        rowKey={spec => spec.id}
                        columns={specTableColumns}
                        dataSource={visibleSpecSchedules}
                        pagination={false}
                        tableLayout="fixed"
                        panelClassName="spec-table-panel-shell"
                        scrollClassName="spec-table-container"
                        className="spec-table"
                        components={{
                          body: {
                            wrapper: () => (
                              <tbody className="spec-table-body">
                            {visibleSpecSchedules.map((spec, specIndex) => {
                              const specTerminated = normalizeSpecScheduleStatus(spec.status) === 'terminated';
                              const canEditPlanDates = isEditing && !specTerminated;
                              const canEditActualNote = canEditActualOrNote && !specTerminated;
                              return (
                              <tr key={spec.id} className="spec-table-row">
                                <td 
                                  className="spec-cell spec-name-cell"
                                  style={{ 
                                    width: getColumnWidth(schedule.id, 'specName'),
                                    minWidth: getColumnWidth(schedule.id, 'specName'),
                                    maxWidth: getColumnWidth(schedule.id, 'specName'),
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="spec-name-card spec-name-card--editable"
                                    onClick={() => openSkuPickerForSpec(schedule.id, spec, { readOnly: !isEditing })}
                                    title={
                                      isEditing
                                        ? formatSpecRowTitle(spec)
                                        : `${formatSpecRowTitle(spec)}（点击查看）`
                                    }
                                  >
                                    <span className="spec-name-card__title-row">
                                      <span className="spec-name-card__title">{formatSpecRowTitle(spec)}</span>
                                      {hasLinkedIpd(spec) ? (
                                        <span className="spec-name-card__ipd-mark" title="已关联 IPD">
                                          IPD
                                        </span>
                                      ) : null}
                                    </span>
                                    {formatSpecRowSkuCodes(spec) ? (
                                      <span className="spec-name-card__skus">{formatSpecRowSkuCodes(spec)}</span>
                                    ) : null}
                                  </button>
                                </td>
                                <td
                                  className="spec-cell spec-status-cell"
                                  style={{
                                    width: getColumnWidth(schedule.id, 'specStatus'),
                                    minWidth: getColumnWidth(schedule.id, 'specStatus'),
                                    maxWidth: getColumnWidth(schedule.id, 'specStatus'),
                                  }}
                                >
                                  <IpcStatusBadge variant={specStatusBadgeVariant(spec.status)}>
                                    {specScheduleStatusDisplayText(spec.status)}
                                  </IpcStatusBadge>
                                </td>
                                
                                {milestoneColumns.map(milestoneDef => {
                                  let scheduleMilestone = spec.milestones?.find(
                                    m => String(m.milestoneId) === String(milestoneDef.id)
                                  );
                                  
                                  // 获取关联关系信息
                                  const relation = relationMap.get(milestoneDef.id);
                                  const hasConflict = hasDateConflict(schedule, spec, milestoneDef.id, relation, scheduleMilestone);
                                  const isAutoCalculated = relation?.calculationMode === 'auto';
                                  const colWidth = getMilestoneColumnWidth(schedule, milestoneDef.id);
                                  
                                  // Cross-spec merge is driven by relation.merge_across_specs (not base node definition)
                                  const shouldMerge = (relation?.mergeAcrossSpecs ?? milestoneDef.mergeAcrossSpecs) === true;
                                  const isFirstSpec = specIndex === 0;
                                  const totalSpecs = visibleSpecSchedules.length;
                                  
                                  // For merged milestones, only render on the first spec row with rowSpan
                                  if (shouldMerge && !isFirstSpec) {
                                    return null; // Skip rendering for non-first rows
                                  }
                                  
                                  // 若当前规格缺少该里程碑记录：
                                  // - 只读时保持空白
                                  // - 草稿编辑中，或已发布版可直接改备注/实际时间：构造临时 milestone 供编辑
                                  if (!scheduleMilestone) {
                                    if (!canEditPlanDates && !canEditActualNote) {
                                      return (
                                        <td 
                                          key={milestoneDef.id} 
                                          className="spec-cell empty-cell"
                                          style={{ 
                                            width: colWidth,
                                            minWidth: colWidth,
                                            maxWidth: colWidth,
                                          }}
                                          {...(shouldMerge ? { rowSpan: totalSpecs } : {})}
                                        />
                                      );
                                    }
                                    scheduleMilestone = {
                                      milestoneId: String(milestoneDef.id),
                                      startDate: '',
                                      endDate: '',
                                      actualStartDate: undefined,
                                      actualEndDate: undefined,
                                      status: 'pending',
                                    } as ScheduleMilestone;
                                  }
                                  
                                  const sm = scheduleMilestone as ScheduleMilestone;
                                  const hasPlanDates = !!(sm.startDate || sm.endDate);
                                  const hasActualDates = !!(sm.actualStartDate || sm.actualEndDate);
                                  const isEditingThisMilestone = !!editingCell &&
                                    editingCell.versionId === schedule.id &&
                                    editingCell.specId === spec.id &&
                                    editingCell.milestoneId === milestoneDef.id;
                                  const cellNote = getCellNote(spec, milestoneDef.id);
                                  const isEditingStart = isEditingThisMilestone && editingCell?.field === 'start';
                                  const isEditingEnd = isEditingThisMilestone && editingCell?.field === 'end';
                                  const isEditingActualStart = isEditingThisMilestone && editingCell?.field === 'actualStart';
                                  const isEditingActualEnd = isEditingThisMilestone && editingCell?.field === 'actualEnd';
                                  const isEditingNote = editingCellNote?.versionId === schedule.id 
                                    && editingCellNote?.specId === spec.id 
                                    && editingCellNote?.milestoneId === milestoneDef.id;
                                  
                                  return (
                                    <td
                                      key={milestoneDef.id}
                                      className={`spec-cell milestone-cell ${shouldMerge ? 'merged-cell' : ''} ${isAutoCalculated ? 'auto-calculated' : ''} ${hasConflict ? 'has-conflict' : ''} ${hasActualDates ? 'has-actual' : 'plan-only'}`}
                                      style={{
                                        backgroundColor: getCellBackgroundColor(sm.status),
                                        width: colWidth,
                                        minWidth: colWidth,
                                        maxWidth: colWidth,
                                      }}
                                      {...(shouldMerge ? { rowSpan: totalSpecs } : {})}
                                      data-merged={shouldMerge ? 'true' : undefined}
                                    >
                                      <div className="milestone-cell-inner">
                                        <div className="milestone-status-btn-wrapper">
                                          {renderMilestoneStatusControl(
                                            schedule,
                                            spec.id,
                                            milestoneDef.id,
                                            sm.status,
                                            sm.startDate,
                                            sm.endDate,
                                          )}
                                        </div>

                                        <div className="milestone-date-row milestone-date-row-plan">
                                          <span className="date-row-label">计划</span>
                                          <span className="milestone-dates milestone-dates-expected">
                                            {hasPlanDates || canEditPlanDates ? (
                                              <>
                                                {renderMilestoneDateCell(
                                                  schedule.id,
                                                  spec.id,
                                                  milestoneDef.id,
                                                  canEditPlanDates,
                                                  isEditingStart,
                                                  'start',
                                                  sm.startDate,
                                                  'date-text start-date',
                                                  '开始',
                                                )}
                                                {(sm.startDate && sm.endDate) || canEditPlanDates ? (
                                                  <span className="date-separator">~</span>
                                                ) : null}
                                                {renderMilestoneDateCell(
                                                  schedule.id,
                                                  spec.id,
                                                  milestoneDef.id,
                                                  canEditPlanDates,
                                                  isEditingEnd,
                                                  'end',
                                                  sm.endDate,
                                                  'date-text end-date',
                                                  '结束',
                                                )}
                                              </>
                                            ) : (
                                              <span className="date-empty">—</span>
                                            )}
                                          </span>
                                        </div>

                                        <div className="milestone-date-row milestone-date-row-actual">
                                          <span className="date-row-label">实际</span>
                                          <span className="milestone-dates milestone-dates-actual">
                                            {hasActualDates || canEditActualNote ? (
                                              <>
                                                {renderMilestoneDateCell(
                                                  schedule.id,
                                                  spec.id,
                                                  milestoneDef.id,
                                                  canEditActualNote,
                                                  isEditingActualStart,
                                                  'actualStart',
                                                  sm.actualStartDate,
                                                  'date-text start-date',
                                                  '开始',
                                                )}
                                                {(sm.actualStartDate && sm.actualEndDate) || canEditActualNote ? (
                                                  <span className="date-separator">~</span>
                                                ) : null}
                                                {renderMilestoneDateCell(
                                                  schedule.id,
                                                  spec.id,
                                                  milestoneDef.id,
                                                  canEditActualNote,
                                                  isEditingActualEnd,
                                                  'actualEnd',
                                                  sm.actualEndDate,
                                                  'date-text end-date',
                                                  '结束',
                                                )}
                                              </>
                                            ) : (
                                              <span className="date-empty">—</span>
                                            )}
                                          </span>
                                        </div>

                                        {/* 备注：始终占位一行；有内容按高度换行，无内容显示占位 */}
                                        <div className="cell-note">
                                          <span className="cell-note-label">备注</span>
                                          {canEditActualNote && isEditingNote ? (
                                            <IpcRichTextEditor
                                              variant="inline"
                                              className="cell-note-input"
                                              value={cellNote}
                                              placeholder="添加备注..."
                                              autoFocus
                                              onBlur={html =>
                                                handleUpdateCellNote(
                                                  schedule.id,
                                                  spec.id,
                                                  milestoneDef.id,
                                                  html,
                                                )
                                              }
                                            />
                                          ) : (
                                            <button
                                              type="button"
                                              className={[
                                                'cell-note-text',
                                                !isRichTextEmpty(cellNote) ? 'has-note' : '',
                                                isRichTextEmpty(cellNote) && canEditActualNote ? 'editable' : '',
                                                isRichTextEmpty(cellNote) && !canEditActualNote ? 'is-empty' : '',
                                              ].filter(Boolean).join(' ')}
                                              title={
                                                richTextToPlain(cellNote)
                                                || (canEditActualNote ? '点击添加备注' : undefined)
                                              }
                                              disabled={!canEditActualNote}
                                              onClick={() =>
                                                canEditActualNote &&
                                                setEditingCellNote({
                                                  versionId: schedule.id,
                                                  specId: spec.id,
                                                  milestoneId: milestoneDef.id,
                                                })
                                              }
                                            >
                                              {!isRichTextEmpty(cellNote) ? (
                                                <IpcRichTextView value={cellNote} />
                                              ) : (
                                                canEditActualNote ? '添加备注' : '—'
                                              )}
                                            </button>
                                          )}
                                        </div>

                                        {!isEditing
                                          && isAutoCompletionNode(milestoneDef.id)
                                          && (spec.linkedSkus?.length ?? 0) > 1
                                          ? (
                                            <div className="cell-sku-list">
                                              {buildAutoNodeSkuDisplayRows(spec, milestoneDef.id).map(row => (
                                                <div key={row.key} className="cell-sku-list__row">
                                                  <span className="cell-sku-list__spec" title={row.specText}>{row.specText}</span>
                                                  <span
                                                    className="cell-sku-list__status"
                                                    style={{
                                                      color: nodeStatusForegroundVar(row.status),
                                                      backgroundColor: nodeStatusCellBackground(row.status),
                                                    }}
                                                  >
                                                    {milestoneStatusLabel(row.status)}
                                                  </span>
                                                  <span className="cell-sku-list__end">
                                                    {row.actualEndDate
                                                      ? (formatDateDisplay(row.actualEndDate) || row.actualEndDate)
                                                      : '--'}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          ) : null}
                                      </div>
                                    </td>
                                  );
                                })}
                                
                                {/* 操作列：右侧固定；进行中可终止；已终止在编辑态可复制 */}
                                {canMutateProject && schedule.status !== 'archived' && (
                                  <td className="spec-cell action-cell">
                                    {(() => {
                                      const specStatus = normalizeSpecScheduleStatus(spec.status);
                                      const isTerminated = specStatus === 'terminated';
                                      const isCompleted = specStatus === 'completed';
                                      const terminalHint: MenuProps['items'] = [
                                        {
                                          key: 'terminal',
                                          label: '— 终态，无操作',
                                          disabled: true,
                                          className: 'spec-ops-menu-item--terminal',
                                        },
                                      ];
                                      const editCopyDelete: MenuProps['items'] = isEditing
                                        ? [
                                            { type: 'divider' as const },
                                            {
                                              key: 'copy',
                                              label: '复制',
                                              onClick: () => handleCopySpec(schedule.id, spec.id),
                                            },
                                            ...(schedule.specSchedules.length > 1
                                              ? [{
                                                  key: 'delete',
                                                  label: '删除',
                                                  danger: true,
                                                  onClick: () => handleDeleteSpec(schedule.id, spec.id),
                                                }]
                                              : []),
                                          ]
                                        : [];
                                      const menuItems: MenuProps['items'] = isTerminated
                                        ? (isEditing
                                            ? [{
                                                key: 'copy',
                                                label: '复制',
                                                onClick: () => handleCopySpec(schedule.id, spec.id),
                                              }]
                                            : terminalHint)
                                        : isCompleted
                                          ? terminalHint
                                          : [
                                              {
                                                key: 'terminate',
                                                label: '终止',
                                                danger: true,
                                                onClick: () => void openTerminateSpecModal(schedule, spec),
                                              },
                                              ...editCopyDelete,
                                            ];
                                      return (
                                        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                                          <button type="button" className="spec-ops-trigger">
                                            操作
                                            <ChevronDown size={14} className="spec-ops-trigger__caret" aria-hidden />
                                          </button>
                                        </Dropdown>
                                      );
                                    })()}
                                  </td>
                                )}
                              </tr>
                            );
                            })}
                              </tbody>
                            )
                          },
                        }}
                      />

                      {/* 进度同步 */}
                      <div className="version-notes-section">
                        <div className="notes-header">
                          <label className="notes-label" htmlFor={`version-notes-${schedule.id}`}>
                            进度同步
                          </label>
                          {(isPublished || schedule.status === 'archived') && !canEditProgressSync && (
                            <span className="notes-hint">
                              {schedule.status === 'archived' ? '已归档版本不可修改' : '无权限修改生效版本'}
                            </span>
                          )}
                        </div>
                        <IpcRichTextEditor
                          id={`version-notes-${schedule.id}`}
                          variant="block"
                          className="version-notes-textarea"
                          value={schedule.versionNotes || ''}
                          onChange={html => handleUpdateVersionNotes(schedule.id, html)}
                          onFocus={() => {
                            versionNotesFocusRef.current[schedule.id] = schedule.versionNotes || '';
                          }}
                          onBlur={() => {
                            if (isPublished && canEditProgressSync) {
                              const latest = projectSchedulesRef.current.find(s => s.id === schedule.id);
                              if (!latest) return;
                              const previousNotes = versionNotesFocusRef.current[schedule.id] ?? '';
                              void autoSaveVersionNotes(latest, previousNotes);
                            }
                          }}
                          placeholder={
                            !canEditProgressSync && isPublished
                              ? '无权限修改生效版本'
                              : '说明整体开发进度及问题...'
                          }
                          disabled={!canEditProgressSync}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
                  ),
                },
                {
                  key: 'gantt',
                  label: (
                    <span className="schedule-detail-tab-label">
                      <BarChart3 size={16} aria-hidden />
                      甘特图
                    </span>
                  ),
                  children: (
                    <div className="schedule-gantt-tab">
                      <ScheduleGanttChart
                        schedule={projectSchedules.find(s => s.id === activeVersionId) ?? null}
                        isNonWorkingDay={isGanttNonWorkingDay}
                        onMilestoneDrag={
                          canMutateProject
                            ? async (specId, milestoneId, newStartDate, newEndDate) => {
                          const scheduleId = Number(activeVersionId);
                          if (!scheduleId) return;
                          await milestoneApi.update(scheduleId, specId, milestoneId, {
                            start_date: newStartDate,
                            end_date: newEndDate,
                          });
                          // 刷新当前版本数据
                          try {
                            const detail = await scheduleApi.getById(scheduleId);
                            const mapped = mapDetailToScheduleVersion(detail);
                            setProjectSchedules(prev =>
                              prev.map(s => (s.id === String(scheduleId) ? { ...mapped } : s)),
                            );
                          } catch (e) {
                            console.error('刷新排期数据失败', e);
                          }
                            }
                            : undefined
                        }
                      />
                    </div>
                  ),
                },
                {
                  key: 'oplog',
                  label: (
                    <span className="schedule-detail-tab-label">
                      <ScrollText size={16} aria-hidden />
                      操作日志
                    </span>
                  ),
                  children: (
                    <ScheduleOperationLogTab
                      projectId={selectedProject ? Number(selectedProject.id) : null}
                      active={scheduleDetailTab === 'oplog'}
                    />
                  ),
                },
              ]}
            />
          </div>

          {/* 列配置弹窗 */}
          {columnConfigVersionId && canMutateProject && (
            <IpcModal
              className="column-config-modal"
              title="配置显示列"
              open
              onCancel={() => setColumnConfigVersionId(null)}
              onOk={handleSaveColumnConfig}
              okText="保存配置"
              cancelText="取消"
              width={500}
            >
              <p className="schedule-modal-hint">请选择要显示的里程碑列（数据源为全部里程碑节点，仅影响当前版本）：</p>

              <div className="milestone-checkbox-list">
                {allMilestoneNodes
                  .slice()
                  .sort((a, b) => (a.phase ?? '').localeCompare(b.phase ?? '') || (a.name ?? '').localeCompare(b.name ?? ''))
                  .map(node => {
                    const nodeId = String(node.id);
                    const isChecked = tempSelectedMilestones.includes(nodeId);
                    return (
                      <label key={nodeId} className="milestone-checkbox-item">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTempSelectedMilestones([...tempSelectedMilestones, nodeId]);
                            } else {
                              setTempSelectedMilestones(tempSelectedMilestones.filter(id => id !== nodeId));
                            }
                          }}
                        />
                        <span className="milestone-checkbox-name">{node.name ?? node.milestone_code ?? `节点${nodeId}`}</span>
                        {node.phase && <span className="milestone-checkbox-phase">{node.phase}</span>}
                      </label>
                    );
                  })}
              </div>
            </IpcModal>
          )}

          {/* 关联配置弹窗 */}
          {relationConfigVersionId && canMutateProject && (() => {
            const schedule = projectSchedules.find(s => s.id === relationConfigVersionId);
            const columns = schedule ? getVersionMilestoneColumns(schedule) : [];
            const activeMilestone = columns.find(milestone => milestone.id === activeMilestoneId) ?? columns[0];
            const activeRelation = activeMilestone ? getTempRelation(activeMilestone.id) : undefined;
            const activeMode = activeRelation?.calculationMode ?? 'manual';
            const activeDirection = getRelationDirection(activeRelation, schedule, activeMilestone);
            const parentOptions = schedule && activeMilestone ? getAvailableParentMilestones(schedule, activeMilestone.id) : [];
            const nextOptions = schedule && activeMilestone ? getAvailableNextMilestones(schedule, activeMilestone.id) : [];
            const dependencyOptions = activeDirection === 'parent' ? parentOptions : nextOptions;
            const fieldErrors =
              relationShowFieldErrors && schedule && activeMilestone
                ? getRelationFieldErrors(schedule, activeMilestone)
                : {};

            return (
              <IpcModal
                title="配置里程碑关联"
                open
                onCancel={() => {
                  setRelationConfigVersionId(null);
                  setActiveMilestoneId(null);
                  setRelationDirectionDrafts({});
                  setRelationShowFieldErrors(false);
                }}
                onOk={handleSaveRelationConfig}
                okText="保存配置"
                cancelText="取消"
                width={820}
                className="relation-config-modal"
              >
                {schedule && columns.length > 0 && activeMilestone ? (
                  <div className="relation-config-workspace">
                    <aside className="relation-node-panel" aria-label="里程碑节点">
                      <div className="relation-config-summary">
                        <span>共 {columns.length} 个节点</span>
                      </div>
                      <div className="relation-node-list">
                        {columns.map(milestone => (
                          <button
                            key={milestone.id}
                            type="button"
                            className={[
                              'relation-node-item',
                              activeMilestone.id === milestone.id ? 'relation-node-item--active' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => setActiveMilestoneId(milestone.id)}
                          >
                            <span className="relation-node-main">
                              <span className="relation-node-name">{milestone.name}</span>
                              {milestone.phase && <span className="relation-node-phase">{milestone.phase}</span>}
                            </span>
                          </button>
                        ))}
                      </div>
                    </aside>

                    <section className="relation-editor-panel">
                      <div className="relation-editor-header">
                        <div className="relation-editor-title">
                          <h3>{activeMilestone.name}</h3>
                          {activeMilestone.phase ? (
                            <span className="relation-editor-phase">{activeMilestone.phase}</span>
                          ) : null}
                        </div>
                      </div>

                      <div className="relation-editor-section">
                        <IpcFormField className="relation-form-field relation-form-field--mode" label="计算模式">
                          <IpcSegmented
                            block
                            value={activeMode}
                            onChange={value => handleRelationModeChange(schedule, activeMilestone, value as 'auto' | 'manual')}
                            options={[
                              { value: 'manual', label: '手动设置' },
                              { value: 'auto', label: '自动计算' },
                            ]}
                          />
                        </IpcFormField>

                        {activeMode === 'auto' ? (
                          <>
                            <IpcFormField className="relation-form-field relation-form-field--direction" label="依赖方向">
                              <IpcSegmented
                                block
                                value={activeDirection}
                                onChange={value => handleRelationDirectionChange(activeMilestone.id, value as RelationDependencyDirection)}
                                options={[
                                  { value: 'parent', label: '前序节点', disabled: parentOptions.length === 0 },
                                  { value: 'next', label: '后序节点', disabled: nextOptions.length === 0 },
                                ]}
                              />
                            </IpcFormField>

                            <div className="relation-auto-stack">
                              <IpcFormField
                                className="relation-form-field relation-form-field--dependency"
                                label={activeDirection === 'parent' ? '前序节点' : '后序节点'}
                                required
                                error={fieldErrors.dependency}
                              >
                                <Select
                                  value={activeDirection === 'parent' ? activeRelation?.parentMilestoneId : activeRelation?.nextMilestoneId}
                                  onChange={(value) => updateTempRelation(activeMilestone.id, {
                                    parentMilestoneId: activeDirection === 'parent' ? value || undefined : undefined,
                                    nextMilestoneId: activeDirection === 'next' ? value || undefined : undefined,
                                  })}
                                  placeholder={dependencyOptions.length > 0 ? '请选择节点' : '暂无可用节点'}
                                  allowClear
                                  disabled={dependencyOptions.length === 0}
                                  status={fieldErrors.dependency ? 'error' : undefined}
                                  options={dependencyOptions.map(option => ({ value: option.id, label: option.name }))}
                                  getPopupContainer={() => document.body}
                                />
                              </IpcFormField>

                              <IpcFormField
                                className="relation-form-field"
                                label="持续天数"
                                required
                                error={fieldErrors.duration}
                              >
                                <InputNumber
                                  min={1}
                                  precision={0}
                                  value={activeRelation?.durationDays}
                                  onChange={(value) => updateTempRelation(activeMilestone.id, {
                                    durationDays: typeof value === 'number' ? value : undefined
                                  })}
                                  placeholder="输入天数"
                                  status={fieldErrors.duration ? 'error' : undefined}
                                  style={{ width: '100%' }}
                                />
                              </IpcFormField>

                              <IpcFormField className="relation-form-field" label="时间偏移">
                                <InputNumber
                                  precision={0}
                                  value={activeRelation?.timeOffsetDays ?? 1}
                                  onChange={(value) => updateTempRelation(activeMilestone.id, {
                                    timeOffsetDays: typeof value === 'number' ? value : 1
                                  })}
                                  placeholder="支持正负整数"
                                  style={{ width: '100%' }}
                                />
                              </IpcFormField>

                              <div className="relation-checkbox-row">
                                <Checkbox
                                  checked={activeRelation?.excludeWeekends || false}
                                  onChange={(e) => updateTempRelation(activeMilestone.id, {
                                    excludeWeekends: e.target.checked
                                  })}
                                >
                                  排除非工作日
                                </Checkbox>
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="relation-manual-hint">手动模式下日期在表格中直接维护。</p>
                        )}
                      </div>

                      <div className="relation-editor-section relation-editor-section--merge">
                        <Checkbox
                          checked={activeRelation?.mergeAcrossSpecs || false}
                          onChange={(e) => updateTempRelation(activeMilestone.id, {
                            mergeAcrossSpecs: e.target.checked
                          })}
                        >
                          跨规格合并
                        </Checkbox>
                      </div>
                    </section>
                  </div>
                ) : (
                  <div className="relation-config-empty">当前版本暂无里程碑节点，请先配置列。</div>
                )}
              </IpcModal>
            );
          })()}
        </div>
      )}

      {/* 导入配置弹窗 */}
      <ImportConfigModal
        open={showImportModal && canMutateProject}
        currentProjectId={selectedProject ? Number(selectedProject.id) : 0}
        pageSize={20}
        onClose={() => setShowImportModal(false)}
        onImported={() => {
          if (!selectedProject) return;
          void scheduleApi.getListByProject(Number(selectedProject.id), { includeDetail: true }).then((list: ScheduleVO[]) => {
            const mappedVersions = [...list]
              .sort((a, b) => b.version - a.version)
              .map(vo => mapDetailToScheduleVersion(vo as ScheduleDetailVO));
            setProjectSchedules(mappedVersions);
            if (mappedVersions.length > 0) {
              setActiveVersionId(mappedVersions[0].id);
            }
            setProjects(prev =>
              prev.map(p =>
                p.id === selectedProject.id ? { ...p, scheduleCount: list.length } : p,
              ),
            );
          }).catch(() => message.error('导入成功，但刷新版本明细失败，请刷新页面'));
        }}
      />
    </div>
  );
};

export default ProjectSchedule;
